import { showArmRestrictionNotice } from '@components/ArmsCushionsPanel/ArmRestrictionNoticeModal'
import {
  AnyID,
  Getter,
  Item,
  LinkedItem,
  Morphs,
  Partition,
  SceneObjects,
  Setter,
  Textures,
  Walls,
  Zone,
} from '@types'
import {
  parseSeatHeightFromCatalog,
  SEAT_HEIGHT_DEFAULT_CM,
  SEAT_HEIGHT_TALL_CM,
  shouldApplySeatHeightToCatalog,
  stripSeatHeightPrefix,
} from '@utils/atemporal/seatHeightCatalog'
import {
  isSeatOrCushionLinkedSon,
  placementForSeatHeightChange,
} from '@utils/atemporal/seatHeightPlacement'
import { runWithHistoryBatchAsync, getHistoryBatchDepth } from '@utils/historyRecording'
import { enforceSeatCushionLocalCoords } from '@utils/atemporalSeatCushionLocalCoords'
import toQuaternion from '@utils/conversion/toQuaternion'
import { toVector3 } from '@utils/conversion/toVector3'
import { validateHex } from '@utils/conversion/validateHex'
import { resolveApiTextureColor } from '@utils/sceneObjects/resolvePbrMaterial'
import { degrees, radians } from '@utils/generalMaths/geometry'
import {
  buildApproachMatrixForArm,
  buildBr12FineTunedMatrix,
  buildFlushMatrixForArm,
  decomposeMatrixPosition,
  getArmSideSign,
} from '@utils/linkedArms/armPlacement'
import {
  clearArmFromUnitSnappedSides,
  stripArmIdsFromSnappedSides,
} from '@utils/linkedArms/armOccupancy'
import { evaluateSingleArmSeriesPlacement } from '@utils/linkedArms/resolveSingleArmSeriesPlacement'
import {
  getRelaxBaseCode,
  isRelaxIntegratedUnitByDefaults,
} from '@utils/atemporal/relaxArmVariants'
import { pickExteriorArmPlacement } from '@utils/linkedArms/resolveExteriorArmPlacement'
import {
  ARM_NOTICE_ONLY_ONE_SIDE,
  ARM_NOTICE_REPLACE_WITHOUT_ARMS,
  allowedArmSideForUnit,
  collectArmIdsLinkedToParent,
  isSingleArmSeries,
  pickArmSnapshotToKeepForSingleArmSeries,
} from '@utils/linkedArms/singleArmSeriesRules'
import { API } from '@utils/queryBuilder'
import isArea from '@utils/sceneObjects/assertions/isArea'
import isItem from '@utils/sceneObjects/assertions/isItem'
import isLinkedItem from '@utils/sceneObjects/assertions/isLinkedItem'
import isPartition from '@utils/sceneObjects/assertions/isPartition'
import isWall from '@utils/sceneObjects/assertions/isWall'
import {
  cloneLinkedItemTexturesForInheritance,
  getExistingTexturesFromScene,
  mapExistingTexturesToNewItemByDisplayName,
  syncZonesWithMappedTextures,
  type LinkedItemTextureInheritance,
} from '@utils/sceneObjects/getExistingTexturesFromScene'
import getId from '@utils/sceneObjects/getId'
import { addItemOffset } from '@utils/sceneObjects/items/addItemOffset'
import { getGLB } from '@utils/sceneObjects/items/getGLB'
import { getRelativeMatrix } from '@utils/sceneObjects/items/getRelativeMatrix'
import { newGetDataFromGLB } from '@utils/sceneObjects/items/newGetDataFromGLB'
import { composeStraightModuleAtRowExterior } from '@utils/modularSnap/rowSnapGuards'
import { composeCornerXLChaiseAgainstCorner } from '@utils/modularSnap/rinconeroSnapEngine/resolveCornerInsertPlan'
import {
  composeMatrixCornerXLOnCornerXLBack,
  composeRinconeroDragRowEndSnap,
  composeStraightModuleSnapOnRinconero,
  isCornerXLBrazoCatalog,
  isOreja,
  isModularCornerSnapCatalog,
  isRinconeroCatalog,
  isStraightModularSnapCatalog,
  modulePlanWidth,
  isRinconeroDerecha as isModularCornerDerecha,
  isRinconeroIzquierda as isModularCornerIzquierda,
  resolveRinconeroDragRowAttachEnd,
  rinconeroHandednessFromItem,
} from '@utils/sceneObjects/items/rinconeroUtils'
import { getPouffPenetratingObstacles } from '@utils/sceneObjects/items/pouffDragCollision'
import { filterPouffPenetrationsForArmAnchor } from '@utils/sceneObjects/items/pouffArmSnap'
import { computePouffSnapPositionToAnchor, POUFF_SNAP_FACE_GAP_MM } from '@utils/sceneObjects/items/pouffSnapPlacement'
import { setGLTFLoader } from '@utils/sceneObjects/setGLTFLoader'
import {
  buildCatalogIDWithSuffix,
  getBaseCatalogID,
  mapUnitsWithSuffixes,
  updateGlbPathWithSuffix,
  updateReferenciaWithSuffix,
} from '@utils/sceneObjects/suffixUtils'
import { Euler, Matrix3, Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB'
import { radToDeg } from 'three/src/math/MathUtils'
import { pushGeometryOperation } from '@utils/geometryIntegrity/operation'
import { computeParentSnapPlacement } from './drawItemPlacement'
import { drawSnapLog } from './drawSnapLog'
import { runDrawItemPostProcessing } from './drawItemPostProcessing'

export type ItemActions = ReturnType<typeof createItemActions>

export type DrawItemOptions = {
  /** ID seleccionado justo antes de deseleccionar en UI para conservar contexto de ancla. */
  preDrawSelectedAnchorId?: AnyID | ''
  /** Matriz ya resuelta por Toolbar/MenuItem (getRelativeMatrix). No recalcular posición. */
  useChainedDrawMatrix?: boolean
  /** Lado de snap usado al calcular la matriz encadenada (pata/fila rinconero). */
  chainedDrawSnapSide?: 'left' | 'right'
  preserveSelection?: boolean
  suppressCameraFit?: boolean
  suppressAutoSeatAndCushion?: boolean
  /** Evita realinear brazos al dibujar (sustitución de unidad / pack). */
  suppressExteriorArmRealign?: boolean
  /** Mantiene matriz/posición/rotación al sustituir (no auto-snap de colocación). */
  preserveWorldTransform?: boolean
  /** Texturas ya aplicadas en la unidad sustituida (antes de purgar escena). */
  inheritTexturesFrom?: LinkedItemTextureInheritance | null
}

export type LinkedArmMigrationSnapshot = {
  oldArmId: AnyID
  oldCatalogID: string
  preservedSideSign: 1 | -1
  depth: number
  preservedMatrix?: Matrix4
}

export const dictionary = {
  MAL: 'highItems' as const,
  MBJ: 'lowItems' as const,
}

export const isCushionLikeSceneObject = (obj: any) => {
  if (!obj) return false
  const idUnidades = Number(obj?.idUnidades || 0)
  const catalogText = String(obj?.catalogID || obj?.codigo || '').toUpperCase()
  const referenceText = String(obj?.referencia || '').toUpperCase()
  return (
    idUnidades === 7719 ||
    idUnidades === 7720 ||
    catalogText.includes('COJIN') ||
    catalogText.includes('COJINES') ||
    referenceText.includes('COJIN') ||
    referenceText.includes('COJINES')
  )
}

export const isArmSceneObject = (obj: any) => {
  const catalogText = String(obj?.catalogID || obj?.codigo || '').toUpperCase()
  return /^BR\d+(-\d+)?$/i.test(catalogText)
}

const isRinconeroDerecha = (catalogID: unknown, hand?: string | null) => {
  if (!isRinconeroCatalog(String(catalogID ?? ''))) return false
  return !isModularCornerIzquierda(catalogID, hand)
}

let parentUnitReplaceInFlight = false

const purgeSceneObjectIds = (set: Setter, ids: Iterable<AnyID>) => {
  const idList = Array.from(ids)
  if (!idList.length) return
  set((store) => {
    idList.forEach((id) => {
      const idStr = String(id)
      // Resolver clave real del Map (evita fallos por mismatch de tipo AnyID/string).
      let mapKey: AnyID | string | undefined = store.items.has(id)
        ? id
        : undefined
      if (mapKey == null) {
        for (const key of store.items.keys()) {
          if (String(key) === idStr) {
            mapKey = key
            break
          }
        }
      }
      if (mapKey == null) {
        for (const [key, value] of store.items) {
          if (String((value as any)?.id) === idStr) {
            mapKey = key
            break
          }
        }
      }
      if (mapKey == null) return

      const item = store.items.get(mapKey as AnyID) as any
      const parentId = item?.firstParentID
      if (parentId) {
        const parent = store.items.get(parentId) as any
        if (parent?.childs?.delete) {
          parent.childs.delete(mapKey)
          parent.childs.delete(idStr)
          if (item?.id) parent.childs.delete(item.id)
        }
      }

      store.items.delete(mapKey as AnyID)
      store.layers[store.currentLayer].items.delete(mapKey as AnyID)
      store.layers[store.currentLayer].selected.delete(mapKey as AnyID)
      store.sceneBoundingBoxes.delete(mapKey as AnyID)
      store.collisionableItems.delete(mapKey as AnyID)
      if (String(store.currentObjectID) === idStr) store.currentObjectID = ''
    })
  })
}

const SIDE_ADJACENCY_TOLERANCE_MM = 140

/** ¿El vecino sigue a tope en ese labio tras un cambio de ancho? */
const isNeighborFlushOnSide = (
  unit: any,
  neighbor: any,
  side: 'left' | 'right',
): boolean => {
  if (!unit?.matrix || !neighbor?.matrix) return false
  const unitPos = new Vector3()
  const unitQuat = new Quaternion()
  unit.matrix.decompose(unitPos, unitQuat, new Vector3())
  const neighborPos = new Vector3()
  neighbor.matrix.decompose(neighborPos, new Quaternion(), new Vector3())
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(unitQuat)
  const zAxis = new Vector3(0, 0, 1).applyQuaternion(unitQuat)
  const delta = neighborPos.clone().sub(unitPos)
  if (Math.abs(delta.dot(zAxis)) > 160) return false
  const sideProj = delta.dot(xAxis)
  const unitW = Number(unit.width || 0)
  const neighborW = Number(neighbor.width || 0)
  if (side === 'right') {
    return Math.abs(sideProj - unitW) <= SIDE_ADJACENCY_TOLERANCE_MM
  }
  return Math.abs(sideProj + neighborW) <= SIDE_ADJACENCY_TOLERANCE_MM
}

/**
 * Tras sustituir un recto (p.ej. 1420) por RELAX (950), el vecino de la derecha
 * queda con hueco y snappedSides fantasma bloquea el labio izquierdo.
 * Recoloca la cadena derecha/izquierda al nuevo ancho y limpia enlaces inválidos.
 */
const reflowSnappedRowAfterWidthReplace = (
  set: Setter,
  get: Getter,
  oldItem: any,
  newItemId: AnyID,
) => {
  const oldWidth = Number(oldItem?.width || 0)
  const newItem = get().items.get(newItemId as AnyID) as any
  if (!newItem?.matrix || !oldItem?.matrix) return
  let newWidth = Number(newItem.width || modulePlanWidth(newItem) || 0)
  if (!(newWidth > 0) && isRelaxIntegratedUnitByDefaults(newItem.catalogID)) {
    newWidth = 950
  }
  if (!(oldWidth > 0 && newWidth > 0) || Math.abs(oldWidth - newWidth) < 1) {
    // Aunque el ancho no cambie, limpiar enlaces no geométricos (labio izq. fantasma).
    set((state) => {
      const live = state.items.get(newItemId as AnyID) as any
      if (!live) return
      const nextSides: Partial<Record<'left' | 'right', string>> = {
        ...((live.snappedSides as any) || {}),
      }
      ;(['left', 'right'] as const).forEach((side) => {
        const neighborId = nextSides[side]
        if (!neighborId) return
        const neighbor = state.items.get(neighborId as AnyID) as any
        if (!neighbor || !isNeighborFlushOnSide(live, neighbor, side)) {
          delete nextSides[side]
          if (neighbor?.snappedSides) {
            const neighborSides = { ...neighbor.snappedSides }
            const opposite = side === 'left' ? 'right' : 'left'
            if (String(neighborSides[opposite]) === String(newItemId)) {
              delete neighborSides[opposite]
              neighbor.snappedSides = neighborSides
            }
          }
        }
      })
      live.snappedSides = nextSides
    })
    get().setOBB(newItemId)
    return
  }

  const widthDelta = newWidth - oldWidth // negativo si se estrecha
  const origin = new Vector3()
  const quat = new Quaternion()
  newItem.matrix.decompose(origin, quat, new Vector3())
  const xAxis = new Vector3(1, 0, 0).applyQuaternion(quat)

  const sides = {
    left: (newItem.snappedSides as any)?.left as string | undefined,
    right: (newItem.snappedSides as any)?.right as string | undefined,
  }

  const shiftChain = (startId: string | undefined) => {
    if (!startId || Math.abs(widthDelta) < 1) return
    const visited = new Set<string>()
    let currentId: string | undefined = startId
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId)
      const node = get().items.get(currentId as AnyID) as any
      if (!node?.matrix) break
      const pos = new Vector3()
      node.matrix.decompose(pos, new Quaternion(), new Vector3())
      pos.add(xAxis.clone().multiplyScalar(widthDelta))
      const nextMatrix = node.matrix.clone()
      nextMatrix.setPosition(pos.x, pos.y, pos.z)
      get().editItem(currentId, {
        matrix: nextMatrix,
        x: pos.x,
        y: pos.y,
        z: pos.z,
      } as any)
      get().setOBB(currentId as AnyID)
      const nodeSides = (node.snappedSides as Partial<Record<'left' | 'right', string>>) || {}
      currentId = nodeSides.right
    }
  }

  // Cara izquierda fija → acercar/alejar la cadena derecha al nuevo ancho (idempotente).
  if (sides.right) {
    const rightNeighbor = get().items.get(sides.right as AnyID) as any
    if (rightNeighbor && !isNeighborFlushOnSide(newItem, rightNeighbor, 'right')) {
      shiftChain(sides.right)
    }
  }

  set((state) => {
    const live = state.items.get(newItemId as AnyID) as any
    if (!live) return
    const nextSides: Partial<Record<'left' | 'right', string>> = {
      ...((live.snappedSides as any) || {}),
    }

    ;(['left', 'right'] as const).forEach((side) => {
      const neighborId = nextSides[side]
      if (!neighborId) return
      const neighbor = state.items.get(neighborId as AnyID) as any
      if (!neighbor || !isNeighborFlushOnSide(live, neighbor, side)) {
        // Enlace fantasma tras cambio de ancho: liberar labio (crítico para el izquierdo).
        delete nextSides[side]
        const neighborSides = {
          ...((neighbor?.snappedSides as Partial<Record<'left' | 'right', string>>) || {}),
        }
        const opposite = side === 'left' ? 'right' : 'left'
        if (String(neighborSides[opposite]) === String(newItemId)) {
          delete neighborSides[opposite]
          if (neighbor) neighbor.snappedSides = neighborSides
        }
      }
    })

    live.snappedSides = nextSides
  })

  get().setOBB(newItemId)
}

/** Tras sustituir: misma posición/rotación y re-enlazar snappedSides de vecinos. */
const restoreReplacedItemWorldTransform = (
  set: Setter,
  get: Getter,
  oldItem: any,
  newItemId: AnyID,
) => {
  if (!oldItem?.matrix) return

  const oldId = String(oldItem.id)
  const newId = String(newItemId)
  const matrix = oldItem.matrix.clone() as Matrix4
  const pos = new Vector3()
  matrix.decompose(pos, new Quaternion(), new Vector3())

  set((state) => {
    state.items.forEach((sceneItem, sceneId) => {
      if (String(sceneId) === oldId) return
      const sides = (sceneItem as any).snappedSides as
        | Partial<Record<'left' | 'right', string>>
        | undefined
      if (!sides) return
      const next = { ...sides }
      let dirty = false
      if (String(next.left) === oldId) {
        next.left = newId
        dirty = true
      }
      if (String(next.right) === oldId) {
        next.right = newId
        dirty = true
      }
      if (dirty) (sceneItem as any).snappedSides = next
    })

    const newItem = state.items.get(newItemId as AnyID) as any
    if (!newItem) return

    newItem.matrix = matrix.clone()
    newItem.x = pos.x
    newItem.y = pos.y
    newItem.z = pos.z
    if (oldItem.rotation !== undefined) newItem.rotation = oldItem.rotation
    if ((oldItem as any).snappedSides) {
      newItem.snappedSides = { ...(oldItem as any).snappedSides }
    }
  })

  // RELAX/recto con distinto ancho: reflow + limpiar snappedSides fantasma.
  reflowSnappedRowAfterWidthReplace(set, get, oldItem, newItemId)
}

export const createItemActions = (set: Setter, get: Getter) => ({
  removeSceneItemsByIds: (ids: Iterable<AnyID>) => {
    purgeSceneObjectIds(set, ids)
  },
  drawItem: (
    id: string,
    catalogID: string,
    icono: string,
    visible: boolean, // Solo para puertas es false
    matrix: Matrix4 | null,
    type = 'items' as SceneObjects['type'],
    realCatalogID?: string,
    shouldGoToBackHeight = false,
    options?: DrawItemOptions,
  ): Promise<AnyID> => {
    return new Promise((resolve, reject) => {
      ;(async () => {
        try {
          const executeDraw = async (): Promise<AnyID> => {
            const glbInScene = get().sceneGLB as Map<
              string | number,
              { glb: any; backGLBInfo: any }
            >
            let backGLBInfo, glb
            const store = get()
            const globalLastSelected =
              typeof window !== 'undefined'
                ? (
                    window as Window & {
                      __ATEMPORAL_LAST_SELECTED__?: { id?: string; ts?: number }
                    }
                  ).__ATEMPORAL_LAST_SELECTED__
                : undefined
            const globalLastSelectedId = String(globalLastSelected?.id || '')
            const globalLastSelectedAgeMs = globalLastSelected?.ts
              ? Date.now() - Number(globalLastSelected.ts)
              : Number.POSITIVE_INFINITY
            const recentGlobalLastSelectedId =
              globalLastSelectedId && globalLastSelectedAgeMs <= 2500 ? globalLastSelectedId : ''
            const sourceCurrentObjectID =
              options?.preDrawSelectedAnchorId ||
              store.currentObjectID ||
              (recentGlobalLastSelectedId as AnyID | '')
            const preserveSelection = options?.preserveSelection === true
            const suppressCameraFit = options?.suppressCameraFit === true
            const suppressAutoSeatAndCushion = options?.suppressAutoSeatAndCushion === true
            const suppressExteriorArmRealign = options?.suppressExteriorArmRealign === true
            const preserveWorldTransform =
              options?.preserveWorldTransform === true && Boolean(matrix)
            const existingIncrements = Array.from(store.items.values())
              .map((item) => parseInt(item.increment, 10))
              .filter(Number.isInteger)
            const maxExistingIncrement =
              existingIncrements.length > 0 ? Math.max(...existingIncrements) : 0
            const increment = Math.max(store.increment, maxExistingIncrement + 1)

            if (glbInScene.has(catalogID)) {
              const { backGLBInfo: back, glb: _glb } = glbInScene.get(catalogID) as any
              glb = _glb
              backGLBInfo = back
            } else {
              backGLBInfo = await getGLB(glbInScene, catalogID, realCatalogID)
              if (!backGLBInfo) {
                console.error(`❌ No se pudo obtener GLB data para catalogID: ${catalogID}`)
                reject(
                  new Error(
                    `No se pudo cargar el elemento con catalogID: ${catalogID}. El elemento no existe en el catálogo.`,
                  ),
                )
                return
              }
              const referenciaBlender = (backGLBInfo as any)?.referenciaBlender
              if (!referenciaBlender) {
                console.error(`❌ No se encontró referenciaBlender para catalogID: ${catalogID}`)
                reject(
                  new Error(
                    `No se encontró la referencia del modelo 3D para catalogID: ${catalogID}`,
                  ),
                )
                return
              }
              const gltfLoader = await setGLTFLoader()
              const _glb = await gltfLoader.loadAsync(referenciaBlender)
              glb = _glb
            }

            const extraInfo: { zonas: { idAcabadoUnidad: number; zona: string }[] } = { zonas: [] }
            if (!backGLBInfo.universal) {
              const concha = await API().api.acabadosGetUnidadAcabadosInfo(
                (backGLBInfo as any).idUnidades,
              )
              if (concha && Array.isArray(concha.data?.zonas)) {
                extraInfo.zonas = concha.data.zonas as { idAcabadoUnidad: number; zona: string }[]
              }
            }

            const item = newGetDataFromGLB({
              glbData: glb,
              catalogID,
              id: !backGLBInfo.universal ? getId('linkedItem') : id,
              type: !backGLBInfo.universal ? 'linkedItem' : 'items',
              icono,
              visible,
              backGLBInfo,
              paginaPdf: backGLBInfo.paginaPdf,
              matrix,
              extraInfo,
              shouldGoToBackHeight,
              preserveMatrixPosition: preserveWorldTransform,
              // Para linkedItems ya colisionaba por defecto; para universales lo activamos también
              // (salvo que el backend lo fuerce explícitamente a false).
              canCollide: backGLBInfo.colision ?? true,
            })
            const isCushionLike = isCushionLikeSceneObject({
              catalogID,
              ...(backGLBInfo as any),
            })
            if (isCushionLike) {
              ;(item as any).canCollide = false
            }
            // if (!item.universal) {
            //   item.depth = 0
            //   item.type = 'linkedItem'
            //   item.id = getId('linkedItem')
            //   item.childrenOrder = []
            // }

            if (!item.universal) {
              item.depth = 0
              // Respetar el tipo si se especifica 'linkedSon'
              item.type = type === 'linkedSon' ? 'linkedSon' : 'linkedItem'
              item.id = type === 'linkedSon' ? id : getId('linkedItem')
              item.childrenOrder = []
              // Algunos catálogos (ej: 1-95) no devuelven estos campos y el panel de Asiento
              // los necesita para mostrar estado inicial correcto.
              if (item.type === 'linkedItem' && shouldApplySeatHeightToCatalog(catalogID)) {
                const catalogSeatHeight = parseSeatHeightFromCatalog(catalogID)
                const metaSeatHeight = Number((item as any).seatHeightCm || 0)
                if (
                  metaSeatHeight <= 0 ||
                  (catalogSeatHeight === SEAT_HEIGHT_TALL_CM &&
                    metaSeatHeight === SEAT_HEIGHT_DEFAULT_CM)
                ) {
                  ;(item as any).seatHeightCm = catalogSeatHeight
                }
                if (!String((item as any).seatStitchType || '').trim()) {
                  ;(item as any).seatStitchType = 'con'
                }
              }
            }

            item.increment = `${increment}`

            if (!item.universal && item.textures && item.textures.length > 0) {
              const inheritedTextures = options?.inheritTexturesFrom?.textures?.length
                ? options.inheritTexturesFrom.textures
                : getExistingTexturesFromScene(store.items, catalogID, undefined)
              if (inheritedTextures && inheritedTextures.length > 0) {
                item.textures = mapExistingTexturesToNewItemByDisplayName(
                  inheritedTextures,
                  item.textures,
                )
                const syncResult = syncZonesWithMappedTextures(
                  item.zones,
                  item.textures,
                  store.items,
                  item.type,
                  item.description,
                )
                item.zones = syncResult.zones
              }
            }

            const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])

            const centerPosition = new Vector3()
            const quaternion = new Quaternion()
            const drawParentSnapDebugEnabled =
              typeof window !== 'undefined' &&
              Boolean(
                (window as Window & { __DRAW_ITEM_PARENT_SNAP_DEBUG__?: boolean })
                  .__DRAW_ITEM_PARENT_SNAP_DEBUG__,
              )
            const logDrawParentSnap = (step: string, data?: Record<string, unknown>) => {
              drawSnapLog(`drawItem:${step}`, data)
              if (!drawParentSnapDebugEnabled) return
              console.warn(`[drawItem][parent-snap] ${step}`, data ?? {})
            }
            const abortParentSnap = (reason: string, data?: Record<string, unknown>) => {
              logDrawParentSnap(`abort:${reason}`, data)
              reject(new Error(`drawItem aborted: ${reason}`))
            }
            let pendingParentSnap: {
              anchorId: AnyID
              snapSide: 'left' | 'right'
            } | null = null
            const shouldAutoSnapPlacement = type !== 'linkedSon' && !preserveWorldTransform
            const useChainedDrawMatrix =
              Boolean(matrix) && options?.useChainedDrawMatrix === true && type !== 'linkedSon'
            if ((type === 'linkedSon' || preserveWorldTransform) && matrix) {
              matrix.decompose(centerPosition, quaternion, new Vector3())
              item.matrix = matrix.clone()
            } else if (useChainedDrawMatrix && matrix) {
              matrix.decompose(centerPosition, quaternion, new Vector3())
              item.matrix = matrix.clone()
              item.x = centerPosition.x
              item.y = centerPosition.y
              item.z = centerPosition.z
              drawSnapLog('drawItem:matriz-encadenada', {
                catalogID,
                itemId: String(item.id),
                anchorId: String(options?.preDrawSelectedAnchorId || sourceCurrentObjectID || ''),
                snapSide: options?.chainedDrawSnapSide ?? null,
                position: {
                  x: Number(centerPosition.x.toFixed(2)),
                  y: Number(centerPosition.y.toFixed(2)),
                  z: Number(centerPosition.z.toFixed(2)),
                },
              })
              const chainedAnchorId =
                options?.preDrawSelectedAnchorId || sourceCurrentObjectID || null
              if (chainedAnchorId) {
                const chainedAnchor = store.items.get(chainedAnchorId as AnyID) as any
                const anchorCatalog = String(chainedAnchor?.catalogID ?? '').toLowerCase()
                if (chainedAnchor && isRinconeroCatalog(anchorCatalog)) {
                  pendingParentSnap = {
                    anchorId: chainedAnchorId as AnyID,
                    snapSide:
                      options?.chainedDrawSnapSide ??
                      (isModularCornerIzquierda(
                        anchorCatalog,
                        rinconeroHandednessFromItem(chainedAnchor),
                      )
                        ? 'left'
                        : 'right'),
                  }
                } else if (chainedAnchor) {
                  pendingParentSnap = {
                    anchorId: chainedAnchorId as AnyID,
                    snapSide: options?.chainedDrawSnapSide ?? 'right',
                  }
                }
              }
            } else if (shouldAutoSnapPlacement) {
              // Cálculo de colocación por auto-snap a una unidad existente (padre).
              // Toda la selección de ancla/lado vive en store/drawItemPlacement.ts.
              const placement = computeParentSnapPlacement({
                store,
                item,
                catalogID,
                options,
                sourceCurrentObjectID,
                globalLastSelectedId,
                globalLastSelectedAgeMs,
                recentGlobalLastSelectedId,
                logDrawParentSnap,
                isCushionLikeSceneObject,
                isArmSceneObject,
              })

              if (placement.status === 'abort') {
                abortParentSnap(placement.reason, placement.data)
                return
              }

              centerPosition.copy(placement.centerPosition)
              quaternion.copy(placement.quaternion)
              pendingParentSnap = placement.pendingParentSnap

              item.matrix = new Matrix4()
                .makeRotationFromQuaternion(quaternion)
                .setPosition(centerPosition.x, centerPosition.y, centerPosition.z)
              item.x = centerPosition.x
              item.y = centerPosition.y
              item.z = centerPosition.z
            }

            // Garantizar coordenadas explícitas para render 2D/3D (especialmente linkedSon).
            // LinkedItemSon3D usa x/y/z directamente para position y si vienen undefined no renderiza.
            if (item.matrix && item.type !== 'linkedSon') {
              const matrixPos = new Vector3()
              item.matrix.decompose(matrixPos, new Quaternion(), new Vector3())
              item.x = matrixPos.x
              item.y = matrixPos.y
              item.z = matrixPos.z
            }

            // Asiento/cojín: offset local respecto al padre (nunca posición de mundo en x/y/z).
            if (item.type === 'linkedSon') {
              const parentForLocal =
                (item as any).firstParentID != null
                  ? get().items.get((item as any).firstParentID as AnyID)
                  : undefined
              enforceSeatCushionLocalCoords(item, parentForLocal)
            }

            itemCenter.applyQuaternion(quaternion).add(centerPosition)

            const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
            const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

            // Calcular OBB consistente (para universales y linkedItems) usando dimensiones reales
            // y la transformación final del item.
            const obbCenterPosition = toVector3()
            const obbQuaternion = toQuaternion()
            item.matrix.decompose(obbCenterPosition, obbQuaternion, toVector3())
            const obbItemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
            obbItemCenter.applyQuaternion(obbQuaternion).add(obbCenterPosition)
            const obbRotationMatrix4 = new Matrix4().makeRotationFromQuaternion(obbQuaternion)
            const obbRotationMatrix = new Matrix3().setFromMatrix4(obbRotationMatrix4)

            const popDrawGeometry = pushGeometryOperation('drawItem')
            try {
            set((store) => {
              const posBeforeStore = new Vector3()
              const quatBeforeStore = new Quaternion()
              const scaleBeforeStore = new Vector3()
              item.matrix.decompose(posBeforeStore, quatBeforeStore, scaleBeforeStore)

              store.sceneGLB.set(catalogID, { glb, backGLBInfo })
              store.items.set(item.id, item)

              if (pendingParentSnap) {
                const anchorItem = store.items.get(pendingParentSnap.anchorId as AnyID) as any
                if (anchorItem) {
                  const resolveItemById = (id: string) => store.items.get(id as AnyID) as any
                  // Los brazos no ocupan labios de fila; limpiar contaminación legacy.
                  const cleanedAnchorSides = stripArmIdsFromSnappedSides(
                    (anchorItem.snappedSides as Partial<Record<'left' | 'right', string>>) || {},
                    resolveItemById,
                  )
                  clearArmFromUnitSnappedSides(anchorItem, item.id)
                  if (!isArmSceneObject(item)) {
                    anchorItem.snappedSides = {
                      ...cleanedAnchorSides,
                      [pendingParentSnap.snapSide]: String(item.id),
                    }

                    const oppositeSide: 'left' | 'right' =
                      pendingParentSnap.snapSide === 'left' ? 'right' : 'left'
                    const cleanedItemSides = stripArmIdsFromSnappedSides(
                      ((item as any).snappedSides as Partial<Record<'left' | 'right', string>>) ||
                        {},
                      resolveItemById,
                    )
                    ;(item as any).snappedSides = {
                      ...cleanedItemSides,
                      [oppositeSide]: String(anchorItem.id),
                    }
                  } else {
                    anchorItem.snappedSides = cleanedAnchorSides
                  }

                  logDrawParentSnap('snapped-sides-updated', {
                    itemId: String(item.id),
                    anchorId: String(anchorItem.id),
                    anchorSnappedSides: anchorItem.snappedSides,
                    itemSnappedSides: (item as any).snappedSides,
                    drawingIsArm: isArmSceneObject(item),
                  })
                }
              }

              store.layers[store.currentLayer].items.add(item.id)

              if (!preserveSelection) {
                store.layers[store.currentLayer].selected.clear()
                const isArmCatalog = /^BR/i.test(String(catalogID || ''))
                const sourceItem = sourceCurrentObjectID
                  ? (store.items.get(sourceCurrentObjectID as AnyID) as any)
                  : null
                const shouldKeepParentSelection =
                  isArmCatalog && sourceItem && isLinkedItem(sourceItem as any)
                if (shouldKeepParentSelection) {
                  store.layers[store.currentLayer].selected.add(sourceCurrentObjectID as AnyID)
                  store.currentObjectID = sourceCurrentObjectID as AnyID
                } else {
                  visible && store.layers[store.currentLayer].selected.add(item.id)
                  store.currentObjectID = item.id
                }
              }
              store.increment = increment + 1

              const itemInStore = store.items.get(item.id)
              if (itemInStore && itemInStore.matrix) {
                const posAfterStore = new Vector3()
                const quatAfterStore = new Quaternion()
                const scaleAfterStore = new Vector3()
                itemInStore.matrix.decompose(posAfterStore, quatAfterStore, scaleAfterStore)
              }

              setTimeout(() => {
                const itemAfterDelay = get().items.get(item.id)
                if (itemAfterDelay && itemAfterDelay.matrix) {
                  const posAfterDelay = new Vector3()
                  itemAfterDelay.matrix.decompose(posAfterDelay, new Quaternion(), new Vector3())
                }
              }, 500)

              if (
                (((item as any).canCollide as boolean | undefined) ??
                  backGLBInfo.colision ??
                  true) ||
                item.universal
              ) {
                store.collisionableItems.add(item.id)
              }
            })
            } finally {
              popDrawGeometry()
            }

            // Completa el comportamiento específico de cada tipo de item una vez ya
            // colocado en la escena (snap padre, asiento/cojines, herencia de
            // materiales, encuadre de cámara, realineado de brazos y precarga de
            // texturas). Ver store/drawItemPostProcessing.ts.
            await runDrawItemPostProcessing({
              get,
              set,
              item,
              catalogID,
              type,
              backGLBInfo,
              options,
              pendingParentSnap,
              sourceCurrentObjectID,
              preserveWorldTransform,
              suppressAutoSeatAndCushion,
              suppressCameraFit,
              suppressExteriorArmRealign,
            })

            return item.id
          }

          const drawnId =
            getHistoryBatchDepth() > 0
              ? await executeDraw()
              : await runWithHistoryBatchAsync(executeDraw)
          resolve(drawnId)
        } catch (error) {
          reject(error)
        }
      })()
    })
  },
  addCutKits: () => {
    const store = get()
    const promises = []
    let modifiedCount = 0

    // Mapa para rastrear los kits de corte existentes por mueble original
    const existingKitsByItem = new Map()

    // Primero identificar los kits de corte existentes basándose en firstParentID
    store.items.forEach((item, id) => {
      if (
        item.catalogID &&
        typeof item.catalogID === 'string' &&
        item.catalogID.startsWith('CORTEKIT') &&
        item.firstParentID
      ) {
        // Este es un kit de corte, añadirlo al mapa por su padre
        if (!existingKitsByItem.has(item.firstParentID)) {
          existingKitsByItem.set(item.firstParentID, [])
        }
        existingKitsByItem.get(item.firstParentID).push(id)
      }
    })

    // Recorrer todos los items para añadir o actualizar kits
    store.items.forEach((item, id) => {
      // Verificar si es un linkedItem con morphs
      if (item.type === 'linkedItem' && item.morphs && Array.isArray(item.morphs)) {
        const changedDimensions = []

        // Buscar morphs con valores default diferentes a _default
        item.morphs.forEach((morph) => {
          if (
            morph &&
            typeof morph.name === 'string' &&
            morph._default !== undefined &&
            morph.default !== undefined &&
            Math.abs(morph._default - morph.default) > 0.1
          ) {
            changedDimensions.push(morph.name)
          }
        })

        // Si se encontraron dimensiones modificadasxp
        if (changedDimensions.length > 0) {
          modifiedCount++

          // Determinar qué catalogID usar basado en las dimensiones modificadas
          let catalogID
          if (changedDimensions.length > 1) {
            catalogID = 'CORTEKITESP'
          } else {
            switch (changedDimensions[0]) {
              case 'Alto':
                catalogID = 'CORTEKITALTO'
                break
              case 'Ancho':
                catalogID = 'CORTEKITANCHO'
                break
              case 'Fondo':
                catalogID = 'CORTEKITFONDO'
                break
              default:
                catalogID = 'CORTEKITESP' // Por defecto si es otra dimensión
                break
            }
          }

          // SIEMPRE eliminar todos los kits existentes para este mueble antes de crear uno nuevo
          const existingKits = existingKitsByItem.get(id) || []
          if (existingKits.length > 0) {
            existingKits.forEach((kitId) => {
              // Eliminar el kit existente
              set((state) => {
                state.items.delete(kitId)
                state.layers[state.currentLayer].items.delete(kitId)
                state.layers[state.currentLayer].selected.delete(kitId)
                state.sceneBoundingBoxes.delete(kitId)
                state.collisionableItems.delete(kitId)
              })
            })

            // Limpiar el mapa para este mueble
            existingKitsByItem.delete(id)
          }

          // En lugar de crear una matriz fija, usar la matriz del padre
          const kitMatrix = item.matrix.clone()
          const position = new Vector3()
          const quaternion = new Quaternion()
          const scale = new Vector3()
          kitMatrix.decompose(position, quaternion, scale)

          // Recomponer la matriz con la posición modificada
          const finalKitMatrix = new Matrix4().compose(position, quaternion, scale)

          // Usar drawItem para crear el linkedItemSon y cargar los datos del backend
          const promise = get()
            .drawItem(
              getId('linkedSon'), // Generar ID para linkedSon
              catalogID, // CatalogoID del kit
              '', // Icono
              false, // Visible (invisible ya que está en Y=100000)
              finalKitMatrix, // Matriz con posición (0, 100000, 0)
              'linkedSon', // Tipo: linkedSon
              undefined, // realCatalogID
              false, // shouldGoToBackHeight
            )
            .then((newItemId) => {
              // Después de crear el item, asociarlo como hijo del item padre
              set((state) => {
                const kitItem = state.items.get(newItemId)
                if (kitItem) {
                  kitItem.firstParentID = id // Establecer el padre
                  kitItem.depth = 1 // Profundidad 1 (hijo directo)
                  kitItem.snappedWall = '' // No asociado a pared
                }
                state.sceneBoundingBoxes.delete(newItemId)
                state.collisionableItems.delete(newItemId)
              })

              return { id: newItemId, originalId: id, catalogID }
            })
            .catch((error) => {
              console.error(`Error añadiendo kit de corte ${catalogID} para ${id}:`, error)
              return null
            })

          promises.push(promise)
        } else {
          // Si no hay dimensiones modificadas, eliminar cualquier kit existente para este mueble
          const existingKits = existingKitsByItem.get(id) || []
          if (existingKits.length > 0) {
            existingKits.forEach((kitId) => {
              set((state) => {
                state.items.delete(kitId)
                state.layers[state.currentLayer].items.delete(kitId)
                state.layers[state.currentLayer].selected.delete(kitId)
                state.sceneBoundingBoxes.delete(kitId)
                state.collisionableItems.delete(kitId)
              })
            })
            existingKitsByItem.delete(id)
          }
        }
      }
    })

    // Esperar a que todas las promesas se resuelvan
    return Promise.all(promises).then((results) => {
      const validResults = results.filter(Boolean)
      return validResults
    })
  },
  replaceItem: (
    selectedItemId: string,
    newCatalogID: string,
    icono: string = '',
    type: SceneObjects['type'] = 'items',
    realCatalogID?: string,
    options?: {
      preserveSeatCushionChildren?: boolean
      targetSeatHeightCm?: number
    },
  ): Promise<AnyID> => {
    return new Promise((resolve, reject) => {
      const executeReplace = async () => {
        const replacedUnitId = String(selectedItemId)
        let isParentUnitReplace = false
        let didBeginCameraPreservation = false

        try {
          // 1. Obtener el item seleccionado y guardar sus datos
          const storeBeforeReplace = get()
          const selectedItem = storeBeforeReplace.items.get(selectedItemId)
          if (!selectedItem) {
            reject(new Error(`No se encontró el item con ID ${selectedItemId}`))
            return
          }

          const isParentLinkedItem = isLinkedItem(selectedItem as any)
          const selectedItemIsArm = isArmSceneObject(selectedItem)
          isParentUnitReplace = isParentLinkedItem && !selectedItemIsArm

          if (isParentUnitReplace && parentUnitReplaceInFlight) {
            reject(new Error('Ya hay una sustitución de unidad en curso'))
            return
          }
          if (isParentUnitReplace) {
            parentUnitReplaceInFlight = true
            get().setUnitReplaceInFlight(true)
          }

          get().clearCameraFitToItems()
          get().beginCameraViewPreservation()
          didBeginCameraPreservation = true

          type LinkedArmSnapshot = LinkedArmMigrationSnapshot
          const linkedArmsToMigrate: LinkedArmSnapshot[] = []

          const resolveCatalogDepthCm = (catalogID: string): number => {
            const match = String(catalogID || '').match(/-(\d{2,3})$/)
            return match ? Number(match[1]) : 0
          }
          const resolveDesiredArmCatalog = (baseArmCode: string, parentItem: any): string => {
            const safeBase = String(baseArmCode || '').toUpperCase()
            if (!/^BR\d+$/i.test(safeBase)) return safeBase
            const parentCatalogText = String(parentItem?.catalogID || '')
            const parentDepthFromThickness = Number(parentItem?.thickness || 0)
            const parentDepthFromCatalogCm = resolveCatalogDepthCm(parentCatalogText)
            const parentDepthFromCatalogMm =
              parentDepthFromCatalogCm > 0 ? parentDepthFromCatalogCm * 10 : 0
            const parentDepthMm = Math.max(parentDepthFromThickness, parentDepthFromCatalogMm)
            if (parentDepthMm > 1000) return `${safeBase}-105`
            if (parentDepthFromCatalogCm === 100 || parentCatalogText.includes('-100'))
              return `${safeBase}-100`
            return safeBase
          }

          if (isParentLinkedItem && !selectedItemIsArm) {
            const parentPos = new Vector3()
            const parentQuat = new Quaternion()
            const parentXAxis = new Vector3(1, 0, 0)
            if ((selectedItem as any).matrix) {
              ;(selectedItem as any).matrix.decompose(parentPos, parentQuat, new Vector3())
              parentXAxis.applyQuaternion(parentQuat)
            }
            storeBeforeReplace.items.forEach((sceneItem: any, sceneId: AnyID) => {
              if (!sceneItem || sceneItem.type !== 'linkedItem') return
              if (!isArmSceneObject(sceneItem)) return
              if (String(sceneItem.firstParentID || '') !== String(selectedItemId)) return
              let preservedSideSign: 1 | -1 = 1
              if (sceneItem.matrix && (selectedItem as any).matrix) {
                const armPos = new Vector3()
                sceneItem.matrix.decompose(armPos, new Quaternion(), new Vector3())
                const toArm = armPos.clone().sub(parentPos)
                preservedSideSign = toArm.dot(parentXAxis) >= 0 ? 1 : -1
              }
              linkedArmsToMigrate.push({
                oldArmId: sceneId,
                oldCatalogID: String(sceneItem.catalogID || ''),
                preservedSideSign,
                depth: Number(sceneItem.depth || 1),
                preservedMatrix: sceneItem.matrix?.clone?.() ?? undefined,
              })
            })
          }

          // Guardar la matriz y otras propiedades importantes
          const matrix = selectedItem.matrix?.clone() || null
          const visible = selectedItem.visible
          const snappedWall = selectedItem.snappedWall

          let oldSeatSnapshot: any = null
          let oldCushionSnapshot: any = null
          const collectAutoGroupChildSnapshots = (childId: AnyID) => {
            const childItem = storeBeforeReplace.items.get(childId) as any
            if (!childItem || childItem.type !== 'linkedSon') return
            const ref = String(childItem.catalogID || childItem.referencia || '').toLowerCase()
            if (ref.includes('asiento') && !oldSeatSnapshot) oldSeatSnapshot = childItem
            if (ref.includes('cojin') && !oldCushionSnapshot) oldCushionSnapshot = childItem
          }

          const preserveSeatCushionChildren = Boolean(options?.preserveSeatCushionChildren)

          // 2. Eliminar el item seleccionado y, si es linkedItem, también sus linkedSon hijos
          const idsToDelete = new Set<AnyID>([selectedItemId as AnyID])
          if (isLinkedItem(selectedItem as any)) {
            const linkedChildren = Array.from((selectedItem as any).childs || [])
            linkedChildren.forEach((childId) => {
              const childItem = storeBeforeReplace.items.get(childId as AnyID) as any
              if (preserveSeatCushionChildren && isSeatOrCushionLinkedSon(childItem)) {
                collectAutoGroupChildSnapshots(childId as AnyID)
                return
              }
              idsToDelete.add(childId as AnyID)
              collectAutoGroupChildSnapshots(childId as AnyID)
            })

            // Fallback por si el set `childs` no está sincronizado.
            get().items.forEach((sceneItem, sceneId) => {
              if ((sceneItem as any)?.type !== 'linkedSon') return
              if ((sceneItem as any)?.firstParentID !== selectedItemId) return
              if (preserveSeatCushionChildren && isSeatOrCushionLinkedSon(sceneItem)) {
                collectAutoGroupChildSnapshots(sceneId as AnyID)
                return
              }
              idsToDelete.add(sceneId as AnyID)
              collectAutoGroupChildSnapshots(sceneId as AnyID)
            })
          }

          const shouldRecreateAutoGroupChildren = Boolean(
            isParentLinkedItem &&
              (oldSeatSnapshot || oldCushionSnapshot) &&
              !preserveSeatCushionChildren,
          )

          const inheritParentTextures = isParentUnitReplace
            ? cloneLinkedItemTexturesForInheritance(selectedItem)
            : null
          const inheritSeatTextures = oldSeatSnapshot
            ? cloneLinkedItemTexturesForInheritance(oldSeatSnapshot)
            : null
          const inheritCushionTextures = oldCushionSnapshot
            ? cloneLinkedItemTexturesForInheritance(oldCushionSnapshot)
            : null

          const drawType: SceneObjects['type'] =
            isParentLinkedItem && !selectedItemIsArm ? 'linkedItem' : type

          const newItemId = await get().drawItem(
            getId('Items'),
            newCatalogID,
            icono,
            visible,
            matrix,
            drawType,
            realCatalogID,
            false,
            {
              preserveSelection: true,
              suppressCameraFit: true,
              preserveWorldTransform: Boolean(matrix),
              suppressAutoSeatAndCushion:
                shouldRecreateAutoGroupChildren || preserveSeatCushionChildren,
              suppressExteriorArmRealign: isParentUnitReplace,
              inheritTexturesFrom: inheritParentTextures,
            },
          )

          restoreReplacedItemWorldTransform(set, get, selectedItem, newItemId as AnyID)

          if (options?.targetSeatHeightCm && isParentUnitReplace) {
            set((state) => {
              const parentItem = state.items.get(newItemId as AnyID) as any
              if (!parentItem) return
              parentItem.seatHeightCm = Number(options.targetSeatHeightCm)
            })
          }

          if (
            preserveSeatCushionChildren &&
            (oldSeatSnapshot || oldCushionSnapshot)
          ) {
            const newSeatHeightCm = Number(options?.targetSeatHeightCm || 0)
            set((state) => {
              const parentItem = state.items.get(newItemId as AnyID) as any
              if (!parentItem) return
              if (!parentItem.childs) parentItem.childs = new Set()
              if (newSeatHeightCm > 0) {
                parentItem.seatHeightCm = newSeatHeightCm
              }

              const relinkChild = (snap: any) => {
                if (!snap?.id) return
                const child = state.items.get(snap.id as AnyID) as any
                if (!child) return
                const placement = placementForSeatHeightChange(newSeatHeightCm)
                child.firstParentID = newItemId
                child.depth = 1
                child.canCollide = false
                child.x = placement.x
                child.y = placement.y
                child.z = placement.z
                child.matrix = placement.matrix
                parentItem.childs.add(snap.id as AnyID)
                enforceSeatCushionLocalCoords(child, parentItem)
              }

              if (oldSeatSnapshot) relinkChild(oldSeatSnapshot)
              if (oldCushionSnapshot) relinkChild(oldCushionSnapshot)
            })
          }

          // Solo eliminar la unidad original tras confirmar que la nueva se dibujó bien.
          purgeSceneObjectIds(set, idsToDelete)

          if (isParentUnitReplace) {
            const orphanIds: AnyID[] = []
            get().items.forEach((sceneItem, sceneId) => {
              if (String(sceneId) === String(newItemId)) return
              if (String((sceneItem as any)?.firstParentID || '') !== replacedUnitId) return
              if (isArmSceneObject(sceneItem)) return
              orphanIds.push(sceneId as AnyID)
            })
            if (orphanIds.length > 0) {
              purgeSceneObjectIds(set, orphanIds)
            }
          }

          // 4. Restaurar la relación con la pared si existía
          if (snappedWall) {
            get().editSnappedWall(newItemId, snappedWall)
          }

          const newParentAfterDraw = get().items.get(newItemId as AnyID) as any
          const newUnitCatalogId = newParentAfterDraw?.catalogID || newCatalogID || realCatalogID
          const replacingWithRelax =
            isParentUnitReplace &&
            (isRelaxIntegratedUnitByDefaults(newCatalogID) ||
              isRelaxIntegratedUnitByDefaults(realCatalogID) ||
              isRelaxIntegratedUnitByDefaults(newUnitCatalogId))

          const replacingWithSingleArmSeries =
            isParentUnitReplace &&
            !replacingWithRelax &&
            (isSingleArmSeries(newCatalogID) ||
              isSingleArmSeries(realCatalogID) ||
              isSingleArmSeries(newUnitCatalogId) ||
              // Unidades con mano («… D» / «… I»): un solo brazo, en el lado
              // contrario a su letra.
              allowedArmSideForUnit(newCatalogID) != null ||
              allowedArmSideForUnit(realCatalogID) != null ||
              allowedArmSideForUnit(newUnitCatalogId) != null)

          const migrateArmsPreserveWorldTransform = (armSnapshot: LinkedArmSnapshot) => {
            const liveArm = get().items.get(armSnapshot.oldArmId as AnyID) as any
            if (!liveArm) return

            const matrix =
              armSnapshot.preservedMatrix?.clone?.() ?? liveArm.matrix?.clone?.() ?? null
            if (!matrix) return

            const pos = decomposeMatrixPosition(matrix)
            get().editItem(armSnapshot.oldArmId, {
              matrix,
              x: pos.x,
              y: pos.y,
              z: pos.z,
              firstParentID: newItemId,
              depth: armSnapshot.depth,
            } as any)
            get().setOBB(armSnapshot.oldArmId as AnyID)
          }

          const migrateOneArmToNewParent = async (armSnapshot: (typeof linkedArmsToMigrate)[0]) => {
            const liveParent = get().items.get(newItemId as AnyID) as any
            if (!liveParent?.matrix) return

            const oldCatalogUpper = String(armSnapshot.oldCatalogID || '').toUpperCase()
            const armBaseMatch = oldCatalogUpper.match(/^BR(\d+)/i)
            const armBaseCode = armBaseMatch ? `BR${armBaseMatch[1]}` : oldCatalogUpper
            const desiredCatalog = resolveDesiredArmCatalog(armBaseCode, liveParent)

            let effectiveArmId: AnyID = armSnapshot.oldArmId
            if (desiredCatalog && desiredCatalog !== oldCatalogUpper) {
              effectiveArmId = await get().replaceItem(
                armSnapshot.oldArmId as string,
                desiredCatalog,
                '',
                'items',
              )
            }

            const liveArm = get().items.get(effectiveArmId) as any
            if (!liveArm?.matrix) return

            let placementSide = armSnapshot.preservedSideSign
            const placementEval = evaluateSingleArmSeriesPlacement(get().items, liveParent, {
              excludeArmId: String(effectiveArmId),
              getArmSideSign,
            })
            if (
              placementEval.allowed &&
              placementEval.appliesSingleArmSeriesRules &&
              String(placementEval.placement.unit.id) === String(newItemId)
            ) {
              placementSide = placementEval.placement.side
            }
            // Mano D/I del nuevo padre: forzar el lado contrario a su letra.
            const handedSideForMigration = allowedArmSideForUnit(liveParent?.catalogID)
            if (handedSideForMigration != null) {
              placementSide = handedSideForMigration
            }

            const approachMatrix = buildApproachMatrixForArm(liveParent, liveArm, placementSide, 0)
            if (!approachMatrix) return

            const approachPos = decomposeMatrixPosition(approachMatrix)
            get().editItem(effectiveArmId, {
              matrix: approachMatrix,
              x: approachPos.x,
              y: approachPos.y,
              z: approachPos.z,
              firstParentID: newItemId,
              depth: armSnapshot.depth,
            } as any)
            get().collideItems(effectiveArmId as AnyID, newItemId as AnyID)

            const settledArm = get().items.get(effectiveArmId) as any
            const settledParent = get().items.get(newItemId as AnyID) as any
            const fineMatrix =
              settledArm && settledParent
                ? buildBr12FineTunedMatrix(settledArm, settledParent, placementSide)
                : null
            if (fineMatrix) {
              const finePos = decomposeMatrixPosition(fineMatrix)
              get().editItem(effectiveArmId, {
                matrix: fineMatrix,
                x: finePos.x,
                y: finePos.y,
                z: finePos.z,
                firstParentID: newItemId,
                depth: armSnapshot.depth,
              } as any)
            }
          }

          if (preserveSeatCushionChildren && linkedArmsToMigrate.length > 0) {
            linkedArmsToMigrate.forEach((snap) => migrateArmsPreserveWorldTransform(snap))
          } else if (replacingWithRelax && linkedArmsToMigrate.length > 0) {
            // RELAX no hospeda BR: reanclar a un labio exterior de otra unidad, o eliminar.
            const armsToPurge: AnyID[] = []
            for (const armSnapshot of linkedArmsToMigrate) {
              const liveArm = get().items.get(armSnapshot.oldArmId as AnyID) as any
              const liveParent = get().items.get(newItemId as AnyID) as any
              if (!liveArm?.matrix || !liveParent?.matrix) {
                armsToPurge.push(armSnapshot.oldArmId)
                continue
              }

              const altLip = pickExteriorArmPlacement(get().items, liveParent, {
                excludeArmId: String(armSnapshot.oldArmId),
                getArmSideSign,
                preferredSideSign: armSnapshot.preservedSideSign,
              })

              if (
                !altLip?.unit?.id ||
                !altLip.unit.matrix ||
                isRelaxIntegratedUnitByDefaults(altLip.unit.catalogID) ||
                String(altLip.unit.id) === String(newItemId)
              ) {
                armsToPurge.push(armSnapshot.oldArmId)
                continue
              }

              const approachMatrix = buildApproachMatrixForArm(
                altLip.unit,
                liveArm,
                altLip.side,
                0,
              )
              if (!approachMatrix) {
                armsToPurge.push(armSnapshot.oldArmId)
                continue
              }

              const approachPos = decomposeMatrixPosition(approachMatrix)
              get().editItem(armSnapshot.oldArmId, {
                matrix: approachMatrix,
                x: approachPos.x,
                y: approachPos.y,
                z: approachPos.z,
                firstParentID: altLip.unit.id,
                depth: armSnapshot.depth,
              } as any)
              get().setOBB(armSnapshot.oldArmId as AnyID)
              get().collideItems(armSnapshot.oldArmId as AnyID, altLip.unit.id as AnyID)
            }

            if (armsToPurge.length > 0) {
              showArmRestrictionNotice(ARM_NOTICE_REPLACE_WITHOUT_ARMS())
              purgeSceneObjectIds(set, armsToPurge)
            }
          } else if (replacingWithSingleArmSeries && linkedArmsToMigrate.length > 0) {
            const placementEval = evaluateSingleArmSeriesPlacement(
              get().items,
              newParentAfterDraw,
              {
                getArmSideSign,
              },
            )
            const preferredSide =
              placementEval.allowed &&
              placementEval.appliesSingleArmSeriesRules &&
              String(placementEval.placement.unit.id) === String(newItemId)
                ? placementEval.placement.side
                : null

            const { keep, remove } = pickArmSnapshotToKeepForSingleArmSeries(
              linkedArmsToMigrate,
              preferredSide,
            )

            if (remove.length > 0) {
              showArmRestrictionNotice(ARM_NOTICE_ONLY_ONE_SIDE())
              purgeSceneObjectIds(
                set,
                remove.map((arm) => arm.oldArmId),
              )
            }

            if (keep) {
              try {
                await migrateOneArmToNewParent(keep)
              } catch (migrationError) {
                console.warn(
                  '[replaceItem] No se pudo migrar el brazo a serie de un brazo:',
                  migrationError,
                )
              }
            }
          } else if (linkedArmsToMigrate.length > 0) {
            const liveStore = get()
            const liveParent = liveStore.items.get(newItemId as AnyID) as any
            if (liveParent?.matrix) {
              const migratePromises = linkedArmsToMigrate.map(async (armSnapshot) => {
                const oldCatalogUpper = String(armSnapshot.oldCatalogID || '').toUpperCase()
                const armBaseMatch = oldCatalogUpper.match(/^BR(\d+)/i)
                const armBaseCode = armBaseMatch ? `BR${armBaseMatch[1]}` : oldCatalogUpper
                const desiredCatalog = resolveDesiredArmCatalog(armBaseCode, liveParent)

                let effectiveArmId: AnyID = armSnapshot.oldArmId
                if (desiredCatalog && desiredCatalog !== oldCatalogUpper) {
                  effectiveArmId = await get().replaceItem(
                    armSnapshot.oldArmId as string,
                    desiredCatalog,
                    '',
                    'items',
                  )
                }

                const liveArm = get().items.get(effectiveArmId) as any
                if (!liveArm?.matrix) return

                const approachMatrix = buildApproachMatrixForArm(
                  liveParent,
                  liveArm,
                  armSnapshot.preservedSideSign,
                  0,
                )
                if (!approachMatrix) return

                const approachPos = decomposeMatrixPosition(approachMatrix)
                get().editItem(effectiveArmId, {
                  matrix: approachMatrix,
                  x: approachPos.x,
                  y: approachPos.y,
                  z: approachPos.z,
                  firstParentID: newItemId,
                  depth: armSnapshot.depth,
                } as any)
                get().collideItems(effectiveArmId as AnyID, newItemId as AnyID)

                const settledArm = get().items.get(effectiveArmId) as any
                const settledParent = get().items.get(newItemId as AnyID) as any
                const fineMatrix =
                  settledArm && settledParent
                    ? buildBr12FineTunedMatrix(
                        settledArm,
                        settledParent,
                        armSnapshot.preservedSideSign,
                      )
                    : null
                if (fineMatrix) {
                  const finePos = decomposeMatrixPosition(fineMatrix)
                  get().editItem(effectiveArmId, {
                    matrix: fineMatrix,
                    x: finePos.x,
                    y: finePos.y,
                    z: finePos.z,
                    firstParentID: newItemId,
                    depth: armSnapshot.depth,
                  } as any)
                }
              })

              try {
                await Promise.all(migratePromises)
              } catch (migrationError) {
                console.warn('[replaceItem] No se pudieron migrar algunos brazos:', migrationError)
              }
            }
          }

          if (shouldRecreateAutoGroupChildren) {
            const liveParent = get().items.get(newItemId as AnyID) as any
            if (liveParent?.matrix) {
              const oldSeatCatalog = String(
                oldSeatSnapshot?.catalogID || oldSeatSnapshot?.referencia || '',
              ).toLowerCase()
              const oldCushionCatalog = String(
                oldCushionSnapshot?.catalogID || oldCushionSnapshot?.referencia || '',
              ).toLowerCase()
              const seatStitchType = oldSeatCatalog.includes('asientosin') ? 'sin' : 'con'
              const strippedBase = stripSeatHeightPrefix(newCatalogID).baseCatalog
              // RELAX con variante («167 BR3 D») debe usar base «167» para asiento/cojín.
              const childBaseCatalog =
                getRelaxBaseCode(strippedBase) || getRelaxBaseCode(newCatalogID) || strippedBase
              const targetSeatCatalog =
                seatStitchType === 'sin'
                  ? `asientosin ${childBaseCatalog}`
                  : `asiento ${childBaseCatalog}`
              const targetCushionCatalog = `${oldCushionCatalog.includes('cojinesr') ? 'cojinesR' : 'cojines'} ${childBaseCatalog}`

              const preservedCushionLocalY =
                oldCushionSnapshot &&
                Number.isFinite(Number(oldCushionSnapshot.y)) &&
                Math.abs(Number(oldCushionSnapshot.y)) <= 600
                  ? Number(oldCushionSnapshot.y)
                  : 0

              const drawLinkedSon = async (
                code: string,
                inheritTexturesFrom?: LinkedItemTextureInheritance | null,
              ) =>
                await get().drawItem(
                  getId('linkedSon'),
                  code,
                  icono,
                  true,
                  liveParent.matrix.clone(),
                  'linkedSon',
                  newCatalogID,
                  false,
                  {
                    preserveSelection: true,
                    suppressCameraFit: true,
                    preserveWorldTransform: true,
                    inheritTexturesFrom: inheritTexturesFrom ?? undefined,
                  },
                )

              try {
                let seatId: AnyID | null = null
                if (oldSeatSnapshot) {
                  try {
                    seatId = await drawLinkedSon(targetSeatCatalog, inheritSeatTextures)
                  } catch {
                    seatId = await drawLinkedSon(
                      targetSeatCatalog.charAt(0).toUpperCase() + targetSeatCatalog.slice(1),
                      inheritSeatTextures,
                    )
                  }
                }

                let cushionId: AnyID | null = null
                if (oldCushionSnapshot) {
                  try {
                    cushionId = await drawLinkedSon(targetCushionCatalog, inheritCushionTextures)
                  } catch {
                    cushionId = await drawLinkedSon(
                      targetCushionCatalog.charAt(0).toUpperCase() + targetCushionCatalog.slice(1),
                      inheritCushionTextures,
                    )
                  }
                }

                set((state) => {
                  const parentItem = state.items.get(newItemId as AnyID) as any
                  if (!parentItem) return
                  if (!parentItem.childs) parentItem.childs = new Set()
                  parentItem.seatStitchType = seatStitchType
                  if (seatId) {
                    const seatItem = state.items.get(seatId as AnyID) as any
                    if (seatItem) {
                      seatItem.firstParentID = newItemId
                      seatItem.depth = 1
                      seatItem.canCollide = false
                      seatItem.x = 0
                      seatItem.y = 0
                      seatItem.z = 0
                      seatItem.matrix = new Matrix4().setPosition(0, 0, 0)
                      parentItem.childs.add(seatId as AnyID)
                    }
                  }
                  if (cushionId) {
                    const cushionItem = state.items.get(cushionId as AnyID) as any
                    if (cushionItem) {
                      cushionItem.firstParentID = newItemId
                      cushionItem.depth = 1
                      cushionItem.canCollide = false
                      cushionItem.x = 0
                      cushionItem.y = preservedCushionLocalY
                      cushionItem.z = 0
                      cushionItem.matrix = new Matrix4().setPosition(0, preservedCushionLocalY, 0)
                      parentItem.childs.add(cushionId as AnyID)
                    }
                  }
                })
              } catch (autoGroupError) {
                console.warn('[replaceItem] No se pudieron recrear asiento/cojín:', autoGroupError)
              }
            }
          }

          if (isParentUnitReplace) {
            purgeSceneObjectIds(set, idsToDelete)
            // Segunda pasada: el ancho GLB del RELAX ya es definitivo → reflow + liberar labio izq.
            reflowSnappedRowAfterWidthReplace(set, get, selectedItem, newItemId as AnyID)
          }

          if (replacingWithSingleArmSeries) {
            const liveParentFinal = get().items.get(newItemId as AnyID) as any
            const lingeringArms = collectArmIdsLinkedToParent(
              get().items,
              String(newItemId),
              liveParentFinal,
            )
            if (lingeringArms.length > 1) {
              const snapshots = lingeringArms.map((armId) => {
                const arm = get().items.get(armId) as any
                const parent = liveParentFinal
                let preservedSideSign: 1 | -1 = 1
                if (arm?.matrix && parent?.matrix) {
                  const parentPos = decomposeMatrixPosition(parent.matrix)
                  const armPos = decomposeMatrixPosition(arm.matrix)
                  const parentQuat = new Quaternion()
                  parent.matrix.decompose(new Vector3(), parentQuat, new Vector3())
                  const parentXAxis = new Vector3(1, 0, 0).applyQuaternion(parentQuat)
                  preservedSideSign = armPos.clone().sub(parentPos).dot(parentXAxis) >= 0 ? 1 : -1
                }
                return {
                  oldArmId: String(armId),
                  oldCatalogID: String(arm?.catalogID || ''),
                  preservedSideSign,
                  depth: Number(arm?.depth || 1),
                }
              })
              const placementEval = evaluateSingleArmSeriesPlacement(get().items, liveParentFinal, {
                getArmSideSign,
              })
              const preferredSide =
                placementEval.allowed &&
                placementEval.appliesSingleArmSeriesRules &&
                String(placementEval.placement.unit.id) === String(newItemId)
                  ? placementEval.placement.side
                  : null
              const { keep, remove } = pickArmSnapshotToKeepForSingleArmSeries(
                snapshots,
                preferredSide,
              )
              if (remove.length > 0) {
                purgeSceneObjectIds(
                  set,
                  remove.map((arm) => arm.oldArmId),
                )
                showArmRestrictionNotice(ARM_NOTICE_ONLY_ONE_SIDE())
              }
            }
          }

          resolve(newItemId)
        } catch (error) {
          reject(error)
        } finally {
          if (isParentUnitReplace) {
            parentUnitReplaceInFlight = false
            get().setUnitReplaceInFlight(false)
          }
          if (didBeginCameraPreservation) {
            get().endCameraViewPreservation()
          }
        }
      }

      ;(async () => {
        try {
          const preliminaryItem = get().items.get(selectedItemId as AnyID)
          const shouldBatchParentUnitReplace =
            preliminaryItem &&
            isLinkedItem(preliminaryItem as any) &&
            !isArmSceneObject(preliminaryItem) &&
            getHistoryBatchDepth() === 0

          if (shouldBatchParentUnitReplace) {
            await runWithHistoryBatchAsync(executeReplace)
          } else {
            await executeReplace()
          }
        } catch (error) {
          reject(error)
        }
      })()
    })
  },
  setOBB: (id: AnyID) => {
    set((store) => {
      const item = store.items.get(id) as Item | LinkedItem
      if (!isItem(item) && !isLinkedItem(item)) return
      const centerPosition = toVector3()
      const quaternion = toQuaternion()
      item.matrix.decompose(centerPosition, quaternion, toVector3())
      const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
      itemCenter.applyQuaternion(quaternion).add(centerPosition)
      const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
      const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
      store.sceneBoundingBoxes.set(id, {
        boundingBox: new OBB(
          itemCenter,
          new Vector3(item.width / 2 + 200, item.height / 2 + 200, item.thickness / 2 + 200),
          rotationMatrix,
        ),
        type: 'items',
      })
    })
  },
  setOBBScene: () => {
    set((store) => {
      store.items.forEach((item, id) => {
        if (!isItem(item) && !isLinkedItem(item)) return
        const centerPosition = toVector3()
        const quaternion = toQuaternion()
        item.matrix.decompose(centerPosition, quaternion, toVector3())
        const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
        itemCenter.applyQuaternion(quaternion).add(centerPosition)
        const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
        const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
        store.sceneBoundingBoxes.set(id, {
          boundingBox: new OBB(
            itemCenter,
            new Vector3(item.width / 2 + 200, item.height / 2 + 200, item.thickness / 2 + 200),
            rotationMatrix,
          ),
          type: 'items',
        })
      })
    })
  },
  setSceneGLB: (catalogID: string, scene: any) => {
    set((store) => {
      store.sceneGLB.set(catalogID, { glb: scene, glbData: {} })
    })
  },
  setItemPasteId: (id: string) => {
    set((store) => {
      store.itemsCopyId = id
    })
  },
  editItem: <T extends Item>(id: string, config?: Partial<Record<keyof T, T[keyof T]>>) => {
    if (config) {
      const geometryOp =
        config.width !== undefined || config.height !== undefined || config.thickness !== undefined
          ? 'resize'
          : config.rotation !== undefined
            ? 'rotation'
            : 'editItem'
      const popGeometryOp = pushGeometryOperation(geometryOp)
      try {
      set((store) => {
        const items = store.items
        if (id === 'selected') {
          const ids = store.layers[store.currentLayer].selected
          ids.forEach((id) => {
            const item = store.items.get(id) as Item
            if (!item) return
            for (const [property, value] of Object.entries(config)) {
              item[property] = value
            }
            items.set(id, item)
          })
        } else {
          const item = store.items.get(id) as Item
          if (!item) return

          const matrix = item.matrix as Matrix4
          const centerPosition = new Vector3()
          const quaternion = new Quaternion()
          matrix.decompose(centerPosition, quaternion, new Vector3())

          const itemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])

          itemCenter.applyQuaternion(quaternion).add(centerPosition)

          // Convertir la rotación del quaternion a una Matrix4 y extraer la Matrix3
          const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(quaternion)
          const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

          const mode = store.mode

          // Aplicar restricción de altura en modo 3D cuando se actualiza y o matrix
          let restrictedY: number | undefined = undefined
          if (
            (config.y !== undefined || config.matrix !== undefined) &&
            (mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS')
          ) {
            // Obtener el Y actual: de config.y o de la matriz si viene en config.matrix
            let currentY = config.y as number | undefined
            if (currentY === undefined && config.matrix !== undefined) {
              const matrixPos = toVector3()
              ;(config.matrix as Matrix4).decompose(matrixPos, toQuaternion(), toVector3())
              currentY = matrixPos.y
            }
            if (currentY === undefined) {
              currentY = item.y
            }

            restrictedY = currentY

            // Restringir mínimo a 0 siempre
            restrictedY = Math.max(0, restrictedY)

            // Si el item tiene altura, aplicar restricción máxima
            if (item.height) {
              // Obtener altura máxima de la pared
              let maxWallHeight: number | null = null

              // Si el item está pegado a una pared, usar esa pared
              if (item.snappedWall) {
                const wall = store.items.get(item.snappedWall) as Walls
                if (isWall(wall)) {
                  maxWallHeight = wall.height
                }
              } else {
                // Si no está pegado, obtener la altura máxima de todas las paredes
                let maxHeight = 0
                store.items.forEach((sceneObject) => {
                  if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                    maxHeight = sceneObject.height
                  }
                })
                maxWallHeight = maxHeight > 0 ? maxHeight : null
              }

              // Restringir máximo considerando la altura del mueble
              if (maxWallHeight !== null) {
                const maxYAllowed = maxWallHeight - item.height
                restrictedY = Math.min(restrictedY, maxYAllowed)
              }
            }

            // Actualizar config.y y config.matrix con el valor restringido
            if (config.y !== undefined) {
              config.y = restrictedY as T[keyof T]
            }
            if (config.matrix !== undefined) {
              const matrix = config.matrix as Matrix4
              const matrixPos = toVector3()
              const matrixRot = toQuaternion()
              const matrixScale = toVector3()
              matrix.decompose(matrixPos, matrixRot, matrixScale)
              const restrictedMatrix = new Matrix4()
                .makeRotationFromQuaternion(matrixRot)
                .setPosition(matrixPos.x, restrictedY, matrixPos.z)
              config.matrix = restrictedMatrix as T[keyof T]
            }
          }

          for (const [property, value] of Object.entries(config)) {
            item[property] = value
          }

          if (item.type === 'linkedSon') {
            const parentForLocal = (item as any).firstParentID
              ? (store.items.get((item as any).firstParentID as AnyID) as any)
              : undefined
            enforceSeatCushionLocalCoords(item, parentForLocal)
          }

          // Al mover el padre, re-sincronizar hijos asiento/cojín en espacio local.
          if (item.type === 'linkedItem' && item.childs?.size) {
            const parentItem = item as any
            parentItem.childs.forEach((childId: AnyID) => {
              const childItem = store.items.get(childId as AnyID) as any
              if (!childItem) return
              enforceSeatCushionLocalCoords(childItem, parentItem)
              store.items.set(childId as AnyID, childItem)
            })
          }

          // Si se aplicó restricción pero no se actualizó y directamente, actualizarlo ahora
          if (restrictedY !== undefined && config.y === undefined) {
            item.y = restrictedY
            // También actualizar la matriz si existe
            if (item.matrix) {
              const matrixPos = toVector3()
              const matrixRot = toQuaternion()
              const matrixScale = toVector3()
              item.matrix.decompose(matrixPos, matrixRot, matrixScale)
              item.matrix.setPosition(matrixPos.x, restrictedY, matrixPos.z)
            }
          }

          // CORRECCIÓN ABSOLUTA: asegurar que item.y NUNCA sea negativo en modo 3D o alzados
          // Esta es la última línea de defensa - se ejecuta SIEMPRE después de cualquier actualización
          if (mode === 'MODE_3D_VIEW' || mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS') {
            if (item.y < 0) {
              item.y = 0
              // Actualizar la matriz también para mantener consistencia
              const matrixPos = toVector3()
              const matrixRot = toQuaternion()
              const matrixScale = toVector3()
              item.matrix.decompose(matrixPos, matrixRot, matrixScale)
              item.matrix.setPosition(matrixPos.x, 0, matrixPos.z)
            }
          }

          items.set(id, item)

          const finalMatrix = item.matrix as Matrix4
          const finalCenterPosition = toVector3()
          const finalQuaternion = toQuaternion()
          finalMatrix.decompose(finalCenterPosition, finalQuaternion, toVector3())
          const finalItemCenter = toVector3([item.width / 2, item.height / 2, item.thickness / 2])
          finalItemCenter.applyQuaternion(finalQuaternion).add(finalCenterPosition)
          const finalRotationMatrix4 = new Matrix4().makeRotationFromQuaternion(finalQuaternion)
          const finalRotationMatrix = new Matrix3().setFromMatrix4(finalRotationMatrix4)

          store.sceneBoundingBoxes.set(id, {
            boundingBox: new OBB(
              finalItemCenter,
              toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
              finalRotationMatrix,
            ),
            type: 'items',
          })
        }
      })
      } finally {
        popGeometryOp()
      }
    }
  },
  editItemPosition: (id: string, x: number, z: number, y?: number) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      if (!item.visible) item.visible = true
      item.matrix = item.matrix.clone().setPosition(x, item.y, z)
    })
  },
  editItemHeight: (height: number, id?: string) => {
    set((store) => {
      const mode = store.mode

      if (id) {
        const item = store.items.get(id) as Item
        if (!item) return

        // En modo alzados o 3D, restringir entre 0 y altura máxima considerando la altura del mueble
        if (mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS' || mode === 'MODE_3D_VIEW') {
          height = Math.max(0, height)

          // Obtener altura máxima de la pared
          let maxWallHeight: number | null = null

          // Si el item está pegado a una pared, usar esa pared
          if (item.snappedWall && item.height) {
            const wall = store.items.get(item.snappedWall) as Walls
            if (isWall(wall)) {
              maxWallHeight = wall.height
            }
          } else if (item.height) {
            // Si no está pegado, obtener la altura máxima de todas las paredes
            let maxHeight = 0
            store.items.forEach((sceneObject) => {
              if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                maxHeight = sceneObject.height
              }
            })
            maxWallHeight = maxHeight > 0 ? maxHeight : null
          }

          // Restringir máximo considerando la altura del mueble
          // Si la pared tiene altura 2500 y el mueble tiene altura 800, el máximo Y es 2500 - 800 = 1700
          if (maxWallHeight !== null && item.height) {
            const maxYAllowed = maxWallHeight - item.height
            height = Math.min(height, maxYAllowed)
          }
        }

        const pos = toVector3()
        const rotation = toQuaternion()
        const scale = toVector3()
        item.matrix.decompose(pos, rotation, scale)

        // CORRECCIÓN FINAL: asegurar que height nunca sea negativo (última línea de defensa)
        const finalHeight = Math.max(0, height)

        // Para luces, NO reconstruir la matriz - solo cambiar la posición Y
        // Esto preserva la rotación exacta que ya está en la matriz
        if (item.type === 'lights') {
          const rotationAntes = item.rotation

          console.log('🔧 editItemHeight - LUZ (solo cambiando Y):', {
            id: item.id,
            catalogID: item.catalogID,
            rotationFromStore: item.rotation,
            heightAntes: pos.y,
            heightNuevo: finalHeight,
          })

          // Solo cambiar la posición Y sin reconstruir la matriz completa
          // Esto preserva la rotación exacta
          item.matrix.setPosition(pos.x, finalHeight, pos.z)
          item.y = finalHeight

          // Asegurar que la rotación NO cambie
          if (rotationAntes !== item.rotation) {
            console.error('🔧 editItemHeight - ERROR: La rotación cambió!', {
              rotationAntes,
              rotationDespues: item.rotation,
            })
            // Restaurar la rotación original
            item.rotation = rotationAntes
          }

          console.log('🔧 editItemHeight - DESPUÉS:', {
            id: item.id,
            rotationEnStore: item.rotation,
            y: item.y,
          })
        } else {
          // Para otros items, reconstruir la matriz normalmente
          item.matrix.makeRotationFromQuaternion(rotation)
          item.matrix.setPosition(pos.x, finalHeight, pos.z)
          item.matrix.scale(scale)
          item.y = finalHeight
        }
      } else {
        const selectedItems = store.layers[store.currentLayer].selected
        selectedItems.forEach((id) => {
          const item = store.items.get(id) as Item
          if (!item) return

          let finalHeight = height

          // En modo alzados o 3D, restringir entre 0 y altura máxima considerando la altura del mueble
          if (mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS' || mode === 'MODE_3D_VIEW') {
            finalHeight = Math.max(0, finalHeight)

            // Obtener altura máxima de la pared
            let maxWallHeight: number | null = null

            // Si el item está pegado a una pared, usar esa pared
            if (item.snappedWall && item.height) {
              const wall = store.items.get(item.snappedWall) as Walls
              if (isWall(wall)) {
                maxWallHeight = wall.height
              }
            } else if (item.height) {
              // Si no está pegado, obtener la altura máxima de todas las paredes
              let maxHeight = 0
              store.items.forEach((sceneObject) => {
                if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                  maxHeight = sceneObject.height
                }
              })
              maxWallHeight = maxHeight > 0 ? maxHeight : null
            }

            // Restringir máximo considerando la altura del mueble
            // Si la pared tiene altura 2500 y el mueble tiene altura 800, el máximo Y es 2500 - 800 = 1700
            if (maxWallHeight !== null && item.height) {
              const maxYAllowed = maxWallHeight - item.height
              finalHeight = Math.min(finalHeight, maxYAllowed)
            }
          }

          const pos = toVector3()
          item.matrix.decompose(pos, toQuaternion(), toVector3())
          // CORRECCIÓN FINAL: asegurar que finalHeight nunca sea negativo (última línea de defensa)
          const correctedHeight = Math.max(0, finalHeight)
          item.matrix.setPosition(pos.x, correctedHeight, pos.z)
          item.y = correctedHeight
        })
      }
    })
  },
  incrementItemZ: (id: AnyID, z: number) => {
    const axis = 'z'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()
      const rotation = toQuaternion()

      item.matrix = addItemOffset(item.matrix, z, axis)
      item.matrix.decompose(pos, rotation, toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.z = pos.z
      item.y = pos.y
      item.x = pos.x

      // Si es una luz, actualizar también el campo rotation desde la matriz
      if (item.type === 'lights') {
        const euler = new Euler().setFromQuaternion(rotation, 'YXZ')
        let rotationDegrees = euler.y * (180 / Math.PI)
        rotationDegrees = ((rotationDegrees % 360) + 360) % 360
        rotationDegrees = Math.round(rotationDegrees)

        // Si obtenemos 270 (que es -90) pero la store tiene 0 o no está definida, usar 90
        // Esto corrige la inconsistencia entre makeRotationY(radians(90)) y editItemRotation que usa radians(-rotation)
        if (
          rotationDegrees === 270 &&
          (item.rotation === 0 || item.rotation === undefined || item.rotation === null)
        ) {
          rotationDegrees = 90
        }

        item.rotation = rotationDegrees
      }
    })
  },
  incrementItemY: (selectedItems: Set<string>, y: number) => {
    const axis = 'y'
    set((store) => {
      const mode = store.mode

      selectedItems.forEach((id) => {
        const item = store.items.get(id) as Item
        if (!item) return

        const pos = toVector3()

        item.matrix = addItemOffset(item.matrix, y, axis)
        item.matrix.decompose(pos, toQuaternion(), toVector3())

        let newY = pos.y

        // En modo alzados o 3D, restringir entre 0 y altura máxima considerando la altura del mueble
        if (mode === 'MODE_FORCE_ALZADOS' || mode === 'MODE_ALZADOS' || mode === 'MODE_3D_VIEW') {
          // Restringir mínimo a 0 siempre
          newY = Math.max(0, newY)

          // Si el item tiene altura, aplicar restricción máxima
          if (item.height) {
            // Obtener altura máxima de la pared
            let maxWallHeight: number | null = null

            // Si el item está pegado a una pared, usar esa pared
            if (item.snappedWall) {
              const wall = store.items.get(item.snappedWall) as Walls
              if (isWall(wall)) {
                maxWallHeight = wall.height
              }
            } else {
              // Si no está pegado, obtener la altura máxima de todas las paredes
              let maxHeight = 0
              store.items.forEach((sceneObject) => {
                if (isWall(sceneObject) && sceneObject.height > maxHeight) {
                  maxHeight = sceneObject.height
                }
              })
              maxWallHeight = maxHeight > 0 ? maxHeight : null
            }

            // Restringir máximo considerando la altura del mueble
            // Si la pared tiene altura 2500 y el mueble tiene altura 800, el máximo Y es 2500 - 800 = 1700
            if (maxWallHeight !== null) {
              const maxYAllowed = maxWallHeight - item.height
              newY = Math.min(newY, maxYAllowed)
            }
          }
        }

        // CORRECCIÓN FINAL: asegurar que newY nunca sea negativo (última línea de defensa)
        const finalY = Math.max(0, newY)

        item.matrix.setPosition(pos.x, finalY, pos.z)
        item.z = pos.z
        item.y = finalY
        item.x = pos.x

        // Si es una luz, NO recalcular la rotación desde la matriz
        // La rotación debe mantenerse igual cuando solo se cambia la posición Y
        // Solo actualizar si la rotación no está definida en la store
        if (item.type === 'lights') {
          if (item.rotation === undefined || item.rotation === null) {
            // Solo calcular desde la matriz si no está definida
            const rotation = toQuaternion()
            const scale = toVector3()
            item.matrix.decompose(pos, rotation, scale)
            const euler = new Euler().setFromQuaternion(rotation, 'YXZ')
            let rotationDegrees = euler.y * (180 / Math.PI)
            rotationDegrees = ((rotationDegrees % 360) + 360) % 360
            rotationDegrees = Math.round(rotationDegrees)

            // Si obtenemos 270 (que es -90) pero la store tiene 0 o no está definida, usar 90
            // Esto corrige la inconsistencia entre makeRotationY(radians(90)) y editItemRotation que usa radians(-rotation)
            if (
              rotationDegrees === 270 &&
              (item.rotation === 0 || item.rotation === undefined || item.rotation === null)
            ) {
              rotationDegrees = 90
            }

            item.rotation = rotationDegrees
          }
          // Si la rotación ya está definida en la store, mantenerla (no recalcular)
        }
      })
    })
  },
  incrementItemX: (id: AnyID, x: number) => {
    const axis = 'x'
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return

      const pos = toVector3()
      const rotation = toQuaternion()

      item.matrix = addItemOffset(item.matrix, x, axis)
      item.matrix.decompose(pos, rotation, toVector3())
      item.matrix.setPosition(pos.x, pos.y, pos.z)
      item.z = pos.z
      item.y = pos.y
      item.x = pos.x

      // Si es una luz, actualizar también el campo rotation desde la matriz
      if (item.type === 'lights') {
        const euler = new Euler().setFromQuaternion(rotation, 'YXZ')
        let rotationDegrees = euler.y * (180 / Math.PI)
        rotationDegrees = ((rotationDegrees % 360) + 360) % 360
        rotationDegrees = Math.round(rotationDegrees)

        // Si obtenemos 270 (que es -90) pero la store tiene 0 o no está definida, usar 90
        // Esto corrige la inconsistencia entre makeRotationY(radians(90)) y editItemRotation que usa radians(-rotation)
        if (
          rotationDegrees === 270 &&
          (item.rotation === 0 || item.rotation === undefined || item.rotation === null)
        ) {
          rotationDegrees = 90
        }

        item.rotation = rotationDegrees
      }
    })
  },
  editItemRotation: (id: string, rotation: number) => {
    const popGeometryOp = pushGeometryOperation('rotation')
    try {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      // Extraer la posición actual de la matriz
      const position = toVector3()
      const scale = toVector3()
      item.matrix.decompose(position, new Quaternion(), scale)
      // Crear nueva matriz con la rotación especificada y mantener posición y escala
      item.matrix.makeRotationY(radians(-rotation))
      item.matrix.setPosition(position)
      item.matrix.scale(scale)
      // Actualizar el campo rotation en el item
      item.rotation = rotation
    })
    } finally {
      popGeometryOp()
    }
  },

  setItemVisible: (id: string) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      if (!item.visible) item.visible = true
    })
  },
  editItemZone: <T extends Item>(id: string, config?: Partial<Record<keyof T, T[keyof T]>>) => {
    set((store) => {
      if (!config) return
      const item = store.items.get(id) as Item

      if (!item) return

      const property = Object.keys(config)[0]

      if (config[property].value && config[property].id) {
        item.textures.forEach((prop: { zone: string }, propIndex: number) => {
          if (property === prop.zone) {
            //@ts-expect-error
            item.textures[propIndex].value = config[property].value
            //@ts-expect-error
            item.textures[propIndex].id = config[property].id
            item.textures[propIndex].idTexturas = config[property].idTexturas
            item.textures[propIndex].idGruposDeTexturas = config[property].idGruposDeTexturas
            item.textures[propIndex].nombre = config[property].nombre
          }
        })
      }

      Object.entries(item.zones).forEach(([zoneID, zone]: Zone, zoneIndex: number) => {
        console
        if (property === zone.material?.name) {
          const {
            color,
            roughness,
            mirror,
            repeatX,
            repeatY,
            metalness,
            opacity,
            envMapIntensity,
            displacement,
            displacementMap,
            bumpScale,
            map,
          } = config[property]

          const material = Object.values(item.zones)[zoneIndex].material

          const finalRepeatX = repeatX //>= 0.001 ? repeatX / 1000 : repeatX
          const finalRepeatY = repeatY //>= 0.001 ? repeatY / 1000 : repeatY

          const textureUrl = map ?? material.url ?? material.map
          item.zones[zoneID].material = {
            ...item.zones[zoneID].material,
            name: property,
            color: resolveApiTextureColor(
              color ? (validateHex(color) ? color : undefined) : material.color,
              Boolean(textureUrl),
            ),
            roughness: roughness ?? material.roughness,
            mirror: mirror ?? material.mirror,
            repeatX: finalRepeatX ?? material.repeatX,
            repeatY: finalRepeatY ?? material.repeatY,
            map: map ?? material.map,
            metalness: metalness ?? material.metalness,
            opacity: opacity ?? material.opacity,
            envMapIntensity: envMapIntensity ?? material.envMapIntensity,
            displacement: displacement ?? material.displacement,
            displacementMap: displacementMap ?? material.displacementMap,
            bumpScale: bumpScale ?? material.bumpScale,
            transparent: opacity < 1 ? true : false,
            depthWrite: opacity < 1 ? false : true,
            scale: toVector3([1, 1, 1]),
          }
        }
      })
    })
  },
  editGLBSize: (id: string, sizeVector: Vector3) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      item.glbSize = sizeVector
    })
  },
  editSnappedWall: (id: string, wallID?: string) => {
    set((store) => {
      const item = store.items.get(id) as Item
      const wall = store.items.get(wallID) as Walls
      if (!(isItem(item) || !isLinkedItem(item)) || !isWall(wall) || !id || !wallID) return
      item.snappedWall = wallID
      if (item.canCollide !== false && item.tipoNormalizado === 'MAL' && wall.highItems) {
        wall.highItems.add(item.id)
      }
      if (
        item.canCollide !== false &&
        (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') &&
        wall.lowItems
      ) {
        wall.lowItems && wall.lowItems.add(item.id)
      }
      const area = wall.areas
      if (!item.tipoNormalizado || !dictionary[item.tipoNormalizado]) return
      area.forEach((areaID) => {
        const currArea = store.areas.get(areaID)
        if (isArea(currArea)) {
          currArea.sides.forEach((singleWall) => {
            if (singleWall === wallID) return console.log('Same wall found', { wallID, singleWall }) // Si es la misma wall que está snapeando pasamos
            const singleWallObject = store.items.get(singleWall) as Walls
            if (isWall(singleWall)) {
              // Sino, buscamos si cualquier otra pared tiene este objeto pegado
              singleWallObject[dictionary[item.tipoNormalizado]].delete(id)
            }
          })
        }
      })
    })
  },
  removeSnappedWall: (id: string) => {
    set((store) => {
      const selectedIds = store.layers[store.currentLayer].selected as Set<AnyID>
      const targets = new Set<AnyID>()
      if (id) targets.add(id as AnyID)
      selectedIds?.forEach((selectedId) => targets.add(selectedId))

      targets.forEach((targetId) => {
        const item = store.items.get(targetId) as Item | LinkedItem
        const wall = store.items.get(item?.snappedWall ?? '') as Walls
        if (!item || (!isItem(item) && !isLinkedItem(item))) return
        if (!isWall(wall)) return

        if (item.tipoNormalizado === 'MAL') {
          wall.highItems.delete(item.id)
        }
        if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
          wall.lowItems.delete(item.id)
        }
        item.snappedWall = ''
      })
    })
  },
  removeSnappedPartition: (id: string) => {
    set((store) => {
      const selectedIds = store.layers[store.currentLayer].selected as Set<AnyID>
      const targets = new Set<AnyID>()
      if (id) targets.add(id as AnyID)
      selectedIds?.forEach((selectedId) => targets.add(selectedId))

      targets.forEach((targetId) => {
        const item = store.items.get(targetId) as Item | LinkedItem
        const partition = store.items.get(item?.snappedWall ?? '') as Partition
        if (!item || (!isItem(item) && !isLinkedItem(item))) return
        if (!isPartition(partition)) return

        console.log('[removeSnappedPartition]', {
          targetId,
          snappedWall: item.snappedWall,
          tipo: item.tipoNormalizado,
        })

        if (item.tipoNormalizado === 'MAL') {
          partition.highItems.delete(item.id)
        }
        if (item.tipoNormalizado === 'MBJ' || item.tipoNormalizado === 'COL') {
          partition.lowItems.delete(item.id)
        }
        item.snappedWall = ''
      })
    })
  },
  editItemAngle: (id: AnyID, angle: number) => {
    set((store) => {
      const item = store.items.get(id) as Item | LinkedItem
      if (!isItem(item) && !isLinkedItem(item)) return
      const position = toVector3()
      const nextMatrix = item.matrix.clone()
      nextMatrix.decompose(position, new Quaternion(), toVector3())
      nextMatrix.makeRotationY(radians(-angle))
      nextMatrix.setPosition(position)
      store.items.set(id, {
        ...item,
        matrix: nextMatrix,
        rotation: angle,
      })
    })
  },
  editItemAngleX: (id: AnyID, angle: number) => {
    set((store) => {
      const item = store.items.get(id) as Item | LinkedItem
      if (!isItem(item) && !isLinkedItem(item)) return
      const position = toVector3()
      const nextMatrix = item.matrix.clone()
      nextMatrix.decompose(position, new Quaternion(), toVector3())
      nextMatrix.makeRotationX(radians(-angle))
      nextMatrix.setPosition(position)
      store.items.set(id, {
        ...item,
        matrix: nextMatrix,
      })
    })
  },
  editItemAngleZ: (id: AnyID, angle: number) => {
    set((store) => {
      const item = store.items.get(id) as Item | LinkedItem
      if (!isItem(item) && !isLinkedItem(item)) return
      const position = toVector3()
      const nextMatrix = item.matrix.clone()
      nextMatrix.decompose(position, new Quaternion(), toVector3())
      nextMatrix.makeRotationZ(radians(-angle))
      nextMatrix.setPosition(position)
      store.items.set(id, {
        ...item,
        matrix: nextMatrix,
      })
    })
  },
  setIsBlocked: (blocked: boolean, id: AnyID) => {
    set((store) => {
      const item = store.items.get(id) as Item
      if (!item) return
      item.isBlocked = blocked
    })
  },
  setCollision: (collision: boolean, id: AnyID) => {
    set((store) => {
      const item = store.items.get(id) as Item
      const collisionableItems = store.collisionableItems
      if (!item) return
      item.canCollide = collision
      if (!collision && item.snappedWall) {
        const snappedObject = store.items.get(item.snappedWall) as Walls | Partition | undefined
        if (snappedObject && 'highItems' in snappedObject && 'lowItems' in snappedObject) {
          snappedObject.highItems?.delete(id)
          snappedObject.lowItems?.delete(id)
        }
      }
      if (collisionableItems.has(id)) {
        collisionableItems.delete(id)
      } else {
        collisionableItems.add(id)
      }
    })
  },
  remoconsCollision: (collision: boolean, id: string, isShiftKey: boolean) => {
    set((store) => {
      const item = store.items.get(id)
      const collisionableItems = store.collisionableItems
      if (isShiftKey) {
        if (!item) return
        item.canCollide = collision
        collisionableItems.delete(id)
      } else {
        collisionableItems.add(id)
        item.canCollide = !collision
      }
    })
  },
  collideItems: (itemId: AnyID, itemId1: AnyID, options?: { forcedSide?: 'left' | 'right'; proposedPosition?: { x: number; y: number; z: number } }) => {
    const snapResult: { snapped: boolean; side: 'left' | 'right' | null } = {
      snapped: false,
      side: null,
    }

    const popSnap = pushGeometryOperation('snap')
    try {
    set((store) => {
      const item = store.items.get(itemId) as LinkedItem
      const item1 = store.items.get(itemId1) as LinkedItem
      if (!item || !item1) return
      const item1Catalog = String(item1.catalogID ?? '')
        .trim()
        .toLowerCase()
      const isRinconero = isRinconeroCatalog(item1Catalog)
      const isFinalOreja = isOreja(item1Catalog)
      const isFinalOrejaIzquierda = isFinalOreja && item1Catalog.endsWith('d')
      const isFinalOrejaDerecha = isFinalOreja && item1Catalog.endsWith('i')
      const itemCatalog = String(item.catalogID ?? '')
        .trim()
        .toLowerCase()
      const draggedIs166 = itemCatalog.startsWith('166-')
      const item1Is166 = item1Catalog.startsWith('166-')
      // Brazo BR arrastrado sobre una unidad Corner XL BRAZO (181-185): NO debe
      // aplicarse el snap perpendicular de rinconero (giro 90° + desplazamiento en
      // profundidad). El brazo va pegado lateralmente (flush) a su lado, igual que
      // en las orejas. Sin esta exclusión, el sufijo «d/i» del 181 lo clasifica como
      // rinconero izquierdo/derecho y lo coloca en perpendicular (en L).
      const isArmDragged = /^br\d+(-\d+)?$/i.test(itemCatalog)
      const isCornerXLBrazoArmSnap = isArmDragged && isCornerXLBrazoCatalog(item1Catalog)
      const anchorHand = rinconeroHandednessFromItem(item1)
      const anchorIsModularCorner = isModularCornerSnapCatalog(item1Catalog)
      const anchorIsRinconeroLeft =
        anchorIsModularCorner &&
        !isOreja(item1Catalog) &&
        !isCornerXLBrazoArmSnap &&
        isModularCornerIzquierda(item1Catalog, anchorHand)
      const anchorIsRinconeroRight =
        anchorIsModularCorner &&
        !isOreja(item1Catalog) &&
        !isCornerXLBrazoArmSnap &&
        isModularCornerDerecha(item1Catalog, anchorHand)
      const itemWidth = item.width
      const item1Width = item1.width
      const item1Matrix = item1.matrix.clone()
      const item1Pos = new Vector3()
      const item1Quat = new Quaternion()
      item1Matrix.decompose(item1Pos, item1Quat, new Vector3())
      const item1XAxis = new Vector3(1, 0, 0).applyQuaternion(item1Quat)

      const dragReferencePos = options?.proposedPosition
        ? new Vector3(options.proposedPosition.x, options.proposedPosition.y, options.proposedPosition.z)
        : (() => {
            const p = new Vector3()
            item.matrix.decompose(p, new Quaternion(), new Vector3())
            return p
          })()

      let directionMultiplier =
        item1XAxis.dot(
          new Vector3(dragReferencePos.x - item1Pos.x, 0, dragReferencePos.z - item1Pos.z),
        ) < 0
          ? -1
          : 1
      let snapSide: 'left' | 'right' = directionMultiplier < 0 ? 'left' : 'right'
      if (options?.forcedSide) {
        snapSide = options.forcedSide
        directionMultiplier = snapSide === 'left' ? -1 : 1
      }

      const forceSnapSide = (side: 'left' | 'right') => {
        snapSide = side
        directionMultiplier = side === 'left' ? -1 : 1
      }

      // Variables que pueden ser modificadas por la lógica especial cuando se pega a rinconeros
      let newPosition
      const newQuaternion = item1Quat.clone() // Por defecto usa la misma rotación
      let placedAtRowExterior = false

      // Obtener la posición actual del item para casos especiales (MAL sobre MBJ/MAL, MBJ bajo MAL)
      const itemCurrentPos = dragReferencePos.clone()

      const draggedIsStraightModule = isStraightModularSnapCatalog(itemCatalog)
      let appliedStraightOn166 = false
      let appliedCornerXL = false

      // Un solo camino para cualquier módulo recto (160, RELAX, …) contra 166:
      // composeStraightModuleSnapOnRinconero (pata L o fila lateral según snapSide).
      let snapAnchorId = String(itemId1)
      let snapAnchor = item1

      const resolveItemById = (id: string) => store.items.get(id as AnyID) as any

      /**
       * Brazo BR* ↔ unidad base: settle a tope (flush) con el módulo.
       * No deja la distancia de aproximación (+300) ni escribe el brazo en
       * `snappedSides` del módulo (los labios de fila solo son entre unidades).
       */
      if (isArmSceneObject(item) && !isArmSceneObject(item1)) {
        const sideSign: 1 | -1 =
          options?.forcedSide === 'left'
            ? -1
            : options?.forcedSide === 'right'
              ? 1
              : directionMultiplier < 0
                ? -1
                : 1
        snapSide = sideSign < 0 ? 'left' : 'right'

        const previousParentId = (item as any).firstParentID as AnyID | undefined
        if (previousParentId && String(previousParentId) !== String(itemId1)) {
          clearArmFromUnitSnappedSides(
            store.items.get(previousParentId) as any,
            itemId,
          )
        }
        clearArmFromUnitSnappedSides(item1 as any, itemId)

        const flushMatrix = buildFlushMatrixForArm(item1, item, sideSign)
        if (!flushMatrix) return

        const settlePos = new Vector3()
        const settleQuat = new Quaternion()
        flushMatrix.decompose(settlePos, settleQuat, new Vector3())

        item.matrix.makeRotationFromQuaternion(settleQuat)
        item.matrix.setPosition(settlePos.x, settlePos.y, settlePos.z)
        item.x = settlePos.x
        item.y = settlePos.y
        item.z = settlePos.z
        ;(item as any).firstParentID = item1.id

        const fineMatrix = buildBr12FineTunedMatrix(item, item1, sideSign)
        if (fineMatrix) {
          const finePos = decomposeMatrixPosition(fineMatrix)
          item.matrix = fineMatrix
          item.x = finePos.x
          item.y = finePos.y
          item.z = finePos.z
        }

        const finalCenterPosition = toVector3()
        const finalQuaternion = toQuaternion()
        item.matrix.decompose(finalCenterPosition, finalQuaternion, toVector3())
        const itemCenter = new Vector3(item.width / 2, item.height / 2, item.thickness / 2)
        itemCenter.applyQuaternion(finalQuaternion).add(finalCenterPosition)
        const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(finalQuaternion)
        const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)
        const eulerRotation = new Euler().setFromQuaternion(finalQuaternion)
        item.rotation = radToDeg(eulerRotation.y)
        store.sceneBoundingBoxes.set(itemId, {
          boundingBox: new OBB(
            itemCenter,
            toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
            rotationMatrix,
          ),
          type: 'items',
        })

        // Limpieza legacy: quitar IDs de brazo de snappedSides del padre.
        item1.snappedSides = stripArmIdsFromSnappedSides(
          item1.snappedSides as Partial<Record<'left' | 'right', string>>,
          resolveItemById,
        )

        snapResult.snapped = true
        snapResult.side = snapSide
        return
      }

      if (anchorIsRinconeroRight && directionMultiplier > 0 && !item1Is166) {
        newPosition = item1Pos
          .clone()
          .add(item1XAxis.multiplyScalar(directionMultiplier * item1Width))
        const rotationAdjustment = new Quaternion().setFromAxisAngle(
          new Vector3(0, 1, 0),
          -Math.PI / 2,
        )
        newQuaternion.multiply(rotationAdjustment)
        const rinconeroOffset = item1.thickness || 0
        const zAxis = new Vector3(0, 0, 1).applyQuaternion(item1Quat)
        newPosition.add(zAxis.multiplyScalar(rinconeroOffset))
      }
      // CASO ESPECIAL: Mueble normal pegado a la izquierda de un rinconero izquierdo
      else if (anchorIsRinconeroLeft && directionMultiplier < 0 && !(draggedIs166 && item1Is166)) {
        newPosition = item1Pos.clone()
        const rotationAdjustment = new Quaternion().setFromAxisAngle(
          new Vector3(0, 1, 0),
          Math.PI / 2,
        )
        newQuaternion.multiply(rotationAdjustment)
        const rinconeroOffset = (item1.thickness || 0) + (item.width || 0)
        const zAxis = new Vector3(1, 0, 0).applyQuaternion(newQuaternion)
        newPosition.add(zAxis.multiplyScalar(-rinconeroOffset))
      }
      // Módulo recto / RELAX ↔ 166: misma composición que el dibujo (sin camino paralelo).
      else if (item1Is166 && draggedIsStraightModule && !isOreja(item1Catalog)) {
        const moduleW = modulePlanWidth(item) || itemWidth || 0
        const moduleT = Number(item.thickness || 0)
        if (!(moduleW > 0)) return
        const snap = composeStraightModuleSnapOnRinconero(item1, moduleW, snapSide, moduleT)
        if (!snap?.matrix) return
        newPosition = new Vector3()
        snap.matrix.decompose(newPosition, newQuaternion, new Vector3())
        appliedStraightOn166 = true
      }
      // CASO ESPECIAL: rinconero 166- contra 166- por el lado izquierdo.
      else if (draggedIs166 && item1Is166 && snapSide === 'left') {
        console.log('CASO ESPECIAL: rinconero 166- contra 166- por el lado izquierdo.')
        newPosition = item1Pos.clone().add(item1XAxis.multiplyScalar(-item1Width))
        const rotationAdjustment = new Quaternion().setFromAxisAngle(
          new Vector3(0, 1, 0),
          Math.PI / 2,
        )
        newQuaternion.multiply(rotationAdjustment)
        const rinconeroOffset = -29
        const zAxis = new Vector3(0, 0, 1).applyQuaternion(newQuaternion)
        newPosition.add(zAxis.multiplyScalar(rinconeroOffset))
        const itemLocalXAxis = new Vector3(1, 0, 0).applyQuaternion(newQuaternion)
        newPosition.add(itemLocalXAxis.multiplyScalar(-item.thickness - 29))
      }
      // CASO ESPECIAL: item no-recto pegado a un rinconero 166- por el lado derecho (pata L).
      else if (item1Is166 && snapSide !== 'left') {
        newPosition = item1Pos.clone().add(item1XAxis.multiplyScalar(item1Width))
        const rotationAdjustment = new Quaternion().setFromAxisAngle(
          new Vector3(0, 1, 0),
          -Math.PI / 2,
        )
        newQuaternion.multiply(rotationAdjustment)
        const rinconeroOffset = -29
        const zAxis = new Vector3(0, 0, 1).applyQuaternion(newQuaternion)
        newPosition.add(zAxis.multiplyScalar(rinconeroOffset))
        const itemLocalXAxis = new Vector3(1, 0, 0).applyQuaternion(newQuaternion)
        newPosition.add(itemLocalXAxis.multiplyScalar(item.thickness + 29))
      }
      // CASO ESPECIAL: rinconero 166- arrastrado por el lado izquierdo.
      else if (draggedIs166 && snapSide === 'left') {
        newPosition = item1Pos.clone().add(item1XAxis.multiplyScalar(-item1Width))
        const rotationAdjustment = new Quaternion().setFromAxisAngle(
          new Vector3(0, 1, 0),
          Math.PI / 2,
        )
        newQuaternion.multiply(rotationAdjustment)
        const rinconeroOffset = item.width / 2 + 10
        const zAxis = new Vector3(0, 0, 1).applyQuaternion(newQuaternion)
        newPosition.add(zAxis.multiplyScalar(rinconeroOffset))
        // Compensar el pivote en esquina: al girar 90°, desplazar su propio grosor
        // en negativo sobre su eje X local para mantener el encaje correcto.
        const itemLocalXAxis = new Vector3(1, 0, 0).applyQuaternion(newQuaternion)
        newPosition.add(itemLocalXAxis.multiplyScalar(-item.thickness - 29))
      }
      // Corner XL: en el extremo contrario a su mano gira la L y apoya el fondo.
      else if (isCornerXLBrazoCatalog(itemCatalog)) {
        const drawW = Number(item.width || 0)
        const drawT = Number(item.thickness || 0)
        let rowMatrix = null as ReturnType<typeof composeRinconeroDragRowEndSnap>
        if (isCornerXLBrazoCatalog(item1Catalog)) {
          const attachEnd = resolveRinconeroDragRowAttachEnd(
            item1 as any,
            dragReferencePos,
            drawW,
          )
          const naturalEnd = isModularCornerIzquierda(
            item1Catalog,
            rinconeroHandednessFromItem(item1 as any),
          )
            ? 'left'
            : 'right'
          rowMatrix =
            attachEnd === naturalEnd
              ? composeMatrixCornerXLOnCornerXLBack(item1 as any, drawW, itemCatalog)
              : composeCornerXLChaiseAgainstCorner(
                  item1 as any,
                  itemCatalog,
                  drawW,
                  drawT,
                  attachEnd,
                )
        } else {
          rowMatrix = composeRinconeroDragRowEndSnap(
            item1 as any,
            item as any,
            dragReferencePos,
          )
        }
        if (!rowMatrix) return
        newPosition = new Vector3()
        rowMatrix.decompose(newPosition, newQuaternion, new Vector3())
        appliedCornerXL = true
      } else {
        // Calcular ocupación real recorriendo todos los items contiguos al ancla.
        // Esto evita snaps inválidos cuando hay 3+ items y snappedSides quedó desincronizado.
        const item1SnappedSides =
          (item1.snappedSides as Partial<Record<'left' | 'right', string>>) || {}
        const item1ZAxis = new Vector3(0, 0, 1).applyQuaternion(item1Quat)
        const sceneOccupiedSides: Partial<Record<'left' | 'right', string>> = {}
        const SIDE_TOLERANCE_MM = 80
        const DEPTH_ALIGNMENT_TOLERANCE_MM = 120
        const HEIGHT_TOLERANCE_MM = 120

        store.items.forEach((candidate, candidateId) => {
          if (!isLinkedItem(candidate)) return
          // Los brazos no ocupan labios de fila entre módulos.
          if (isArmSceneObject(candidate)) return
          if (String(candidateId) === String(itemId1)) return
          if (String(candidateId) === String(itemId)) {
            // Durante arrastre en vivo, usar la posición propuesta del item arrastrado.
            if (!options?.proposedPosition) return

            const delta = new Vector3(
              dragReferencePos.x - item1Pos.x,
              dragReferencePos.y - item1Pos.y,
              dragReferencePos.z - item1Pos.z,
            )
            const sideProjection = item1XAxis.dot(new Vector3(delta.x, 0, delta.z))
            const depthProjection = Math.abs(item1ZAxis.dot(new Vector3(delta.x, 0, delta.z)))
            const heightDelta = Math.abs(delta.y)

            if (depthProjection > DEPTH_ALIGNMENT_TOLERANCE_MM || heightDelta > HEIGHT_TOLERANCE_MM) {
              return
            }

            const expectedRightProjection = item1Width
            const expectedLeftProjection = -(item.width || 0)

            if (Math.abs(sideProjection - expectedRightProjection) <= SIDE_TOLERANCE_MM) {
              sceneOccupiedSides.right = String(candidateId)
            }
            if (Math.abs(sideProjection - expectedLeftProjection) <= SIDE_TOLERANCE_MM) {
              sceneOccupiedSides.left = String(candidateId)
            }
            return
          }
          if (candidate.visible === false) return

          const candidatePos = new Vector3()
          candidate.matrix.decompose(candidatePos, new Quaternion(), new Vector3())

          const delta = new Vector3(
            candidatePos.x - item1Pos.x,
            candidatePos.y - item1Pos.y,
            candidatePos.z - item1Pos.z,
          )
          const sideProjection = item1XAxis.dot(new Vector3(delta.x, 0, delta.z))
          const depthProjection = Math.abs(item1ZAxis.dot(new Vector3(delta.x, 0, delta.z)))
          const heightDelta = Math.abs(delta.y)

          if (depthProjection > DEPTH_ALIGNMENT_TOLERANCE_MM || heightDelta > HEIGHT_TOLERANCE_MM) {
            return
          }

          const expectedRightProjection = item1Width
          const expectedLeftProjection = -(candidate.width || 0)

          if (Math.abs(sideProjection - expectedRightProjection) <= SIDE_TOLERANCE_MM) {
            sceneOccupiedSides.right = String(candidateId)
          }
          if (Math.abs(sideProjection - expectedLeftProjection) <= SIDE_TOLERANCE_MM) {
            sceneOccupiedSides.left = String(candidateId)
          }
        })

        // No confiar en snappedSides si el vecino ya no está a tope (p.ej. tras
        // sustituir 160→RELAX: el derecho queda lejos y bloqueaba el labio izquierdo).
        const snappedLeftId = item1SnappedSides.left
        const snappedRightId = item1SnappedSides.right
        const snappedLeftItem = snappedLeftId
          ? (store.items.get(snappedLeftId as AnyID) as any)
          : null
        const snappedRightItem = snappedRightId
          ? (store.items.get(snappedRightId as AnyID) as any)
          : null
        const trustedSnappedLeft =
          snappedLeftItem &&
          !isArmSceneObject(snappedLeftItem) &&
          isNeighborFlushOnSide(item1, snappedLeftItem, 'left')
            ? snappedLeftId
            : undefined
        const trustedSnappedRight =
          snappedRightItem &&
          !isArmSceneObject(snappedRightItem) &&
          isNeighborFlushOnSide(item1, snappedRightItem, 'right')
            ? snappedRightId
            : undefined

        const occupiedLeft = sceneOccupiedSides.left || trustedSnappedLeft
        const occupiedRight = sceneOccupiedSides.right || trustedSnappedRight

        // Finales oreja: solo permiten un único adyacente en su lado habilitado.
        const allowedOrejaSide: 'left' | 'right' | null = isFinalOrejaIzquierda
          ? 'left'
          : isFinalOrejaDerecha
            ? 'right'
            : null

        if (allowedOrejaSide && snapSide !== allowedOrejaSide) {
          forceSnapSide(allowedOrejaSide)
        }

        if (allowedOrejaSide) {
          const allowedSideOccupant = allowedOrejaSide === 'left' ? occupiedLeft : occupiedRight
          if (allowedSideOccupant && allowedSideOccupant !== String(itemId)) {
            return
          }
        }

        const sideOccupant = snapSide === 'left' ? occupiedLeft : occupiedRight
        let effectiveDirection = directionMultiplier

        if (sideOccupant && sideOccupant !== String(itemId)) {
          // En orejas no hay fallback al lado contrario: si el lado permitido está ocupado, bloquear.
          if (allowedOrejaSide) {
            return
          }

          // Labio ocupado: colocar en el extremo libre de la fila (160 y RELAX igual).
          if (draggedIsStraightModule && isStraightModularSnapCatalog(item1Catalog)) {
            const exteriorWidth = itemWidth || modulePlanWidth(item) || 0
            if (exteriorWidth > 0) {
              const exterior = composeStraightModuleAtRowExterior(
                store.items as Map<string, any>,
                item1,
                exteriorWidth,
                snapSide,
              )
              if (exterior?.matrix) {
                newPosition = new Vector3()
                exterior.matrix.decompose(newPosition, newQuaternion, new Vector3())
                snapAnchorId = exterior.endUnitId || snapAnchorId
                const endUnit = store.items.get(snapAnchorId as AnyID) as LinkedItem | undefined
                if (endUnit) snapAnchor = endUnit
                forceSnapSide(exterior.side)
                placedAtRowExterior = true
              }
            }
          }

          if (!placedAtRowExterior) {
            const otherSide = snapSide === 'left' ? 'right' : 'left'
            const otherOccupant = otherSide === 'left' ? occupiedLeft : occupiedRight

            // Si ambos lados están ocupados por otros items, no permitir snap.
            if (otherOccupant && otherOccupant !== String(itemId)) {
              return
            }

            effectiveDirection = -directionMultiplier
            snapSide = otherSide
          }
        }

        if (!placedAtRowExterior) {
          // IMPORTANTE: Usar el ancho correcto dependiendo de la dirección
          // - Derecha (effectiveDirection = 1): item1Pos + item1Width
          // - Izquierda (effectiveDirection = -1): item1Pos - itemWidth
          const widthToUse = effectiveDirection > 0 ? item1Width : itemWidth
          const offsetDistance = effectiveDirection * widthToUse

          newPosition = item1Pos.clone().add(item1XAxis.multiplyScalar(offsetDistance))
          if (draggedIsStraightModule) {
            newQuaternion.copy(item1Quat)
          }
        }
      }

      // Para brazos sobre una unidad base, la Y de referencia debe salir del padre.
      // Si reutilizamos la Y actual del brazo, el fine tune de BR1/BR2 acumula el -15
      // cada vez que collideItems se vuelve a ejecutar.
      newPosition.y = isArmSceneObject(item) && !isArmSceneObject(item1) ? item1Pos.y : (options?.proposedPosition?.y ?? item.y)

      const useAdjustedRotation =
        appliedCornerXL ||
        appliedStraightOn166 ||
        placedAtRowExterior ||
        (item1Is166 && snapSide !== 'left') ||
        (anchorIsRinconeroRight && directionMultiplier > 0) ||
        (anchorIsRinconeroLeft && directionMultiplier < 0) ||
        (draggedIs166 && snapSide === 'left')
      item.matrix.makeRotationFromQuaternion(useAdjustedRotation ? newQuaternion : item1Quat)
      item.matrix.setPosition(newPosition.x, newPosition.y, newPosition.z)

      item.x = newPosition.x
      item.y = newPosition.y // Actualizar item.y para mantener consistencia
      item.z = newPosition.z

      if (isArmSceneObject(item) && !isArmSceneObject(snapAnchor)) {
        const armSideSign: 1 | -1 = snapSide === 'left' ? -1 : 1
        const fineMatrix = buildBr12FineTunedMatrix(item, snapAnchor, armSideSign)
        if (fineMatrix) {
          const finePos = decomposeMatrixPosition(fineMatrix)
          item.matrix = fineMatrix
          item.x = finePos.x
          item.y = finePos.y
          item.z = finePos.z
        }
      }

      const finalCenterPosition = toVector3()
      const finalQuaternion = toQuaternion()
      item.matrix.decompose(finalCenterPosition, finalQuaternion, toVector3())
      const itemCenter = new Vector3(item.width / 2, item.height / 2, item.thickness / 2)
      itemCenter.applyQuaternion(finalQuaternion).add(finalCenterPosition)
      const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(finalQuaternion)
      const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

      const eulerRotation = new Euler().setFromQuaternion(finalQuaternion)
      item.rotation = radToDeg(eulerRotation.y)
      store.sceneBoundingBoxes.set(itemId, {
        boundingBox: new OBB(
          itemCenter,
          toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
          rotationMatrix,
        ),
        type: 'items',
      })

      // Solo módulos ocupan labios de fila. Limpiar IDs de brazo legacy antes de registrar.
      const cleanedAnchorSides = stripArmIdsFromSnappedSides(
        snapAnchor.snappedSides as Partial<Record<'left' | 'right', string>>,
        resolveItemById,
      )
      snapAnchor.snappedSides = { ...cleanedAnchorSides, [snapSide]: String(itemId) }

      // Registrar en el item arrastrado qué lado suyo está ahora adyacente al ancla real.
      // Si item quedó en el lado 'left' del ancla, el ancla está en el 'right' de item (y viceversa).
      const oppositeSide: 'left' | 'right' = snapSide === 'left' ? 'right' : 'left'
      const cleanedItemSides = stripArmIdsFromSnappedSides(
        item.snappedSides as Partial<Record<'left' | 'right', string>>,
        resolveItemById,
      )
      item.snappedSides = { ...cleanedItemSides, [oppositeSide]: snapAnchorId }

      snapResult.snapped = true
      snapResult.side = snapSide
    })

    return snapResult
    } finally {
      popSnap()
    }
  },
  // Pouffs (186-/187-/188-) se pegan a la primera cara del OBB de item1 que
  // encuentre el centro del item arrastrado (izq/der/frente/atrás indistintamente)
  // y nunca rotan: solo se desplazan en la normal de esa cara hasta quedar a tope.
  collidePouffItems: (itemId: AnyID, itemId1: AnyID) => {
    const snapResult: { snapped: boolean; side: 'left' | 'right' | null } = {
      snapped: false,
      side: null,
    }

    const popSnap = pushGeometryOperation('snap')
    try {
    set((store) => {
      const item = store.items.get(itemId) as LinkedItem
      const item1 = store.items.get(itemId1) as LinkedItem
      if (!item || !item1) return

      const item1Pos = new Vector3()
      const item1Quat = new Quaternion()
      item1.matrix.decompose(item1Pos, item1Quat, new Vector3())

      const itemPos = new Vector3()
      const itemQuat = new Quaternion()
      item.matrix.decompose(itemPos, itemQuat, new Vector3())

      const snappedCorner = computePouffSnapPositionToAnchor(
        item1,
        item,
        itemPos,
        store.items,
        POUFF_SNAP_FACE_GAP_MM,
      )
      if (!snappedCorner) return

      const newPosition = snappedCorner.clone()
      newPosition.y = item.y

      const testMatrix = item.matrix.clone()
      testMatrix.setPosition(newPosition.x, newPosition.y, newPosition.z)
      const snapPenetrations = filterPouffPenetrationsForArmAnchor(
        item1,
        getPouffPenetratingObstacles(
          testMatrix,
          item.width,
          item.height,
          item.thickness,
          store.items,
          store.collisionableItems,
          itemId,
        ),
      )
      if (snapPenetrations.length > 0) {
        return
      }

      item.matrix.setPosition(newPosition.x, newPosition.y, newPosition.z)
      item.x = newPosition.x
      item.y = newPosition.y
      item.z = newPosition.z

      const finalCenterPosition = toVector3()
      const finalQuaternion = toQuaternion()
      item.matrix.decompose(finalCenterPosition, finalQuaternion, toVector3())
      const itemCenter = new Vector3(item.width / 2, item.height / 2, item.thickness / 2)
      itemCenter.applyQuaternion(finalQuaternion).add(finalCenterPosition)
      const rotationMatrix4 = new Matrix4().makeRotationFromQuaternion(finalQuaternion)
      const rotationMatrix = new Matrix3().setFromMatrix4(rotationMatrix4)

      store.sceneBoundingBoxes.set(itemId, {
        boundingBox: new OBB(
          itemCenter,
          toVector3([item.width / 2, item.height / 2, item.thickness / 2]),
          rotationMatrix,
        ),
        type: 'items',
      })

      snapResult.snapped = true
    })

    return snapResult
    } finally {
      popSnap()
    }
  },
  applyToAllItems: (originalID: string) => {
    set((store) => {
      const items = store.items
      const itemOriginal = items.get(originalID)

      if (!itemOriginal || !originalID) {
        console.error('No se encontró el item original o el ID original es inválido.')
        return
      }

      items.forEach((value, key) => {
        if (value?.idUnidades === itemOriginal.idUnidades && key !== originalID) {
          const originalZones = itemOriginal.zones
          const targetZones = value.zones

          const originalTextures: Textures = itemOriginal.textures
          const targetTextures: Textures = value.textures

          if (itemOriginal.type === 'walls' && value.type === 'partition') {
            Object.keys(originalZones).forEach((zoneName) => {
              if (targetZones[zoneName]) {
                targetZones[zoneName] = { ...originalZones[zoneName] }
              }
            })

            const updatedTextures: Textures = targetTextures.map((targetTexture) => {
              const originalTexture = originalTextures.find(
                (t) => t.zoneName === targetTexture.zoneName,
              )
              return originalTexture ? { ...targetTexture, ...originalTexture } : targetTexture
            })

            value.textures = updatedTextures
          } else {
            value.zones = { ...originalZones }

            const updatedTextures: Textures = targetTextures.map((targetTexture) => {
              const originalTexture = originalTextures.find(
                (t) => t.zoneName === targetTexture.zoneName,
              )
              return originalTexture ? { ...targetTexture, ...originalTexture } : targetTexture
            })

            value.textures = updatedTextures
          }
        }
      })
    })
  },
  getInitialAngleDegrees: (itemId: AnyID) => {
    const items = get().items
    const item = items.get(itemId)
    if (!item) {
      throw new Error(`Item with ID ${itemId} not found`)
    }

    const x = toVector3()
    const y = toVector3()
    const z = toVector3()

    // Extraer las bases de la matriz del item
    item.matrix.extractBasis(x, y, z)

    // Calcular el ángulo de rotación en el eje X
    const xRot = toVector3([1, 0, 0]).angleTo(x)

    // Calcular el producto cruzado
    const crossProduct = toVector3([1, 0, 0]).cross(x)

    // Calcular el ángulo inicial en grados
    const initialAngleDegrees = (degrees(xRot * -Math.sign(crossProduct.y)) + 360) % 360

    return initialAngleDegrees
  },
  getContiguousItems: () => {
    const items = get().items
    // Filtrar solo items y linkedItems que sean visibles, excluyendo frontales
    const validItems = Array.from(items.values()).filter((item) => {
      if (!isLinkedItem(item) || !item.visible) {
        return false
      }
      if ((item as any).canCollide === false) {
        return false
      }
      if (isCushionLikeSceneObject(item)) {
        return false
      }
      const catalog = item.catalogID?.toString() ?? ''
      const catalogLower = catalog.toLowerCase()
      return (
        catalog !== 'Frontal' && catalogLower !== 'frontal' && !catalogLower.includes('frontal')
      )
    }) as LinkedItem[]

    const isKBRItem = (item: LinkedItem) => item.catalogID?.toString().toUpperCase().includes('KBR')

    const kbrItems = validItems.filter(isKBRItem)
    const otherItems = validItems.filter((item) => !isKBRItem(item))

    // Separar ítems por tipoNormalizado para grupos homogéneos
    const malItems = otherItems.filter((item) => item.tipoNormalizado !== 'MBJ')
    const mbjItems = otherItems.filter((item) => item.tipoNormalizado !== 'MAL')

    const createGroups = (itemList: LinkedItem[]) => {
      const groups: (Item | LinkedItem)[][] = []
      const processed = new Set<string>()

      itemList.forEach((item) => {
        if (processed.has(item.id)) return

        const group = [item]
        processed.add(item.id)

        // Buscar todos los muebles contiguos a este grupo
        let foundNew = true
        while (foundNew) {
          foundNew = false

          itemList.forEach((candidateItem) => {
            if (processed.has(candidateItem.id)) return

            // Verificar si el candidato es contiguo a algún mueble del grupo
            const isContiguousToGroup = group.some((groupItem) =>
              areContiguous(groupItem, candidateItem),
            )

            if (isContiguousToGroup) {
              group.push(candidateItem)
              processed.add(candidateItem.id)
              foundNew = true
            }
          })
        }

        // Incluir todos los grupos, incluso los de un solo elemento
        groups.push(group)
      })

      return groups
    }

    let totalDistancesBetweenGroups = 0
    // Función para verificar si dos muebles son contiguos
    const areContiguous = (item1: LinkedItem, item2: LinkedItem) => {
      // Código existente para la detección de contiguidad...
      // Extraer posiciones de la matriz para mayor precisión
      const pos1 = new Vector3()
      const quat1 = new Quaternion()
      item1.matrix.decompose(pos1, quat1, new Vector3())

      const pos2 = new Vector3()
      const quat2 = new Quaternion()
      item2.matrix.decompose(pos2, quat2, new Vector3())

      const height1 = item1.height
      const height2 = item2.height

      // Verificar si están a la misma altura (tolerancia pequeña para flotantes)
      const verticalDistance = Math.abs(pos1.y - pos2.y)

      // Si la distancia vertical es mayor que la altura de cualquiera de los dos items, no son contiguos
      const maxHeight = Math.max(height1, height2)
      if (verticalDistance > maxHeight) {
        return false
      }

      const getVertices = (item: LinkedItem, pos: Vector3) => {
        const width = item.width
        const thickness = item.thickness + item.regleta || 0
        const angle = radians(get().getInitialAngleDegrees(item.id))
        const cos = Math.cos(angle)
        const sin = Math.sin(angle)

        const p = { x: pos.x, z: pos.z }

        // Vértices relativos al punto de pivote (asumido en p)
        const v = [
          { x: 0, z: 0 },
          { x: width, z: 0 },
          { x: width, z: thickness },
          { x: 0, z: thickness },
        ]

        // Rotar y trasladar vértices
        return v.map((vertex) => ({
          x: p.x + vertex.x * cos - vertex.z * sin,
          z: p.z + vertex.x * sin + vertex.z * cos,
        }))
      }

      // Resto del código de detección de contiguidad...
      const vertices1 = getVertices(item1, pos1)
      const vertices2 = getVertices(item2, pos2)

      const segments1 = [
        { p1: vertices1[0], p2: vertices1[1] },
        { p1: vertices1[1], p2: vertices1[2] },
        { p1: vertices1[2], p2: vertices1[3] },
        { p1: vertices1[3], p2: vertices1[0] },
      ]

      const segments2 = [
        { p1: vertices2[0], p2: vertices2[1] },
        { p1: vertices2[1], p2: vertices2[2] },
        { p1: vertices2[2], p2: vertices2[3] },
        { p1: vertices2[3], p2: vertices2[0] },
      ]

      const distSq = (p1, p2) => (p1.x - p2.x) ** 2 + (p1.z - p2.z) ** 2

      const distToSegmentSq = (p, v, w) => {
        const l2 = distSq(v, w)
        if (l2 === 0) return distSq(p, v)
        let t = ((p.x - v.x) * (w.x - v.x) + (p.z - v.z) * (w.z - v.z)) / l2
        t = Math.max(0, Math.min(1, t))
        return distSq(p, { x: v.x + t * (w.x - v.x), z: v.z + t * (w.z - v.z) })
      }

      const segmentDistance = (seg1, seg2) => {
        const d1 = distToSegmentSq(seg1.p1, seg2.p1, seg2.p2)
        const d2 = distToSegmentSq(seg1.p2, seg2.p1, seg2.p2)
        const d3 = distToSegmentSq(seg2.p1, seg1.p1, seg1.p2)
        const d4 = distToSegmentSq(seg2.p2, seg1.p1, seg1.p2)
        return Math.sqrt(Math.min(d1, d2, d3, d4))
      }

      const maxDistance = item1.regleta ? (item1.regleta || 0) + 10 : 30

      for (const seg1 of segments1) {
        for (const seg2 of segments2) {
          if (segmentDistance(seg1, seg2) < maxDistance) {
            // console.log(segmentDistance(seg1, seg2))
            totalDistancesBetweenGroups += segmentDistance(seg1, seg2)
            return true
          }
        }
      }

      return false
    }

    // Crear grupos para MAL y otros por separado
    const malGroups = createGroups(malItems)
    const mbjGroups = createGroups(mbjItems)

    const kbrGroups = kbrItems.map((item) => [item])
    const otherGroups = malGroups.concat(mbjGroups)

    // Combinar grupos de ambas listas
    const allGroupsRaw = [...kbrGroups, ...otherGroups]

    // Ordenar cada grupo según contenga o no rinconeros
    const sortedGroups = allGroupsRaw.map((group) => {
      // Para grupos de un solo elemento, no hay necesidad de ordenar
      if (group.length <= 1) {
        return group.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
          isKBR: isKBRItem(item),
        }))
      }

      // Verificar si hay un rinconero en el grupo
      const hasRinconero = group.some((item) => isRinconeroCatalog(item.catalogID))

      // Si hay rinconeros, usar la lógica de ordenamiento actual (antihorario alrededor del centro)
      if (hasRinconero) {
        // Extraer posiciones y rotaciones para análisis
        const itemPositions = group.map((item) => {
          const pos = new Vector3()
          const quat = new Quaternion()
          const scale = new Vector3()
          item.matrix.decompose(pos, quat, scale)
          return {
            id: item.id,
            position: pos,
            rotation: get().getInitialAngleDegrees(item.id),
          }
        })

        // Calcular el centro del grupo
        const center = new Vector3()
        itemPositions.forEach((item) => {
          center.add(item.position)
        })
        center.divideScalar(itemPositions.length)

        // Determinar la orientación principal del grupo
        const avgRotation =
          group.reduce((sum, item) => {
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees
          }, 0) / group.length

        const normalizedAngle = ((avgRotation % 360) + 360) % 360

        // Ordenar elementos considerando la orientación de la pared
        const sortedItems = group.sort((a, b) => {
          const posA = new Vector3()
          const posB = new Vector3()
          const quatA = new Quaternion()
          const quatB = new Quaternion()
          const scaleA = new Vector3()
          const scaleB = new Vector3()

          a.matrix.decompose(posA, quatA, scaleA)
          b.matrix.decompose(posB, quatB, scaleB)

          // Calcular ángulos polares respecto al centro
          const angleA = Math.atan2(posA.z - center.z, posA.x - center.x)
          const angleB = Math.atan2(posB.z - center.z, posB.x - center.x)

          // Invertir el orden de comparación según la pared
          if (normalizedAngle > 45 && normalizedAngle <= 135) {
            // Pared norte (90°)
            return angleA - angleB // Invertir comparación para ordenar correctamente
          } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
            // Pared sur (270°)
            return angleB - angleA // Invertir comparación para ordenar correctamente
          } else {
            return angleA - angleB // Mantener ordenamiento para paredes este/oeste
          }
        })
        // Mapear a formato de resultado
        return sortedItems.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
          isKBR: isKBRItem(item),
        }))
      } else {
        // Calcular el "ángulo" promedio del grupo para determinar la dirección de ordenamiento
        const avgRotation =
          group.reduce((sum, item) => {
            // Usamos la función que ya calcula el ángulo de forma consistente
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees // Acumulamos en grados
          }, 0) / group.length

        // Ordenar por proyección en el vector derecha según la orientación promedio
        const sortedGroup = group.sort((a, b) => {
          const posA = new Vector3()
          const posB = new Vector3()
          a.matrix.decompose(posA, new Quaternion(), new Vector3())
          b.matrix.decompose(posB, new Quaternion(), new Vector3())

          const normalizedAngle = ((avgRotation % 360) + 360) % 360

          // Usar rangos de ángulos para determinar el eje de ordenación
          if (normalizedAngle > 315 || normalizedAngle <= 45) {
            // Cerca de 0°: Muebles alineados con el eje X. Ordenar por X ascendente.
            return posA.x - posB.x
          } else if (normalizedAngle > 45 && normalizedAngle <= 135) {
            // Cerca de 90°: Muebles alineados con el eje Z. Ordenar por Z ascendente.
            return posA.z - posB.z
          } else if (normalizedAngle > 135 && normalizedAngle <= 225) {
            // Cerca de 180°: Muebles alineados con el eje X. Ordenar por X descendente.
            return posB.x - posA.x
          } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
            // Cerca de 270°: Muebles alineados con el eje Z. Ordenar por Z descendente.
            return posB.z - posA.z
          }

          // Fallback para cualquier caso no cubierto
          const fallbackRightVector = new Vector3(0, 0, 1)
          fallbackRightVector.applyAxisAngle(new Vector3(0, 1, 0), avgRotation * (Math.PI / 180))
          const projectionA = posA.dot(fallbackRightVector)
          const projectionB = posB.dot(fallbackRightVector)
          return projectionA - projectionB
        })

        return sortedGroup.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
          isKBR: isKBRItem(item),
        }))
      }
    })

    return {
      groups: sortedGroups,
      totalGroups: sortedGroups.length,
      totalItemsInGroups: sortedGroups.reduce((sum, group) => sum + group.length, 0),
      totalDistancesBetweenGroups,
    }
  },
  getContiguousItemsForLaterals: () => {
    const items = get().items
    // Filtrar solo items y linkedItems que sean visibles, excluyendo frontales
    const validItems = Array.from(items.values()).filter(
      (item) =>
        isLinkedItem(item) &&
        item.visible &&
        (item as any).canCollide !== false &&
        !isCushionLikeSceneObject(item) &&
        item.catalogID !== 'Frontal' &&
        item.catalogID?.toString() !== 'Frontal',
    ) as LinkedItem[]

    // Separar ítems por tipoNormalizado para grupos homogéneos
    const malItems = validItems.filter((item) => item.tipoNormalizado !== 'MBJ')
    const mbjItems = validItems.filter((item) => item.tipoNormalizado !== 'MAL')

    const createGroups = (itemList: LinkedItem[]) => {
      const groups: (Item | LinkedItem)[][] = []
      const processed = new Set<string>()

      itemList.forEach((item) => {
        if (processed.has(item.id)) return

        const group = [item]
        processed.add(item.id)

        // Buscar todos los muebles contiguos a este grupo
        let foundNew = true
        while (foundNew) {
          foundNew = false

          itemList.forEach((candidateItem) => {
            if (processed.has(candidateItem.id)) return

            // Verificar si el candidato es contiguo a algún mueble del grupo
            const isContiguousToGroup = group.some((groupItem) =>
              areContiguous(groupItem, candidateItem),
            )

            if (isContiguousToGroup) {
              group.push(candidateItem)
              processed.add(candidateItem.id)
              foundNew = true
            }
          })
        }

        // Incluir todos los grupos, incluso los de un solo elemento
        groups.push(group)
      })

      return groups
    }

    // Función para verificar si dos muebles son contiguos
    const areContiguous = (item1: LinkedItem, item2: LinkedItem) => {
      // Código existente para la detección de contiguidad...
      // Extraer posiciones de la matriz para mayor precisión
      const pos1 = new Vector3()
      const quat1 = new Quaternion()
      item1.matrix.decompose(pos1, quat1, new Vector3())

      const pos2 = new Vector3()
      const quat2 = new Quaternion()
      item2.matrix.decompose(pos2, quat2, new Vector3())

      const height1 = item1.height
      const height2 = item2.height

      // Verificar si están a la misma altura (tolerancia pequeña para flotantes)
      const verticalDistance = Math.abs(pos1.y - pos2.y)

      // Si la distancia vertical es mayor que la altura de cualquiera de los dos items, no son contiguos
      const maxHeight = Math.max(height1, height2)
      if (verticalDistance > maxHeight) {
        return false
      }

      const getVertices = (item: LinkedItem, pos: Vector3) => {
        const width = item.width
        const thickness = item.thickness + item.regleta || 0
        const angle = radians(get().getInitialAngleDegrees(item.id))
        const cos = Math.cos(angle)
        const sin = Math.sin(angle)

        const p = { x: pos.x, z: pos.z }

        // Vértices relativos al punto de pivote (asumido en p)
        const v = [
          { x: 0, z: 0 },
          { x: width, z: 0 },
          { x: width, z: thickness },
          { x: 0, z: thickness },
        ]

        // Rotar y trasladar vértices
        return v.map((vertex) => ({
          x: p.x + vertex.x * cos - vertex.z * sin,
          z: p.z + vertex.x * sin + vertex.z * cos,
        }))
      }

      // Resto del código de detección de contiguidad...
      const vertices1 = getVertices(item1, pos1)
      const vertices2 = getVertices(item2, pos2)

      const segments1 = [
        { p1: vertices1[0], p2: vertices1[1] },
        { p1: vertices1[1], p2: vertices1[2] },
        { p1: vertices1[2], p2: vertices1[3] },
        { p1: vertices1[3], p2: vertices1[0] },
      ]

      const segments2 = [
        { p1: vertices2[0], p2: vertices2[1] },
        { p1: vertices2[1], p2: vertices2[2] },
        { p1: vertices2[2], p2: vertices2[3] },
        { p1: vertices2[3], p2: vertices2[0] },
      ]

      const distSq = (p1, p2) => (p1.x - p2.x) ** 2 + (p1.z - p2.z) ** 2

      const distToSegmentSq = (p, v, w) => {
        const l2 = distSq(v, w)
        if (l2 === 0) return distSq(p, v)
        let t = ((p.x - v.x) * (w.x - v.x) + (p.z - v.z) * (w.z - v.z)) / l2
        t = Math.max(0, Math.min(1, t))
        return distSq(p, { x: v.x + t * (w.x - v.x), z: v.z + t * (w.z - v.z) })
      }

      const segmentDistance = (seg1, seg2) => {
        const d1 = distToSegmentSq(seg1.p1, seg2.p1, seg2.p2)
        const d2 = distToSegmentSq(seg1.p2, seg2.p1, seg2.p2)
        const d3 = distToSegmentSq(seg2.p1, seg1.p1, seg1.p2)
        const d4 = distToSegmentSq(seg2.p2, seg1.p1, seg1.p2)
        return Math.sqrt(Math.min(d1, d2, d3, d4))
      }

      for (const seg1 of segments1) {
        for (const seg2 of segments2) {
          if (segmentDistance(seg1, seg2) < 10) {
            // console.log(segmentDistance(seg1, seg2))
            return true
          }
        }
      }

      return false
    }

    // Crear grupos para MAL y otros por separado
    const malGroups = createGroups(malItems)
    const mbjGroups = createGroups(mbjItems)

    // Combinar grupos de ambas listas
    const allGroupsRaw = malGroups.concat(mbjGroups)

    // Ordenar cada grupo según contenga o no rinconeros
    const sortedGroups = allGroupsRaw.map((group) => {
      // Para grupos de un solo elemento, no hay necesidad de ordenar
      if (group.length <= 1) {
        return group.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      }

      // Verificar si hay un rinconero en el grupo
      const hasRinconero = group.some((item) => isRinconeroCatalog(item.catalogID))

      // Si hay rinconeros, usar la lógica de ordenamiento actual (antihorario alrededor del centro)
      if (hasRinconero) {
        // Extraer posiciones y rotaciones para análisis
        const itemPositions = group.map((item) => {
          const pos = new Vector3()
          const quat = new Quaternion()
          const scale = new Vector3()
          item.matrix.decompose(pos, quat, scale)
          return {
            id: item.id,
            position: pos,
            rotation: get().getInitialAngleDegrees(item.id),
          }
        })

        // Calcular el centro del grupo
        const center = new Vector3()
        itemPositions.forEach((item) => {
          center.add(item.position)
        })
        center.divideScalar(itemPositions.length)

        // Determinar la orientación principal del grupo
        const avgRotation =
          group.reduce((sum, item) => {
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees
          }, 0) / group.length

        const normalizedAngle = ((avgRotation % 360) + 360) % 360

        // Ordenar elementos considerando la orientación de la pared
        const sortedItems = group.sort((a, b) => {
          const posA = new Vector3()
          const posB = new Vector3()
          const quatA = new Quaternion()
          const quatB = new Quaternion()
          const scaleA = new Vector3()
          const scaleB = new Vector3()

          a.matrix.decompose(posA, quatA, scaleA)
          b.matrix.decompose(posB, quatB, scaleB)

          // Calcular ángulos polares respecto al centro
          const angleA = Math.atan2(posA.z - center.z, posA.x - center.x)
          const angleB = Math.atan2(posB.z - center.z, posB.x - center.x)

          // Invertir el orden de comparación según la pared
          if (normalizedAngle > 45 && normalizedAngle <= 135) {
            // Pared norte (90°)
            return angleA - angleB // Invertir comparación para ordenar correctamente
          } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
            // Pared sur (270°)
            return angleB - angleA // Invertir comparación para ordenar correctamente
          } else {
            return angleA - angleB // Mantener ordenamiento para paredes este/oeste
          }
        })
        // Mapear a formato de resultado
        return sortedItems.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      } else {
        // Calcular el "ángulo" promedio del grupo para determinar la dirección de ordenamiento
        const avgRotation =
          group.reduce((sum, item) => {
            // Usamos la función que ya calcula el ángulo de forma consistente
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees // Acumulamos en grados
          }, 0) / group.length

        // Ordenar por proyección en el vector derecha según la orientación promedio
        const sortedGroup = group.sort((a, b) => {
          const posA = new Vector3()
          const posB = new Vector3()
          a.matrix.decompose(posA, new Quaternion(), new Vector3())
          b.matrix.decompose(posB, new Quaternion(), new Vector3())

          const normalizedAngle = ((avgRotation % 360) + 360) % 360

          // Usar rangos de ángulos para determinar el eje de ordenación
          if (normalizedAngle > 315 || normalizedAngle <= 45) {
            // Cerca de 0°: Muebles alineados con el eje X. Ordenar por X ascendente.
            return posA.x - posB.x
          } else if (normalizedAngle > 45 && normalizedAngle <= 135) {
            // Cerca de 90°: Muebles alineados con el eje Z. Ordenar por Z ascendente.
            return posA.z - posB.z
          } else if (normalizedAngle > 135 && normalizedAngle <= 225) {
            // Cerca de 180°: Muebles alineados con el eje X. Ordenar por X descendente.
            return posB.x - posA.x
          } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
            // Cerca de 270°: Muebles alineados con el eje Z. Ordenar por Z descendente.
            return posB.z - posA.z
          }

          // Fallback para cualquier caso no cubierto
          const fallbackRightVector = new Vector3(0, 0, 1)
          fallbackRightVector.applyAxisAngle(new Vector3(0, 1, 0), avgRotation * (Math.PI / 180))
          const projectionA = posA.dot(fallbackRightVector)
          const projectionB = posB.dot(fallbackRightVector)
          return projectionA - projectionB
        })

        return sortedGroup.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      }
    })

    return {
      groups: sortedGroups,
      totalGroups: sortedGroups.length,
      totalItemsInGroups: sortedGroups.reduce((sum, group) => sum + group.length, 0),
    }
  },
  getContItemsForEncimeras: () => {
    const items = get().items
    // Filtrar solo items y linkedItems que sean visibles
    const validItems = Array.from(items.values()).filter(
      (item) =>
        isLinkedItem(item) &&
        item.visible &&
        (item as any).canCollide !== false &&
        !isCushionLikeSceneObject(item) &&
        item.tipoNormalizado !== 'MAL',
    ) as LinkedItem[]

    // Función para verificar si dos muebles son contiguos
    const areContiguous = (item1: LinkedItem, item2: LinkedItem) => {
      // IMPORTANTE: Solo considerar contiguos items del mismo tipoNormalizado
      // MBJ solo con MBJ, MAL solo con MAL, COL solo con COL
      // Esto evita que se agrupen items de diferentes alturas (bajos con altos)
      if (item1.tipoNormalizado !== item2.tipoNormalizado) {
        return false
      }

      // Extraer posiciones de la matriz para mayor precisión
      const pos1 = new Vector3()
      const quat1 = new Quaternion()
      item1.matrix.decompose(pos1, quat1, new Vector3())

      const pos2 = new Vector3()
      const quat2 = new Quaternion()
      item2.matrix.decompose(pos2, quat2, new Vector3())

      // console.log(`Verificando contiguidad entre ${item1.id} y ${item2.id}`)
      // console.log(
      //   `Posición item1: x=${pos1.x}, z=${pos1.z}, rotación=${get().getInitialAngleDegrees(item1.id)}`,
      // )
      // console.log(
      //   `Posición item2: x=${pos2.x}, z=${pos2.z}, rotación=${get().getInitialAngleDegrees(item2.id)}`,
      // )

      const height1 = item1.height
      const height2 = item2.height

      // Verificar si están a la misma altura (tolerancia pequeña para flotantes)
      const verticalDistance = Math.abs(pos1.y - pos2.y)

      // Si la distancia vertical es mayor que la altura de cualquiera de los dos items, no son contiguos
      const maxHeight = Math.max(height1, height2)
      if (verticalDistance > maxHeight) {
        // console.log(`No contiguos: distancia vertical ${verticalDistance} > maxHeight ${maxHeight}`)
        return false
      }

      const getVertices = (item: LinkedItem, pos: Vector3) => {
        const width = item.width
        const thickness = item.thickness + (item.regleta || 0)
        const angle = radians(get().getInitialAngleDegrees(item.id))
        const cos = Math.cos(angle)
        const sin = Math.sin(angle)

        const p = { x: pos.x, z: pos.z }

        // Vértices relativos al punto de pivote (asumido en p)
        const v = [
          { x: 0, z: 0 },
          { x: width, z: 0 },
          { x: width, z: thickness },
          { x: 0, z: thickness },
        ]

        // Rotar y trasladar vértices
        return v.map((vertex) => ({
          x: p.x + vertex.x * cos - vertex.z * sin,
          z: p.z + vertex.x * sin + vertex.z * cos,
        }))
      }

      // Resto del código de detección de contiguidad...
      const vertices1 = getVertices(item1, pos1)
      const vertices2 = getVertices(item2, pos2)

      // console.log('Vértices item1:', vertices1)
      // console.log('Vértices item2:', vertices2)

      const segments1 = [
        { p1: vertices1[0], p2: vertices1[1] },
        { p1: vertices1[1], p2: vertices1[2] },
        { p1: vertices1[2], p2: vertices1[3] },
        { p1: vertices1[3], p2: vertices1[0] },
      ]

      const segments2 = [
        { p1: vertices2[0], p2: vertices2[1] },
        { p1: vertices2[1], p2: vertices2[2] },
        { p1: vertices2[2], p2: vertices2[3] },
        { p1: vertices2[3], p2: vertices2[0] },
      ]

      const distSq = (p1, p2) => (p1.x - p2.x) ** 2 + (p1.z - p2.z) ** 2

      const distToSegmentSq = (p, v, w) => {
        const l2 = distSq(v, w)
        if (l2 === 0) return distSq(p, v)
        let t = ((p.x - v.x) * (w.x - v.x) + (p.z - v.z) * (w.z - v.z)) / l2
        t = Math.max(0, Math.min(1, t))
        return distSq(p, { x: v.x + t * (w.x - v.x), z: v.z + t * (w.z - v.z) })
      }

      const segmentDistance = (seg1, seg2) => {
        const d1 = distToSegmentSq(seg1.p1, seg2.p1, seg2.p2)
        const d2 = distToSegmentSq(seg1.p2, seg2.p1, seg2.p2)
        const d3 = distToSegmentSq(seg2.p1, seg1.p1, seg1.p2)
        const d4 = distToSegmentSq(seg2.p2, seg1.p1, seg1.p2)
        return Math.sqrt(Math.min(d1, d2, d3, d4))
      }

      let minDistance = Infinity
      for (const seg1 of segments1) {
        for (const seg2 of segments2) {
          const dist = segmentDistance(seg1, seg2)
          minDistance = Math.min(minDistance, dist)
        }
      }

      // Usar un umbral más preciso (5mm) para detectar contigüidad real
      // Esto evita falsos positivos cuando hay espacios pequeños
      const TOLERANCE = 5 // Margen de error para considerar contiguos
      const isContiguous = minDistance <= TOLERANCE
      return isContiguous
    }

    // Crear grupos de muebles contiguos
    const groups: (Item | LinkedItem)[][] = []
    const processed = new Set<string>()

    validItems.forEach((item) => {
      if (processed.has(item.id)) return

      const group = [item]
      processed.add(item.id)

      // Buscar todos los muebles contiguos a este grupo
      let foundNew = true
      while (foundNew) {
        foundNew = false

        validItems.forEach((candidateItem) => {
          if (processed.has(candidateItem.id)) return

          // Verificar si el candidato es contiguo a algún mueble del grupo
          const isContiguousToGroup = group.some((groupItem) =>
            areContiguous(groupItem, candidateItem),
          )

          if (isContiguousToGroup) {
            group.push(candidateItem)
            processed.add(candidateItem.id)
            foundNew = true
          }
        })
      }

      // Incluir todos los grupos, incluso los de un solo elemento
      groups.push(group)
    })

    // Ordenar cada grupo según contenga o no rinconeros
    const sortedGroups = groups.map((group) => {
      // Para grupos de un solo elemento, no hay necesidad de ordenar
      if (group.length <= 1) {
        return group.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.morphs?.find((morph) => morph.name === 'Ancho')?._default ?? item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      }

      // Verificar si hay un rinconero en el grupo
      const hasRinconero = group.some((item) => isRinconeroCatalog(item.catalogID))

      // Si hay rinconeros, usar la lógica de ordenamiento actual (antihorario alrededor del centro)
      if (hasRinconero) {
        // Extraer posiciones y rotaciones para análisis
        const itemPositions = group.map((item) => {
          const pos = new Vector3()
          const quat = new Quaternion()
          const scale = new Vector3()
          item.matrix.decompose(pos, quat, scale)
          return {
            id: item.id,
            position: pos,
            rotation: get().getInitialAngleDegrees(item.id),
          }
        })

        // Calcular el centro del grupo
        const center = new Vector3()
        itemPositions.forEach((item) => {
          center.add(item.position)
        })
        center.divideScalar(itemPositions.length)

        // Determinar la orientación principal del grupo
        const avgRotation =
          group.reduce((sum, item) => {
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees
          }, 0) / group.length

        const normalizedAngle = ((avgRotation % 360) + 360) % 360

        // Ordenar elementos considerando la orientación de la pared
        const sortedItems = group.sort((a, b) => {
          const posA = new Vector3()
          const posB = new Vector3()
          const quatA = new Quaternion()
          const quatB = new Quaternion()
          const scaleA = new Vector3()
          const scaleB = new Vector3()

          a.matrix.decompose(posA, quatA, scaleA)
          b.matrix.decompose(posB, quatB, scaleB)

          // Calcular ángulos polares respecto al centro
          const angleA = Math.atan2(posA.z - center.z, posA.x - center.x)
          const angleB = Math.atan2(posB.z - center.z, posB.x - center.x)

          // Invertir el orden de comparación según la pared
          if (normalizedAngle > 45 && normalizedAngle <= 135) {
            // Pared norte (90°)
            return angleA - angleB // Invertir comparación para ordenar correctamente
          } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
            // Pared sur (270°)
            return angleB - angleA // Invertir comparación para ordenar correctamente
          } else {
            return angleA - angleB // Mantener ordenamiento para paredes este/oeste
          }
        })
        // Mapear a formato de resultado
        return sortedItems.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.morphs?.find((morph) => morph.name === 'Ancho')?._default ?? item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      } else {
        // Calcular el "ángulo" promedio del grupo para determinar la dirección de ordenamiento
        const avgRotation =
          group.reduce((sum, item) => {
            // Usamos la función que ya calcula el ángulo de forma consistente
            const angleDegrees = get().getInitialAngleDegrees(item.id)
            return sum + angleDegrees // Acumulamos en grados
          }, 0) / group.length

        // Verificar si todos los muebles están en la misma pared
        const items = get().items
        const firstItem = items.get(group[0].id) as Item | LinkedItem
        const commonWall = firstItem?.snappedWall

        let sortedGroup: typeof group

        // Si todos están en la misma pared, ordenar según posición a lo largo de la pared
        if (
          commonWall &&
          group.every((item) => {
            const itemData = items.get(item.id) as Item | LinkedItem
            return itemData?.snappedWall === commonWall
          })
        ) {
          const wall = items.get(commonWall) as Walls
          if (wall) {
            const start = toVector3(wall.start)
            const end = toVector3(wall.end)
            const wallDirection = end.clone().sub(start)
            const wallDirNormalized = wallDirection.clone().normalize()

            sortedGroup = group.sort((a, b) => {
              const posA = new Vector3()
              const posB = new Vector3()
              a.matrix.decompose(posA, new Quaternion(), new Vector3())
              b.matrix.decompose(posB, new Quaternion(), new Vector3())

              // Proyectar posiciones en la dirección de la pared
              const itemAToStart = posA.clone().sub(start)
              const itemBToStart = posB.clone().sub(start)
              const alongWallA = Math.max(0, itemAToStart.dot(wallDirNormalized))
              const alongWallB = Math.max(0, itemBToStart.dot(wallDirNormalized))

              return alongWallA - alongWallB
            })
          } else {
            // Si no se encuentra la pared, usar ordenamiento por proyección en la dirección de la pared
            sortedGroup = group.sort((a, b) => {
              const posA = new Vector3()
              const posB = new Vector3()
              const quatA = new Quaternion()
              const quatB = new Quaternion()

              a.matrix.decompose(posA, quatA, new Vector3())
              b.matrix.decompose(posB, quatB, new Vector3())

              // Obtener la dirección de la pared del primer item
              const angleA = get().getInitialAngleDegrees(a.id)
              const radA = radians(angleA)

              // Calcular vector director de la pared (normalizado)
              const wallDir = new Vector3(Math.cos(radA), 0, Math.sin(radA))

              // Proyectar posiciones en la dirección de la pared
              const projA = posA.dot(wallDir)
              const projB = posB.dot(wallDir)

              // Ordenar por proyección a lo largo de la pared
              return projA - projB
            })
          }
        } else {
          // Si no están todos en la misma pared, usar ordenamiento por coordenadas absolutas
          sortedGroup = group.sort((a, b) => {
            const posA = new Vector3()
            const posB = new Vector3()
            a.matrix.decompose(posA, new Quaternion(), new Vector3())
            b.matrix.decompose(posB, new Quaternion(), new Vector3())

            const normalizedAngle = ((avgRotation % 360) + 360) % 360

            // Usar rangos de ángulos para determinar el eje de ordenación
            if (normalizedAngle > 315 || normalizedAngle <= 45) {
              // Cerca de 0°: Muebles alineados con el eje X. Ordenar por X ascendente.
              return posA.x - posB.x
            } else if (normalizedAngle > 45 && normalizedAngle <= 135) {
              // Cerca de 90°: Muebles alineados con el eje Z. Ordenar por Z ascendente.
              return posA.z - posB.z
            } else if (normalizedAngle > 135 && normalizedAngle <= 225) {
              // Cerca de 180°: Muebles alineados con el eje X. Ordenar por X descendente.
              return posB.x - posA.x
            } else if (normalizedAngle > 225 && normalizedAngle <= 315) {
              // Cerca de 270°: Muebles alineados con el eje Z. Ordenar por Z descendente.
              return posB.z - posA.z
            }

            // Fallback para cualquier caso no cubierto
            const fallbackRightVector = new Vector3(0, 0, 1)
            fallbackRightVector.applyAxisAngle(new Vector3(0, 1, 0), avgRotation * (Math.PI / 180))
            const projectionA = posA.dot(fallbackRightVector)
            const projectionB = posB.dot(fallbackRightVector)
            return projectionA - projectionB
          })
        }

        return sortedGroup.map((item, index) => ({
          id: item.id,
          catalogID: item.catalogID,
          position: { x: item.x, y: item.y, z: item.z },
          width: item.morphs?.find((morph) => morph.name === 'Ancho')?._default ?? item.width,
          height: item.height,
          thickness: item.thickness,
          rotation: get().getInitialAngleDegrees(item.id),
          orderIndex: index,
          type: item.type,
          description:
            'description' in item
              ? (item as { description?: string }).description || 'Unknown'
              : 'Unknown',
        }))
      }
    })

    return {
      groups: sortedGroups,
      totalGroups: sortedGroups.length,
      totalItemsInGroups: sortedGroups.reduce((sum, group) => sum + group.length, 0),
    }
  },
  getFirstItemPositionsOfSnappedGroups: () => {
    const contiguousResult = get().getContiguousItems()
    const items = get().items

    // Array para almacenar las posiciones de los primeros items de grupos (con o sin pared)
    const firstItemPositions: Array<{
      groupIndex: number
      itemId: string
      position: { x: number; y: number; z: number }
      snappedWall: string
      catalogID: string
      groupTotalWidth: number
      matrix: Matrix4
      rotation: number
    }> = []

    contiguousResult.groups.forEach((group, groupIndex) => {
      if (group.length === 0) {
        return
      }

      // Calcular el ancho total del grupo sumando todos los anchos
      const groupTotalWidth = group.reduce((totalWidth, groupItem) => {
        return totalWidth + groupItem.width
      }, 0)

      // Obtener el primer item del grupo (ya están ordenados)
      const firstGroupItem = group[0]
      const firstItem = items.get(firstGroupItem.id)

      if (!firstItem) {
        return
      }

      if (isItem(firstItem) || isLinkedItem(firstItem)) {
        // Procesar tanto grupos con pared como sin pared
        // Si tiene snappedWall, usarlo; si no, usar cadena vacía
        const snappedWall =
          firstItem.snappedWall && firstItem.snappedWall !== '' ? firstItem.snappedWall : ''

        firstItemPositions.push({
          groupIndex,
          itemId: firstGroupItem.id,
          position: firstGroupItem.position,
          snappedWall: snappedWall,
          catalogID: String(firstGroupItem.catalogID),
          groupTotalWidth,
          matrix: firstItem.matrix.clone(),
          rotation: firstGroupItem.rotation,
        })
      }
    })
    return firstItemPositions
  },
  deleteFrontals: () => {
    const itemsToDelete: AnyID[] = []
    get().items.forEach((item, id) => {
      if ((isItem(item) || isLinkedItem(item)) && item.catalogID === 'Frontal') {
        itemsToDelete.push(id)
      }
    })

    itemsToDelete.forEach((id) => {
      set((store) => {
        store.items.delete(id)
        store.layers[store.currentLayer].items.delete(id)
        store.layers[store.currentLayer].selected.delete(id)
        store.sceneBoundingBoxes.delete(id)
        store.collisionableItems.delete(id)
      })
    })

    // Actualizar el estado frontalActive cuando se eliminan todos los frontales
    if (itemsToDelete.length > 0) {
      const remainingFrontals = Array.from(get().items.values()).filter(
        (item) => (isItem(item) || isLinkedItem(item)) && item.catalogID === 'Frontal',
      )
      if (remainingFrontals.length === 0) {
        set((store) => {
          store.frontalActive = false
        })
      }
    }

    return itemsToDelete.length
  },
  drawFrontalsFromSnappedGroups: async (height: number, options?: { force?: boolean }) => {
    const autoEnabled = options?.force ? true : (get().frontalsAutoEnabled ?? true)
    if (!autoEnabled) {
      return
    }
    const isDrawingFrontals = get().isDrawingFrontals
    if (isDrawingFrontals) {
      return
    }

    set((store) => {
      store.isDrawingFrontals = true
    })

    try {
      get().deleteFrontals()

      await new Promise((resolve) => setTimeout(resolve, 100))

      const unitsWithSuffixes = mapUnitsWithSuffixes(get().items)

      const contiguousResult = get().getContiguousItems()
      const items = get().items as Map<AnyID, LinkedItem>
      const frontalPositions = get().getFirstItemPositionsOfSnappedGroups()

      // Función helper para obtener el grosor de la encimera basado en el acabado
      const getEncimeraThickness = (encimera: any): number => {
        if (!encimera) {
          return 0
        }

        const acabado = encimera.textures?.[0]?.acabado
        if (acabado) {
          const acabadoLower = acabado.toLowerCase()
          if (acabadoLower.includes('porcelanicas 12')) {
            return 12
          } else if (acabadoLower.includes('porcelanicas 20')) {
            return 20
          } else if (acabadoLower.includes('postformadas')) {
            return 30
          } else if (acabadoLower.includes('compacto')) {
            return 10
          }
        }

        // Si no hay acabado específico, usar el height de la encimera
        return encimera.height ?? 0
      }

      // Función helper para encontrar la encimera asociada a un grupo de unidades
      const findEncimeraForGroup = (group: any[]): any => {
        for (const groupItem of group) {
          const linkedItem = items.get(groupItem.id) as LinkedItem
          if (!linkedItem || linkedItem.type !== 'linkedItem') continue

          // Buscar encimera en los hijos de esta unidad
          if (linkedItem.childs) {
            for (const childId of linkedItem.childs) {
              const child = items.get(childId)
              if (
                child &&
                child.type === 'linkedSon' &&
                ((child as any).typeOfSon === 'Encimera' ||
                  child.description?.toLowerCase().includes('encimera'))
              ) {
                return child
              }
            }
          }
        }
        return null
      }

      for (const group of contiguousResult.groups) {
        const hasMBJ = group.some((item) => {
          const linkedItem = items.get(item.id) as LinkedItem
          return linkedItem && linkedItem.tipoNormalizado === 'MBJ'
        })

        if (!hasMBJ) {
          continue
        }

        const rinconeroIndex = group.findIndex((item) => isRinconeroCatalog(item.catalogID))

        if (rinconeroIndex !== -1) {
          const rinconero = group[rinconeroIndex]
          const rinconeroItem = items.get(rinconero.id)

          // Buscar encimera para este grupo (se reutiliza para ambos frontales)
          const encimera = findEncimeraForGroup(group)

          const rinconeroHand = rinconeroHandednessFromItem(rinconeroItem as LinkedItem | undefined)
          const rinconeroDerechaFlag = isRinconeroDerecha(rinconero.catalogID, rinconeroHand)
          const rinconeroIzquierdaFlag = isModularCornerIzquierda(rinconero.catalogID, rinconeroHand)

          // Obtener la orientación del rinconero
          const rinconeroRotation = get().getInitialAngleDegrees(rinconero.id)
          const normalizedRinconeroRotation = (Math.round(rinconeroRotation / 90) * 90) % 360

          // Dividir los elementos del grupo según su orientación
          const horizontalItems = []
          const verticalItems = []

          // Clasificar los elementos según estén alineados con el rinconero o perpendiculares
          group.forEach((item) => {
            const itemRotation = get().getInitialAngleDegrees(item.id)
            const normalizedItemRotation = (Math.round(itemRotation / 90) * 90) % 360

            // Si la rotación coincide con el rinconero o está 180° rotada (misma línea)
            if (
              normalizedItemRotation === normalizedRinconeroRotation ||
              normalizedItemRotation === (normalizedRinconeroRotation + 180) % 360
            ) {
              horizontalItems.push(item)
            }
            // Si está a 90° o 270° del rinconero (perpendicular)
            else if (
              normalizedItemRotation === (normalizedRinconeroRotation + 90) % 360 ||
              normalizedItemRotation === (normalizedRinconeroRotation + 270) % 360
            ) {
              verticalItems.push(item)
            }
          })

          // console.log(
          //   '[DEBUG] drawFrontalsFromSnappedGroupsNEW: Ítems horizontales:',
          //   horizontalItems.map((i) => i.id),
          //   'verticales:',
          //   verticalItems.map((i) => i.id),
          // )

          // Función helper para obtener regleta invertida (misma lógica que collideItems)
          const getRegletaInvertedValue = (
            value: number,
            morph: { max: number; min: number } | undefined,
          ) => {
            if (!morph) return value
            if (value >= morph.max) return morph.min
            if (value <= morph.min) return morph.max
            return morph.max + morph.min - value
          }

          // PRIMER FRONTAL: todos los elementos alineados con la primera dirección del rinconero
          // Calcular ancho total sumando SOLO anchos de elementos MBJ
          const widthFirstFrontal = horizontalItems.reduce((sum, item) => {
            const linkedItem = items.get(item.id) as LinkedItem
            if (linkedItem && linkedItem.tipoNormalizado === 'MBJ') {
              // Usar el valor por defecto del morph "Ancho" si existe, sino usar width
              const widthMorph = linkedItem.morphs?.find(
                (morph) => morph.name === 'Ancho' || morph.name === 'width',
              )
              const itemWidth = widthMorph?.default ?? widthMorph?._default ?? item.width
              return sum + itemWidth
            }
            return sum
          }, 0)

          // Encontrar el elemento más a la izquierda/derecha según la orientación para posicionar el frontal
          const rinconeroPos1 = new Vector3()
          const rinconeroQuat1 = new Quaternion()
          rinconeroItem.matrix.decompose(rinconeroPos1, rinconeroQuat1, new Vector3())

          // Ordenar elementos horizontales por posición para encontrar el primero de la línea
          horizontalItems.sort((a, b) => {
            const posA = new Vector3()
            const posB = new Vector3()
            items.get(a.id).matrix.decompose(posA, new Quaternion(), new Vector3())
            items.get(b.id).matrix.decompose(posB, new Quaternion(), new Vector3())

            // Proyectar sobre el eje X del rinconero para ordenarlos correctamente
            const directionVector = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat1)
            const projectionA = posA.clone().sub(rinconeroPos1).dot(directionVector)
            const projectionB = posB.clone().sub(rinconeroPos1).dot(directionVector)

            return projectionA - projectionB
          })

          // Usar la matriz del primer elemento ordenado para el frontal
          const firstAlignedItem = items.get(horizontalItems[0].id)
          const firstFrontalMatrix = firstAlignedItem.matrix.clone()

          // Ajustar posición Y del primer frontal basado en encimera
          if (encimera) {
            // Buscar la altura máxima de los items MBJ del grupo (excluyendo regletas)
            let maxUnitHeight = 0
            for (const groupItem of group) {
              const item = items.get(groupItem.id) as LinkedItem
              if (item && item.tipoNormalizado === 'MBJ') {
                const catalogIDStr = item.catalogID?.toString().toUpperCase() || ''
                const isRegleta =
                  catalogIDStr.startsWith('RB') ||
                  item.description?.toLowerCase().includes('regleta')

                if (!isRegleta && item.height && item.height > maxUnitHeight) {
                  maxUnitHeight = item.height
                }
              }
            }

            // Si no encontramos altura de unidad no-regleta, usar la máxima altura del grupo
            if (maxUnitHeight === 0) {
              for (const groupItem of group) {
                const item = items.get(groupItem.id) as LinkedItem
                if (item && item.height && item.height > maxUnitHeight) {
                  maxUnitHeight = item.height
                }
              }
            }

            const unitHeight = maxUnitHeight || (firstAlignedItem.height ?? 0)
            const encimeraThickness = getEncimeraThickness(encimera)
            const newY = unitHeight + encimeraThickness

            const currentPos = new Vector3()
            const quaternion = new Quaternion()
            const scale = new Vector3()
            firstFrontalMatrix.decompose(currentPos, quaternion, scale)
            firstFrontalMatrix.setPosition(currentPos.x, newY, currentPos.z)
          }

          const firstFrontalId = await get().drawItem(
            getId('linkedItem'),
            'Frontal',
            '',
            true,
            firstFrontalMatrix,
            'items',
          )

          // Configurar dimensiones del primer frontal
          get().editMorph(firstFrontalId, 'Ancho', widthFirstFrontal)
          get().editMorph(firstFrontalId, 'Alto', height)
          get().editItem(firstFrontalId, { width: widthFirstFrontal, height })
          get().setOBB(firstFrontalId)

          // SEGUNDO FRONTAL: perpendicular, desde la esquina del rinconero
          // Calcular ancho total sumando SOLO anchos de elementos MBJ
          const widthSecondFrontalNoRinconero = verticalItems.reduce((sum, item) => {
            const linkedItem = items.get(item.id) as LinkedItem
            if (linkedItem && linkedItem.tipoNormalizado === 'MBJ') {
              // Usar el valor por defecto del morph "Ancho" si existe, sino usar width
              const widthMorph = linkedItem.morphs?.find(
                (morph) => morph.name === 'Ancho' || morph.name === 'width',
              )
              const itemWidth = widthMorph?.default ?? widthMorph?._default ?? item.width
              return sum + itemWidth
            }
            return sum
          }, 0)

          // Ajustar ancho y offset según el tipo de rinconero usando la misma lógica que collideItems
          let widthSecondFrontal: number
          let secondFrontalMatrix: Matrix4
          let newPosition: Vector3
          let newQuaternion: Quaternion

          const rinconeroPos = new Vector3()
          const rinconeroQuat = new Quaternion()
          const rinconeroScale = new Vector3()
          rinconeroItem.matrix.decompose(rinconeroPos, rinconeroQuat, rinconeroScale)

          if (rinconeroDerechaFlag) {
            // RINCONERO DERECHO: Calcular posición manual desde el borde derecho del rinconero

            // Ancho del frontal = items verticales + thickness + regleta del rinconero
            widthSecondFrontal =
              widthSecondFrontalNoRinconero +
              rinconero.thickness +
              (getRegletaInvertedValue(
                rinconeroItem.regleta || 0,
                rinconeroItem.morphs?.find((m) => m.name === 'Regleta'),
              ) || 0)

            // 1. Posición base: desde el rinconero, desplazar por su ancho en el eje X local
            const rinconeroXAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
            newPosition = rinconeroPos.clone().add(rinconeroXAxis.multiplyScalar(rinconero.width))

            // 2. Rotación: -90° en Y (perpendicular hacia la derecha)
            const rotationAdjustment = new Quaternion().setFromAxisAngle(
              new Vector3(0, 1, 0),
              -Math.PI / 2,
            )
            newQuaternion = rinconeroQuat.clone().multiply(rotationAdjustment)

            // Reconstruir matriz
            secondFrontalMatrix = new Matrix4().compose(newPosition, newQuaternion, rinconeroScale)
          } else if (rinconeroIzquierdaFlag) {
            // RINCONERO IZQUIERDO: Ancho del frontal = items verticales + thickness (NO width)

            // CORREGIDO: Usar thickness en lugar de width del rinconero
            widthSecondFrontal =
              widthSecondFrontalNoRinconero +
              rinconero.thickness +
              (getRegletaInvertedValue(
                rinconeroItem.regleta || 0,
                rinconeroItem.morphs?.find((m) => m.name === 'Regleta'),
              ) || 0)

            // 1. Posición: exactamente en la posición del rinconero
            newPosition = rinconeroPos.clone()

            // 2. Rotación: +90° en Y (perpendicular hacia la izquierda)
            const rotationAdjustment = new Quaternion().setFromAxisAngle(
              new Vector3(0, 1, 0),
              Math.PI / 2,
            )
            newQuaternion = rinconeroQuat.clone().multiply(rotationAdjustment)

            // 3. Desplazar en el eje X local del nuevo quaternion (hacia atrás en la nueva orientación)
            const rinconeroOffset =
              rinconero.thickness +
              (getRegletaInvertedValue(
                rinconeroItem.regleta || 0,
                rinconeroItem.morphs?.find((m) => m.name === 'Regleta'),
              ) || 0) +
              widthSecondFrontalNoRinconero
            const xAxisAdjusted = new Vector3(1, 0, 0).applyQuaternion(newQuaternion)
            newPosition.add(xAxisAdjusted.multiplyScalar(-rinconeroOffset))

            // Reconstruir matriz
            secondFrontalMatrix = new Matrix4().compose(newPosition, newQuaternion, rinconeroScale)
          } else {
            // Fallback (no debería ocurrir)
            widthSecondFrontal =
              widthSecondFrontalNoRinconero + rinconero.thickness + rinconeroItem.regleta
            const rotationAdjustment = new Quaternion().setFromAxisAngle(
              new Vector3(0, 1, 0),
              -Math.PI / 2,
            )
            newQuaternion = rinconeroQuat.clone().multiply(rotationAdjustment)
            const rightVector = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
            newPosition = rinconeroPos
              .clone()
              .add(rightVector.clone().multiplyScalar(rinconero.width))
            secondFrontalMatrix = new Matrix4().compose(newPosition, newQuaternion, rinconeroScale)
          }

          // Ajustar posición Y del segundo frontal basado en encimera
          if (encimera) {
            // Buscar la altura máxima de los items MBJ del grupo (excluyendo regletas)
            let maxUnitHeight = 0
            for (const groupItem of group) {
              const item = items.get(groupItem.id) as LinkedItem
              if (item && item.tipoNormalizado === 'MBJ') {
                const catalogIDStr = item.catalogID?.toString().toUpperCase() || ''
                const isRegleta =
                  catalogIDStr.startsWith('RB') ||
                  item.description?.toLowerCase().includes('regleta')

                if (!isRegleta && item.height && item.height > maxUnitHeight) {
                  maxUnitHeight = item.height
                }
              }
            }

            // Si no encontramos altura de unidad no-regleta, usar la máxima altura del grupo
            if (maxUnitHeight === 0) {
              for (const groupItem of group) {
                const item = items.get(groupItem.id) as LinkedItem
                if (item && item.height && item.height > maxUnitHeight) {
                  maxUnitHeight = item.height
                }
              }
            }

            const unitHeight = maxUnitHeight || (rinconeroItem.height ?? 0)
            const encimeraThickness = getEncimeraThickness(encimera)
            const newY = unitHeight + encimeraThickness

            const currentPos = new Vector3()
            const quaternion = new Quaternion()
            const scale = new Vector3()
            secondFrontalMatrix.decompose(currentPos, quaternion, scale)
            secondFrontalMatrix.setPosition(currentPos.x, newY, currentPos.z)
          }

          const secondFrontalId = await get().drawItem(
            getId('linkedItem'),
            'Frontal',
            '',
            true,
            secondFrontalMatrix,
            'items',
          )

          // Configurar dimensiones del segundo frontal
          get().editMorph(secondFrontalId, 'Ancho', widthSecondFrontal)
          get().editMorph(secondFrontalId, 'Alto', height)
          get().editItem(secondFrontalId, { width: widthSecondFrontal, height })
          get().setOBB(secondFrontalId)
        } else {
          // Caso normal: un solo frontal lineal
          // Verificar si al menos un item del grupo tiene snappedWall
          const hasAnySnappedWall = group.some((item) => {
            const linkedItem = items.get(item.id) as LinkedItem
            return linkedItem && linkedItem.snappedWall && linkedItem.snappedWall !== ''
          })

          // Filtrar el grupo según si hay algún snappedWall
          const filteredGroup = group.filter((item) => {
            const linkedItem = items.get(item.id) as LinkedItem

            // Si al menos un item tiene snappedWall, solo filtrar por MBJ
            if (hasAnySnappedWall) {
              return linkedItem && linkedItem.tipoNormalizado === 'MBJ'
            }

            // Si NINGUNO tiene snappedWall, no dibujar frontal (filtrar todo)
            return false
          })

          // Si no quedan ítems válidos en el grupo, saltar al siguiente
          if (filteredGroup.length === 0) {
            continue
          }

          // Buscar el frontalInfo correspondiente al primer ítem del grupo filtrado
          const groupFirstItemId = filteredGroup[0]?.id
          const frontalInfo = frontalPositions.find((info) => info.itemId === groupFirstItemId)
          const extraWidth = contiguousResult.totalDistancesBetweenGroups
          if (frontalInfo) {
            try {
              const groupTotalWidth = filteredGroup.reduce((totalWidth, groupItem) => {
                const item = items.get(groupItem.id) as LinkedItem
                if (!item) {
                  return totalWidth + groupItem.width
                }

                // Obtener el ancho correcto usando el morph "Ancho" con su valor por defecto
                // Esto es especialmente importante para regletas que pueden tener width = max pero default = valor real
                const widthMorph = item.morphs?.find(
                  (morph) => morph.name === 'Ancho' || morph.name === 'width',
                )

                let itemWidth: number
                if (widthMorph) {
                  // Usar el valor por defecto del morph en lugar del width actual
                  // Esto asegura que regletas y otros items usen su valor por defecto, no el máximo
                  itemWidth =
                    widthMorph.default ?? widthMorph._default ?? item.width ?? groupItem.width
                } else {
                  itemWidth = item.width ?? groupItem.width
                }

                return totalWidth + itemWidth
              }, 0)

              frontalInfo.groupTotalWidth = groupTotalWidth
              const frontalMatrix = frontalInfo.matrix.clone()

              const offset = new Vector3(0, 0, 0)
              const currentPosition = new Vector3()
              const quaternion = new Quaternion()
              const scale = new Vector3()

              frontalMatrix.decompose(currentPosition, quaternion, scale)
              offset.applyQuaternion(quaternion)
              currentPosition.add(offset)

              const encimera = findEncimeraForGroup(filteredGroup)
              if (encimera) {
                let maxUnitHeight = 0
                for (const groupItem of filteredGroup) {
                  const item = items.get(groupItem.id) as LinkedItem
                  if (item && item.tipoNormalizado === 'MBJ') {
                    // Excluir regletas (tienen catalogID que empieza con RB)
                    const catalogIDStr = item.catalogID?.toString().toUpperCase() || ''
                    const isRegleta =
                      catalogIDStr.startsWith('RB') ||
                      item.description?.toLowerCase().includes('regleta')

                    if (!isRegleta && item.height && item.height > maxUnitHeight) {
                      maxUnitHeight = item.height
                    }
                  }
                }

                // Si no encontramos altura de unidad no-regleta, usar la máxima altura del grupo
                if (maxUnitHeight === 0) {
                  for (const groupItem of filteredGroup) {
                    const item = items.get(groupItem.id) as LinkedItem
                    if (item && item.height && item.height > maxUnitHeight) {
                      maxUnitHeight = item.height
                    }
                  }
                }

                const unitHeight = maxUnitHeight
                const encimeraThickness = getEncimeraThickness(encimera)
                const newY = unitHeight + encimeraThickness
                currentPosition.y = newY
              }

              frontalMatrix.setPosition(currentPosition)
              // Generar un ID único para el frontal
              const frontalId = getId('linkedItem')

              const drawnFrontalId = await get().drawItem(
                frontalId,
                'Frontal',
                '',
                true,
                frontalMatrix,
                'items',
                undefined,
                false,
              )

              await new Promise((resolve) => setTimeout(resolve, 500))

              const frontalAfterTimeout = get().items.get(drawnFrontalId)
              if (!frontalAfterTimeout) {
                continue
              }

              get().editMorph(drawnFrontalId, 'Ancho', frontalInfo.groupTotalWidth + extraWidth)
              get().editMorph(drawnFrontalId, 'Alto', height)
              get().editItem(drawnFrontalId, {
                width: frontalInfo.groupTotalWidth,
                height: height,
              })
              set((store) => {
                store.collisionableItems.delete(drawnFrontalId)
              })
              get().setOBB(drawnFrontalId)
            } catch (error) {
              console.error(
                `[DEBUG error] drawFrontalsFromSnappedGroupsNEW: Error dibujando frontal para grupo ${frontalInfo.groupIndex}:`,
                error,
              )
            }
          }
        }
      }

      // Restaurar sufijos (si es necesario) después de procesar todos los grupos
      if (unitsWithSuffixes.size > 0) {
        setTimeout(async () => {
          for (const [unitId, suffix] of unitsWithSuffixes) {
            const unit = get().items.get(unitId)
            if (unit && unit.type === 'linkedItem') {
              const currentCatalogID = unit.catalogID.toString()
              const baseCatalogID = getBaseCatalogID(currentCatalogID)
              const expectedCatalogID = buildCatalogIDWithSuffix(baseCatalogID, suffix)

              if (currentCatalogID !== expectedCatalogID) {
                try {
                  set((s) => {
                    const newItems = new Map(s.items)
                    const item = { ...newItems.get(unitId) }

                    if (item && item.type === 'linkedItem') {
                      item.catalogID = expectedCatalogID

                      if (item.glbPath) {
                        item.glbPath = updateGlbPathWithSuffix(item.glbPath, suffix)
                      }

                      if (item.referencia) {
                        item.referencia = updateReferenciaWithSuffix(item.referencia, suffix)
                      }

                      newItems.set(unitId, item)
                    }

                    return {
                      items: newItems,
                    }
                  })
                } catch (error) {
                  console.error(`Error restaurando sufijo para unidad ${unitId}:`, error)
                }
              }
            }
          }
        }, 1000)
      }
    } finally {
      // Resetear el flag de ejecución
      set((store) => {
        store.isDrawingFrontals = false
      })
    }
  },
  deleteLaterales: () => {
    const itemsToDelete: AnyID[] = []
    const lateralIds = new Set<AnyID>()

    get().items.forEach((item, id) => {
      if ((isItem(item) || isLinkedItem(item)) && item.catalogID) {
        const catalogID = item.catalogID.toString()
        if (
          catalogID.startsWith('LA') ||
          catalogID.startsWith('LB') ||
          catalogID.startsWith('LC') ||
          catalogID.startsWith('CC') ||
          catalogID.startsWith('CB')
        ) {
          itemsToDelete.push(id)
          lateralIds.add(id)
        }
      }
    })

    if (lateralIds.size > 0) {
      get().items.forEach((item, id) => {
        if (!item || (item as any).type !== 'linkedSon') return
        const firstParentID = (item as any).firstParentID as AnyID | undefined
        if (!firstParentID) return
        if (!lateralIds.has(firstParentID)) return

        itemsToDelete.push(id)
      })
    }

    itemsToDelete.forEach((id) => {
      set((store) => {
        store.items.delete(id)
        store.layers[store.currentLayer].items.delete(id)
        store.layers[store.currentLayer].selected.delete(id)
        store.sceneBoundingBoxes.delete(id)
        store.collisionableItems.delete(id)
      })
    })

    return itemsToDelete.length
  },
  updateFrontalsForUnit: (unitId: string) => {
    const items = get().items
    const unit = items.get(unitId)

    // Verificar que la unidad existe y no es un frontal
    if (!unit) {
      return
    }
    if ((isItem(unit) || isLinkedItem(unit)) && unit.catalogID === 'Frontal') {
      return
    }

    const contiguousResult = get().getContiguousItems()

    const unitGroup = contiguousResult.groups.find((group) =>
      group.some((item) => item.id === unitId),
    )

    if (!unitGroup || unitGroup.length === 0) {
      return
    }

    // Calcular el ancho total del grupo usando los anchos actualizados de los items
    const groupTotalWidth = unitGroup.reduce((totalWidth, groupItem) => {
      const item = items.get(groupItem.id)
      if (!item || (!isItem(item) && !isLinkedItem(item))) {
        return totalWidth + groupItem.width
      }
      // Usar el ancho actualizado del item (priorizar width property que es el más actualizado)
      // Si width no está disponible, usar el morph default (valor actualizado), y si no, usar el groupItem.width
      // NO usar _default porque es el valor por defecto original, no el actualizado
      const itemWidth =
        item.width ??
        item.morphs?.find((morph) => morph.name === 'Ancho')?.default ??
        groupItem.width
      return totalWidth + itemWidth
    }, 0)

    const firstGroupItemId = unitGroup[0]?.id
    if (!firstGroupItemId) {
      return
    }

    const allFrontals = Array.from(items.values()).filter(
      (item) => (isItem(item) || isLinkedItem(item)) && item.catalogID === 'Frontal',
    )

    if (allFrontals.length === 0) {
      return
    }

    const firstGroupItem = items.get(firstGroupItemId)
    if (!firstGroupItem || (!isItem(firstGroupItem) && !isLinkedItem(firstGroupItem))) {
      return
    }

    const firstGroupItemSnappedWall = firstGroupItem.snappedWall || ''

    let matchingFrontals = allFrontals.filter((frontal) => {
      if (!isItem(frontal) && !isLinkedItem(frontal)) return false
      const frontalSnappedWall = frontal.snappedWall || ''
      return frontalSnappedWall === firstGroupItemSnappedWall
    })

    if (matchingFrontals.length > 1) {
      const firstGroupItemPos = new Vector3()
      const firstGroupItemQuat = new Quaternion()
      firstGroupItem.matrix.decompose(firstGroupItemPos, firstGroupItemQuat, new Vector3())

      // Encontrar el frontal más cercano al primer item del grupo
      let closestFrontal = matchingFrontals[0]
      let minDistance = Infinity

      matchingFrontals.forEach((frontal) => {
        if (!isItem(frontal) && !isLinkedItem(frontal)) return
        const frontalPos = new Vector3()
        frontal.matrix.decompose(frontalPos, new Quaternion(), new Vector3())
        const distance = firstGroupItemPos.distanceTo(frontalPos)
        if (distance < minDistance) {
          minDistance = distance
          closestFrontal = frontal
        }
      })

      matchingFrontals = [closestFrontal]
    } else if (matchingFrontals.length === 0) {
      // Intentar buscar por posición si no hay coincidencia por snappedWall
      const firstGroupItemPos = new Vector3()
      firstGroupItem.matrix.decompose(firstGroupItemPos, new Quaternion(), new Vector3())

      let closestFrontal = allFrontals[0]
      let minDistance = Infinity

      allFrontals.forEach((frontal) => {
        if (!isItem(frontal) && !isLinkedItem(frontal)) return
        const frontalPos = new Vector3()
        frontal.matrix.decompose(frontalPos, new Quaternion(), new Vector3())
        const distance = firstGroupItemPos.distanceTo(frontalPos)
        if (distance < minDistance) {
          minDistance = distance
          closestFrontal = frontal
        }
      })

      matchingFrontals = [closestFrontal]
    }

    // Actualizar el/los frontal(es) encontrado(s)
    // Guardar la selección actual para restaurarla después
    const currentLayer = get().currentLayer
    const originalSelected = new Set(get().layers[currentLayer].selected)

    matchingFrontals.forEach((frontal) => {
      if (!isItem(frontal) && !isLinkedItem(frontal)) return

      // Temporalmente seleccionar solo el frontal para que editMorph solo lo afecte
      set((store) => {
        store.layers[store.currentLayer].selected.clear()
        store.layers[store.currentLayer].selected.add(frontal.id)
      })

      // Actualizar el ancho del frontal
      get().editMorph(frontal.id, 'Ancho', groupTotalWidth)
      get().editItem(frontal.id, { width: groupTotalWidth })
      get().setOBB(frontal.id)
    })

    set((store) => {
      store.layers[store.currentLayer].selected = originalSelected
    })
  },
  drawLateralesForGroups: async (options?: { force?: boolean }) => {
    const autoEnabled = options?.force ? true : (get().lateralsAutoEnabled ?? true)
    if (!autoEnabled) {
      return
    }

    get().deleteLaterales()
    const { groups } = get().getContiguousItemsForLaterals()
    const items = get().items

    // Función helper para obtener el valor REAL (invertido) de regleta para cálculos geométricos
    const getRegletaRealValue = (item: LinkedItem): number => {
      if (!item.regleta) return 0
      const regletaMorph = item.morphs?.find((m) => m.name === 'Regleta')
      if (!regletaMorph) return item.regleta
      // El valor está invertido: si regleta = 20, el valor real es 120; si regleta = 120, el valor real es 20
      const storedValue = item.regleta
      if (storedValue >= regletaMorph.max) return regletaMorph.min
      if (storedValue <= regletaMorph.min) return regletaMorph.max
      return regletaMorph.max + regletaMorph.min - storedValue
    }

    // Función helper para obtener el valor invertido de regleta (igual que en collideItems)
    const getRegletaInvertedValue = (value: number, morph: Morphs | undefined): number => {
      if (!morph) return value
      if (value >= morph.max) return morph.min
      if (value <= morph.min) return morph.max
      return morph.max + morph.min - value
    }

    // Función para encontrar la unidad pegada a la regleta de un KARB/KBR
    const findUnitSnappedToRegleta = (
      rinconero: LinkedItem,
      items: Map<string, SceneObjects>,
    ): LinkedItem | null => {
      const rinconeroPos = new Vector3()
      const rinconeroQuat = new Quaternion()
      rinconero.matrix.decompose(rinconeroPos, rinconeroQuat, new Vector3())

      const isKBR = rinconero.catalogID?.toString().toUpperCase().includes('KBR')
      const isKAR = rinconero.catalogID?.toString().toUpperCase().includes('KARB')
      if ((!isKBR && !isKAR) || !rinconero.regleta) return null

      const catalog = rinconero.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')
      const isRightRinconero = !isLeftRinconero

      for (const [id, otherItem] of items) {
        if (id === rinconero.id) continue
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue
        if (!otherItem.visible) continue

        const otherPos = new Vector3()
        const otherQuat = new Quaternion()
        otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

        const item1XAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
        const directionMultiplier =
          item1XAxis.dot(new Vector3(otherPos.x - rinconeroPos.x, 0, otherPos.z - rinconeroPos.z)) <
          0
            ? -1
            : 1

        const regletaMorph = rinconero.morphs?.find((m) => m.name === 'Regleta')
        const regletaValue = isKAR
          ? rinconero.regleta || 0
          : getRegletaInvertedValue(rinconero.regleta || 0, regletaMorph)

        let expectedPosition: Vector3

        if (isRightRinconero && directionMultiplier > 0) {
          expectedPosition = rinconeroPos
            .clone()
            .add(item1XAxis.multiplyScalar(directionMultiplier * rinconero.width))
          const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinconeroQuat)
          const rinconeroOffset = (rinconero.thickness || 0) + regletaValue
          expectedPosition.add(zAxis.multiplyScalar(rinconeroOffset))
        } else if (isLeftRinconero && directionMultiplier < 0) {
          expectedPosition = rinconeroPos.clone()
          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const adjustedQuat = rinconeroQuat.clone().multiply(rotationAdjustment)
          const rinconeroOffset = (rinconero.thickness || 0) + regletaValue + (otherItem.width || 0)
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
          expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))
        } else {
          continue
        }

        const distance = expectedPosition.distanceTo(otherPos)
        if (distance <= 60) {
          return otherItem as LinkedItem
        }
      }

      return null
    }

    // Función para verificar si hay una unidad contigua en el lado donde se va a dibujar el lateral
    // Usa la misma lógica que collideItems para detectar contiguidad con regletas
    const hasContiguousUnit = (item: LinkedItem, isFirstItem: boolean): boolean => {
      const items = get().items
      const itemPos = new Vector3()
      const itemQuat = new Quaternion()
      item.matrix.decompose(itemPos, itemQuat, new Vector3())

      // Variable para rastrear si el item está pegado a alguna regleta (para evitar verificar contiguidad normal con KBR con regleta)
      let isPegadoARegleta = false

      for (const [id, otherItem] of items) {
        if (id === item.id) continue
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue // Ignorar frontales
        if (!otherItem.visible) continue

        const otherPos = new Vector3()
        const otherQuat = new Quaternion()
        otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

        const isKBR = item.catalogID?.toString().toUpperCase().includes('KBR')
        const isKAR = item.catalogID?.toString().toUpperCase().includes('KARB')
        const hasRegleta = (isKBR || isKAR) && item.regleta
        const catalog = item.catalogID?.toString().toLowerCase() ?? ''
        const itemRinconeroHand = rinconeroHandednessFromItem(item)
        const isLeftRinconero =
          isModularCornerIzquierda(item.catalogID, itemRinconeroHand) ||
          ((isKBR || isKAR) && catalog.endsWith('i'))
        const isRightRinconero =
          isRinconeroDerecha(item.catalogID, itemRinconeroHand) ||
          ((isKBR || isKAR) && !catalog.endsWith('i'))

        if (hasRegleta) {
          // Determinar en qué lado está la regleta
          // KBR/KAR izquierdo: regleta en lado izquierdo (isFirstItem = true)
          // KBR/KAR derecho: regleta en lado derecho (isFirstItem = false)
          const isRegletaSide =
            (isLeftRinconero && isFirstItem) || (isRightRinconero && !isFirstItem)

          if (isRegletaSide) {
            // Calcular la posición donde collideItems colocaría otherItem pegado a la regleta
            const item1XAxis = new Vector3(1, 0, 0).applyQuaternion(itemQuat)
            const directionMultiplier =
              item1XAxis.dot(new Vector3(otherPos.x - itemPos.x, 0, otherPos.z - itemPos.z)) < 0
                ? -1
                : 1

            let expectedPosition: Vector3
            const regletaMorph = item.morphs?.find((m) => m.name === 'Regleta')
            // KAR usa regleta directa (no invertida), KBR usa regleta invertida
            const regletaValue = isKAR
              ? item.regleta || 0 // KAR: regleta directa
              : getRegletaInvertedValue(item.regleta || 0, regletaMorph) // KBR: regleta invertida

            if (isRightRinconero && directionMultiplier > 0) {
              // KBR/KAR derecho: mueble normal pegado a la derecha
              expectedPosition = itemPos
                .clone()
                .add(item1XAxis.multiplyScalar(directionMultiplier * item.width))
              const zAxis = new Vector3(0, 0, 1).applyQuaternion(itemQuat)
              const rinconeroOffset = (item.thickness || 0) + regletaValue
              expectedPosition.add(zAxis.multiplyScalar(rinconeroOffset))
            } else if (isLeftRinconero && directionMultiplier < 0) {
              // KBR/KAR izquierdo: mueble normal pegado a la izquierda
              expectedPosition = itemPos.clone()
              const rotationAdjustment = new Quaternion().setFromAxisAngle(
                new Vector3(0, 1, 0),
                Math.PI / 2,
              )
              const adjustedQuat = itemQuat.clone().multiply(rotationAdjustment)
              const rinconeroOffset = (item.thickness || 0) + regletaValue + (otherItem.width || 0)
              const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
              expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))
            } else {
              continue // No es el caso de regleta
            }

            const distance = expectedPosition.distanceTo(otherPos)

            if (distance <= 60) {
              return true
            }
          }
        }

        // El item actual NO es KBR/KAR, verificar si está pegado a la regleta de otherItem (KBR/KAR)
        const otherIsKBR = otherItem.catalogID?.toString().toUpperCase().includes('KBR')
        const otherIsKAR = otherItem.catalogID?.toString().toUpperCase().includes('KARB')
        const otherHasRegleta = (otherIsKBR || otherIsKAR) && otherItem.regleta

        if (!isKBR && !isKAR && otherHasRegleta) {
          const otherCatalog = otherItem.catalogID?.toString().toLowerCase() ?? ''
          const otherRinconeroHand = rinconeroHandednessFromItem(otherItem)
          const otherIsLeftRinconero =
            isModularCornerIzquierda(otherItem.catalogID, otherRinconeroHand) ||
            ((otherIsKBR || otherIsKAR) && otherCatalog.endsWith('i'))
          const otherIsRightRinconero =
            isRinconeroDerecha(otherItem.catalogID, otherRinconeroHand) ||
            ((otherIsKBR || otherIsKAR) && !otherCatalog.endsWith('i'))

          const otherItemXAxis = new Vector3(1, 0, 0).applyQuaternion(otherQuat)
          const directionMultiplier =
            otherItemXAxis.dot(new Vector3(itemPos.x - otherPos.x, 0, itemPos.z - otherPos.z)) < 0
              ? -1
              : 1

          let expectedPosition: Vector3
          const otherRegletaMorph = otherItem.morphs?.find((m) => m.name === 'Regleta')
          // KAR usa regleta directa (no invertida), KBR usa regleta invertida
          const otherRegletaValue = otherIsKAR
            ? otherItem.regleta || 0 // KAR: regleta directa
            : getRegletaInvertedValue(otherItem.regleta || 0, otherRegletaMorph) // KBR: regleta invertida

          if (otherIsRightRinconero && directionMultiplier > 0) {
            // KBR/KAR derecho: Item normal pegado a la regleta
            // La regleta está en el lado IZQUIERDO del item (donde colisionó)
            // El lateral debe estar en el lado DERECHO (opuesto, exterior)
            expectedPosition = otherPos
              .clone()
              .add(otherItemXAxis.multiplyScalar(directionMultiplier * otherItem.width))
            const zAxis = new Vector3(0, 0, 1).applyQuaternion(otherQuat)
            const rinconeroOffset = (otherItem.thickness || 0) + otherRegletaValue
            expectedPosition.add(zAxis.multiplyScalar(rinconeroOffset))

            const distance = expectedPosition.distanceTo(itemPos)

            if (distance < 50) {
              isPegadoARegleta = true
              if (isFirstItem) {
                return true
              } else {
                return false // Permitir dibujar lateral en el lado opuesto
              }
            }
          } else if (otherIsLeftRinconero && directionMultiplier < 0) {
            expectedPosition = otherPos.clone()
            const rotationAdjustment = new Quaternion().setFromAxisAngle(
              new Vector3(0, 1, 0),
              Math.PI / 2,
            )
            const adjustedQuat = otherQuat.clone().multiply(rotationAdjustment)
            const rinconeroOffset =
              (otherItem.thickness || 0) + otherRegletaValue + (item.width || 0)
            const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
            expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))

            const distance = expectedPosition.distanceTo(itemPos)
            if (distance < 50) {
              isPegadoARegleta = true

              if (!isFirstItem) {
                return true
              } else {
                return false // Permitir dibujar lateral en el lado opuesto
              }
            }
          }
        }

        if (!isKBR && !isKAR && isPegadoARegleta && (otherIsKBR || otherIsKAR) && otherHasRegleta) {
          continue
        }

        // Usar OBB reales si están disponibles para una detección más precisa
        const sceneBoundingBoxes = get().sceneBoundingBoxes
        const itemOBB = sceneBoundingBoxes.get(item.id)
        const otherItemOBB = sceneBoundingBoxes.get(id)

        // Calcular la posición donde iría el lateral usando la misma lógica que processLateral
        const lateralWidth = 19
        let lateralPosition: Vector3
        let lateralMatrix: Matrix4

        if (isFirstItem) {
          const offset = -19
          lateralMatrix = getRelativeMatrix(item.matrix, offset, item, 0)
        } else {
          const offset = item.width
          lateralMatrix = getRelativeMatrix(item.matrix, offset, item, 0)
        }

        const lateralPos = new Vector3()
        const lateralQuat = new Quaternion()
        const lateralScale = new Vector3()
        lateralMatrix.decompose(lateralPos, lateralQuat, lateralScale)
        lateralPosition = lateralPos

        // Crear una OBB aproximada para el lateral (19mm de ancho, misma altura y profundidad que el item)
        const lateralHeight = item.height || 0
        const lateralDepth = item.thickness || 0
        const lateralOBB = new OBB(
          lateralPosition,
          new Vector3(lateralWidth / 2, lateralHeight / 2, lateralDepth / 2),
        )
        const lateralRotationMatrix = new Matrix3().setFromMatrix4(
          new Matrix4().makeRotationFromQuaternion(lateralQuat),
        )
        lateralOBB.rotation = lateralRotationMatrix

        if (itemOBB && otherItemOBB) {
          if (lateralOBB.intersectsOBB(otherItemOBB.boundingBox)) {
            return true
          }
        }

        const distance = lateralPosition.distanceTo(otherPos)

        // Si el otro item está muy cerca, verificar alineación usando límites más precisos
        const maxItemDimension = Math.max(otherItem.width || 0, otherItem.thickness || 0)
        // Reducir el umbral para ser más estricto cuando las unidades están cerca pero no snapadas
        const distanceThreshold = Math.min(maxItemDimension + 50, 200)
        if (distance < distanceThreshold) {
          // Verificar si están en la misma pared y alineados
          if (
            areItemsAlignedOnWall(
              item,
              otherItem,
              isFirstItem ? 'left' : 'right',
              getRegletaRealValue,
            )
          ) {
            return true
          }
        }
      }
      return false
    }

    // Función para verificar si dos items están alineados en la misma pared
    const areItemsAlignedOnWall = (
      item1: LinkedItem,
      item2: LinkedItem,
      side: 'left' | 'right',
      getRegletaRealValue: (item: LinkedItem) => number,
    ): boolean => {
      // Extraer posiciones y rotaciones
      const pos1 = new Vector3()
      const quat1 = new Quaternion()
      item1.matrix.decompose(pos1, quat1, new Vector3())

      const pos2 = new Vector3()
      const quat2 = new Quaternion()
      item2.matrix.decompose(pos2, quat2, new Vector3())

      // Verificar si comparten la misma pared
      if (item1.snappedWall && item2.snappedWall && item1.snappedWall !== item2.snappedWall) {
        return false
      }

      const sceneBoundingBoxes = get().sceneBoundingBoxes
      const item1OBB = sceneBoundingBoxes.get(item1.id)
      const item2OBB = sceneBoundingBoxes.get(item2.id)

      if (item1OBB && item2OBB) {
        // Usar OBB reales para calcular límites más precisos
        const obb1 = item1OBB.boundingBox
        const obb2 = item2OBB.boundingBox

        // Obtener los puntos de las esquinas de las OBB proyectadas en el plano XZ
        const getOBBBoundsXZ = (obb: OBB) => {
          const corners = [
            new Vector3(-1, 0, -1),
            new Vector3(1, 0, -1),
            new Vector3(-1, 0, 1),
            new Vector3(1, 0, 1),
          ]
          const worldCorners = corners.map((corner) => {
            const worldCorner = corner.clone()
            // Multiplicar cada componente por el halfSize correspondiente
            worldCorner.x *= obb.halfSize.x
            worldCorner.y *= obb.halfSize.y
            worldCorner.z *= obb.halfSize.z
            worldCorner.applyMatrix3(obb.rotation)
            worldCorner.add(obb.center)
            return worldCorner
          })

          const xValues = worldCorners.map((c) => c.x)
          const zValues = worldCorners.map((c) => c.z)
          const minX = Math.min(...xValues)
          const maxX = Math.max(...xValues)
          const minZ = Math.min(...zValues)
          const maxZ = Math.max(...zValues)

          return { minX, maxX, minZ, maxZ, center: obb.center }
        }

        const bounds1 = getOBBBoundsXZ(obb1)
        const bounds2 = getOBBBoundsXZ(obb2)

        // Verificar superposición en el eje relevante usando límites OBB
        if (side === 'left') {
          // Para lateral izquierdo, verificar si el item2 está a la izquierda del item1
          // El lateral estaría en bounds1.minX - 19/2, verificar si bounds2.maxX está cerca
          const lateralX = bounds1.minX - 9.5 // 19/2
          const distanceLeft = Math.abs(bounds2.maxX - lateralX)
          // Verificar también si hay superposición en el eje Z (profundidad)
          const zOverlap = bounds1.minZ <= bounds2.maxZ + 20 && bounds1.maxZ >= bounds2.minZ - 20
          return distanceLeft < 30 && zOverlap // Tolerancia más estricta: 30 unidades
        } else {
          // Para lateral derecho, verificar si el item2 está a la derecha del item1
          // El lateral estaría en bounds1.maxX + 19/2, verificar si bounds2.minX está cerca
          const lateralX = bounds1.maxX + 9.5 // 19/2
          const distanceRight = Math.abs(bounds2.minX - lateralX)
          // Verificar también si hay superposición en el eje Z (profundidad)
          const zOverlap = bounds1.minZ <= bounds2.maxZ + 20 && bounds1.maxZ >= bounds2.minZ - 20
          return distanceRight < 30 && zOverlap // Tolerancia más estricta: 30 unidades
        }
      }

      // Fallback: Calcular los límites de cada item usando el valor REAL de regleta
      const getItemBounds = (item: LinkedItem, pos: Vector3, quat: Quaternion) => {
        const forward = new Vector3(0, 0, 1).applyQuaternion(quat)
        const right = new Vector3(1, 0, 0).applyQuaternion(quat)
        const center = pos.clone()
        const width = item.width
        // Usar el valor REAL (invertido) de regleta para cálculos geométricos
        const isKBR = item.catalogID?.toString().toUpperCase().includes('KBR')
        const regletaRealValue = isKBR && item.regleta ? getRegletaRealValue(item) : 0
        const depth = item.thickness + regletaRealValue

        return {
          front: center.clone().add(forward.multiplyScalar(depth / 2)),
          back: center.clone().add(forward.multiplyScalar(-depth / 2)),
          left: center.clone().add(right.multiplyScalar(-width / 2)),
          right: center.clone().add(right.multiplyScalar(width / 2)),
          center: center,
        }
      }

      const bounds1 = getItemBounds(item1, pos1, quat1)
      const bounds2 = getItemBounds(item2, pos2, quat2)

      // Verificar superposición en el eje relevante con tolerancia más estricta
      if (side === 'left') {
        // Para lateral izquierdo, verificar si el item2 está a la izquierda del item1
        const distanceLeft = bounds1.left.distanceTo(bounds2.right)
        // Verificar también superposición en profundidad
        const depthOverlap =
          Math.abs(bounds1.front.z - bounds2.back.z) < 50 ||
          Math.abs(bounds1.back.z - bounds2.front.z) < 50 ||
          (bounds1.front.z >= bounds2.back.z - 20 && bounds1.back.z <= bounds2.front.z + 20)
        return distanceLeft < 30 && depthOverlap // Tolerancia más estricta: 30 unidades
      } else {
        // Para lateral derecho, verificar si el item2 está a la derecha del item1
        const distanceRight = bounds1.right.distanceTo(bounds2.left)
        // Verificar también superposición en profundidad
        const depthOverlap =
          Math.abs(bounds1.front.z - bounds2.back.z) < 50 ||
          Math.abs(bounds1.back.z - bounds2.front.z) < 50 ||
          (bounds1.front.z >= bounds2.back.z - 20 && bounds1.back.z <= bounds2.front.z + 20)
        return distanceRight < 30 && depthOverlap // Tolerancia más estricta: 30 unidades
      }
    }

    for (const group of groups) {
      // if (group.length === 0) continue

      // Obtener primer y último elemento del grupo
      const firstItemInfo = group[0]
      const lastItemInfo = group[group.length - 1]

      const firstItem = items.get(firstItemInfo.id) as LinkedItem
      const lastItem = items.get(lastItemInfo.id) as LinkedItem

      if (!firstItem || !lastItem) continue

      const catalogUpper = firstItem.catalogID?.toString().toUpperCase() ?? ''
      // Separar KALB (en L) de KARB (rectos en esquina, igual que KBR)
      const isKALB = catalogUpper.includes('KALB')
      const isKARB = catalogUpper.includes('KARB')
      const isKBRGroup = Boolean(firstItemInfo?.isKBR)

      if (isKBRGroup) {
        const hasLeftContiguous = hasContiguousUnit(firstItem, true)
        const hasRightContiguous = hasContiguousUnit(firstItem, false)

        if (!hasLeftContiguous) {
          await processLateralKBR(firstItem, true)
        }
        if (!hasRightContiguous) {
          await processLateralKBR(firstItem, false)
        }
        continue
      }

      if (isKARB) {
        const hasLeftContiguous = hasContiguousUnit(firstItem, true)
        const hasRightContiguous = hasContiguousUnit(firstItem, false)

        if (!hasLeftContiguous) {
          await processLateralKARB(firstItem, true)
        } else {
          if (firstItem.regleta) {
            const catalog = firstItem.catalogID?.toString().toLowerCase() ?? ''
            const isLeftRinconero = catalog.endsWith('i')
            if (isLeftRinconero) {
              const unitPegadaARegleta = findUnitSnappedToRegleta(firstItem, items)
              if (unitPegadaARegleta) {
                const lateralSide = true // true = izquierdo (extremo opuesto)
                await processLateralKARB(unitPegadaARegleta, lateralSide)
              }
            }
          }
        }
        if (!hasRightContiguous) {
          await processLateralKARB(firstItem, false)
        } else {
          if (firstItem.regleta) {
            const catalog = firstItem.catalogID?.toString().toLowerCase() ?? ''
            const isLeftRinconero = catalog.endsWith('i')
            const isRightRinconero = !isLeftRinconero

            if (isRightRinconero) {
              const unitPegadaARegleta = findUnitSnappedToRegleta(firstItem, items)
              if (unitPegadaARegleta) {
                const lateralSide = false // false = derecho (extremo opuesto)
                await processLateralKARB(unitPegadaARegleta, lateralSide)
              }
            }
          }
        }
        continue
      }

      // Para KALB (en L), usar siempre la lógica específica de rinconeros en L
      if (isKALB) {
        await processLateralKALB(firstItem, true)
        await processLateralKALB(firstItem, false)
        continue
      }

      // Caso general (no rinconero alto ni KBR)
      await processLateral(firstItem, true)
      if (group.length === 1) {
        await processLateral(firstItem, false)
      }
      if (group.length > 1) {
        await processLateral(lastItem, false)
      }
    }

    /**
     * Procesa un lateral para un mueble específico
     */
    async function processLateral(item: LinkedItem, isFirstItem: boolean) {
      // Verificar si hay una unidad contigua (especialmente importante para unidades pegadas a regletas)
      const hasContiguous = hasContiguousUnit(item, isFirstItem)
      if (hasContiguous) {
        return
      }

      // Verificar si el item está pegado a una regleta y determinar el lado correcto para el lateral
      // Para KBR derecho: lateral en lado opuesto al gizmo (lado izquierdo, isFirstItem = true)
      // Para KBR izquierdo: lateral en lado del gizmo (lado derecho, isFirstItem = false)
      const items = get().items
      const itemPos = new Vector3()
      const itemQuat = new Quaternion()
      item.matrix.decompose(itemPos, itemQuat, new Vector3())

      let shouldDrawLateral = true
      // const correctSide = isFirstItem

      for (const [id, otherItem] of items) {
        if (id === item.id) continue
        if (!isLinkedItem(otherItem)) continue
        if (otherItem.catalogID === 'Frontal') continue
        if (!otherItem.visible) continue

        const otherIsKBR = otherItem.catalogID?.toString().toUpperCase().includes('KBR')
        const otherHasRegleta = otherIsKBR && otherItem.regleta

        if (!otherIsKBR || !otherHasRegleta) continue

        const otherPos = new Vector3()
        const otherQuat = new Quaternion()
        otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

        const otherCatalog = otherItem.catalogID?.toString().toLowerCase() ?? ''
        const otherRinconeroHand = rinconeroHandednessFromItem(otherItem)
        const otherIsLeftRinconero =
          isModularCornerIzquierda(otherItem.catalogID, otherRinconeroHand) ||
          (otherIsKBR && otherCatalog.endsWith('i'))
        const otherIsRightRinconero =
          isRinconeroDerecha(otherItem.catalogID, otherRinconeroHand) ||
          (otherIsKBR && !otherCatalog.endsWith('i'))

        // Calcular la posición donde collideItems colocaría este item pegado a la regleta
        const otherItemXAxis = new Vector3(1, 0, 0).applyQuaternion(otherQuat)
        const directionMultiplier =
          otherItemXAxis.dot(new Vector3(itemPos.x - otherPos.x, 0, itemPos.z - otherPos.z)) < 0
            ? -1
            : 1

        const otherRegletaMorph = otherItem.morphs?.find((m) => m.name === 'Regleta')
        const otherRegletaInvertedValue = getRegletaInvertedValue(
          otherItem.regleta || 0,
          otherRegletaMorph,
        )

        let expectedPosition: Vector3

        if (otherIsRightRinconero && directionMultiplier > 0) {
          // KBR derecho: Item normal pegado a la regleta en su lado IZQUIERDO
          // El lateral debe estar en el lado DERECHO (opuesto, isFirstItem = false)
          expectedPosition = otherPos
            .clone()
            .add(otherItemXAxis.multiplyScalar(directionMultiplier * otherItem.width))
          const zAxis = new Vector3(0, 0, 1).applyQuaternion(otherQuat)
          const rinconeroOffset = (otherItem.thickness || 0) + otherRegletaInvertedValue
          expectedPosition.add(zAxis.multiplyScalar(rinconeroOffset))

          const distance = expectedPosition.distanceTo(itemPos)
          if (distance < 50) {
            // El lateral debe estar en el lado DERECHO (isFirstItem = false)
            if (isFirstItem) {
              shouldDrawLateral = false
              break
            }
            // isFirstItem = false es correcto, continuar
          }
        } else if (otherIsLeftRinconero && directionMultiplier < 0) {
          // KBR izquierdo: Item normal pegado a la regleta en su lado DERECHO
          // El lateral debe estar en el lado IZQUIERDO (opuesto, isFirstItem = true)
          expectedPosition = otherPos.clone()
          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const adjustedQuat = otherQuat.clone().multiply(rotationAdjustment)
          const rinconeroOffset =
            (otherItem.thickness || 0) + otherRegletaInvertedValue + (item.width || 0)
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
          expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))

          const distance = expectedPosition.distanceTo(itemPos)
          if (distance < 50) {
            // El lateral debe estar en el lado IZQUIERDO (isFirstItem = true)
            if (!isFirstItem) {
              // Estamos intentando dibujar en el lado derecho (donde está la regleta), pero debe estar en el izquierdo
              shouldDrawLateral = false
              break
            }
            // isFirstItem = true es correcto, continuar
          }
        }
      }

      if (!shouldDrawLateral) {
        return
      }

      // Para rinconeros 166-, siempre permitir laterales incluso en esquinas
      const isRinconero = isRinconeroCatalog(item.catalogID)
      if (!isRinconero && !hasAvailableSpace(item, isFirstItem)) return
      // if (item.catalogID?.toString().toLowerCase().includes('deco')) return
      const lateralConfig = buildLateralConfig(item)
      if (!lateralConfig) return
      const { catalogID, heightOffset } = lateralConfig

      const isKBR = item.catalogID?.toString().toUpperCase().includes('KBR')
      let lateralMatrix: Matrix4

      if (isKBR && item.regleta) {
        // Decidir cuál lateral es el de regleta:
        // - KBR...I (izquierdo): el PRIMER lateral (isFirstItem === true) es el de regleta
        // - KBR...  (derecho) : el SEGUNDO lateral (isFirstItem === false) es el de regleta
        const catalog = item.catalogID?.toString().toLowerCase() ?? ''
        const isLeftRinconero = catalog.endsWith('i')
        const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

        if (isRegletaLateral) {
          lateralMatrix = calculateKBRLateralMatrix(item, isFirstItem, heightOffset)
        } else {
          const offset = isFirstItem ? -19 : item.width
          lateralMatrix = getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
        }
      } else {
        // No es KBR: lógica estándar para muebles normales
        // El offset base: -19 para lado izquierdo, item.width para lado derecho
        let offset = isFirstItem ? -19 : item.width

        // Verificar si el item está pegado a una regleta
        // Si está pegado, el lateral debe estar en el lado OPUESTO y debe incluir el fondo (thickness) de la unidad
        const items = get().items
        const itemPos = new Vector3()
        const itemQuat = new Quaternion()
        item.matrix.decompose(itemPos, itemQuat, new Vector3())

        let isPegadoARegleta = false
        let ladoColision = '' // 'left' o 'right'

        for (const [id, otherItem] of items) {
          if (id === item.id) continue
          if (!isLinkedItem(otherItem)) continue
          if (otherItem.catalogID === 'Frontal') continue
          if (!otherItem.visible) continue

          const otherIsKBR = otherItem.catalogID?.toString().toUpperCase().includes('KBR')
          const otherHasRegleta = otherIsKBR && otherItem.regleta

          if (!otherIsKBR || !otherHasRegleta) continue

          const otherPos = new Vector3()
          const otherQuat = new Quaternion()
          otherItem.matrix.decompose(otherPos, otherQuat, new Vector3())

          const otherCatalog = otherItem.catalogID?.toString().toLowerCase() ?? ''
          const otherRinconeroHand = rinconeroHandednessFromItem(otherItem)
          const otherIsLeftRinconero =
            isModularCornerIzquierda(otherItem.catalogID, otherRinconeroHand) ||
            (otherIsKBR && otherCatalog.endsWith('i'))
          const otherIsRightRinconero =
            isRinconeroDerecha(otherItem.catalogID, otherRinconeroHand) ||
            (otherIsKBR && !otherCatalog.endsWith('i'))

          const otherItemXAxis = new Vector3(1, 0, 0).applyQuaternion(otherQuat)
          const directionMultiplier =
            otherItemXAxis.dot(new Vector3(itemPos.x - otherPos.x, 0, itemPos.z - otherPos.z)) < 0
              ? -1
              : 1

          const otherRegletaMorph = otherItem.morphs?.find((m) => m.name === 'Regleta')
          const otherRegletaInvertedValue = getRegletaInvertedValue(
            otherItem.regleta || 0,
            otherRegletaMorph,
          )

          let expectedPosition: Vector3

          if (otherIsRightRinconero && directionMultiplier > 0) {
            // KBR derecho: Item normal pegado a la regleta en su lado IZQUIERDO
            expectedPosition = otherPos
              .clone()
              .add(otherItemXAxis.multiplyScalar(directionMultiplier * otherItem.width))
            const zAxis = new Vector3(0, 0, 1).applyQuaternion(otherQuat)
            const rinconeroOffset = (otherItem.thickness || 0) + otherRegletaInvertedValue
            expectedPosition.add(zAxis.multiplyScalar(rinconeroOffset))

            const distance = expectedPosition.distanceTo(itemPos)
            if (distance < 50) {
              isPegadoARegleta = true
              ladoColision = 'left' // Colisionó en el lado IZQUIERDO del item

              if (!isFirstItem) {
                offset = item.width || 0
                // Continuar para dibujar el lateral
              } else {
                return // NO dibujar lateral en el lado donde está la regleta
              }
            }
          } else if (otherIsLeftRinconero && directionMultiplier < 0) {
            // KBR izquierdo: Item normal pegado a la regleta en su lado DERECHO
            expectedPosition = otherPos.clone()
            const rotationAdjustment = new Quaternion().setFromAxisAngle(
              new Vector3(0, 1, 0),
              Math.PI / 2,
            )
            const adjustedQuat = otherQuat.clone().multiply(rotationAdjustment)
            const rinconeroOffset =
              (otherItem.thickness || 0) + otherRegletaInvertedValue + (item.width || 0)
            const xAxis = new Vector3(1, 0, 0).applyQuaternion(adjustedQuat)
            expectedPosition.add(xAxis.multiplyScalar(-rinconeroOffset))

            const distance = expectedPosition.distanceTo(itemPos)
            if (distance < 50) {
              isPegadoARegleta = true
              ladoColision = 'right' // Colisionó en el lado DERECHO del item
              // El lateral debe estar en el lado IZQUIERDO (opuesto)
              if (isFirstItem) {
                // Estamos en el lado izquierdo, que es correcto - SÍ dibujar
                // El offset debe ser negativo estándar: -19
                offset = -19
                // Continuar para dibujar el lateral
              } else {
                return // NO dibujar lateral en el lado donde está la regleta
              }
            }
          }
        }

        lateralMatrix = getRelativeMatrix(item.matrix, offset, item, heightOffset)
      }

      // Formatear altura y fondo para el ID
      //       let height = Math.round(item.height || 0)
      //       let depth = Math.round(item.thickness || 0)
      //       if (depth < 350) {
      //         depth = 350
      //       }
      //       if (idPrefix === 'CB') {
      //         depth = 600
      //       }
      //       if (idPrefix === 'CC') {
      //         height = height + 100
      //       }
      //       let heightOffset = 0
      //       if (idPrefix === 'LA') {
      //         if (height === 820 || height === 920) {
      //           //no hacemos nada
      //         } else if (height > 900) {
      //           height = 900
      //         } else if (height < 900) {
      //           height = 800
      //         }
      //         heightOffset = 20
      //       }
      //       const catalogID = `${idPrefix}${height / 10}${depth / 10}`
      //       // Calcular la matriz para la posición del lateral
      //       // Offset negativo para el primer elemento, positivo para el último
      //       const offset = isFirstItem ? -19 : item.width
      //       const lateralMatrix = getRelativeMatrix(item.matrix, offset, item, heightOffset)

      const originalCurrentObjectID = get().currentObjectID

      try {
        // Guardar el currentObjectID actual y establecerlo temporalmente al item del cual se crea el lateral
        // para evitar que herede texturas/propiedades de otras unidades seleccionadas
        set((store) => {
          store.currentObjectID = item.id
        })

        const lateralId = await get().drawItem(
          getId('linkedItem'),
          catalogID,
          '',
          true,
          lateralMatrix,
          'items',
        )

        // Restaurar el currentObjectID original
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })

        // Actualizar x, z del lateral desde la matriz (como los elementos enlazados)
        const finalPosition = new Vector3()
        lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
        get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

        if (item.tipoNormalizado === 'MAL') {
          get().editItemHeight(item.y, lateralId)
        }

        if (item.snappedWall && !isKBR) {
          get().editSnappedWall(lateralId, item.snappedWall)
          // Proyectar el lateral en la pared para asegurar que esté correctamente alineado
          // Usar un pequeño delay para asegurar que el lateral se haya dibujado completamente
          setTimeout(() => {
            const lateralItem = get().items.get(lateralId)
            if (lateralItem && lateralItem.matrix) {
              get().projectItemOnWall(lateralId, item.snappedWall, lateralItem.matrix)
            }
          }, 100)
        }
      } catch (error: any) {
        // Restaurar el currentObjectID original en caso de error
        // Nota: originalCurrentObjectID ya está guardado antes del try
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })
        console.error(
          `❌ Error al dibujar lateral ${catalogID} para mueble ${item.id} (${item.catalogID}):`,
          error,
        )
      }
    }

    async function processLateralKBR(item: LinkedItem, isFirstItem: boolean) {
      const lateralConfig = buildLateralConfig(item)
      if (!lateralConfig) return
      const { catalogID, heightOffset } = lateralConfig
      let lateralMatrix: Matrix4
      const originalCurrentObjectID = get().currentObjectID

      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')

      if (item.regleta) {
        const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem
        if (isRegletaLateral) {
          lateralMatrix = calculateKBRLateralMatrix(item, isFirstItem, heightOffset)
        } else {
          const offset = isFirstItem ? -19 : item.width
          lateralMatrix = getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
        }
      } else {
        const offset = isFirstItem ? -19 : item.width
        lateralMatrix = getRelativeMatrix(item.matrix, offset, item, heightOffset)
      }

      try {
        // Guardar el currentObjectID actual y establecerlo temporalmente al item del cual se crea el lateral
        // para evitar que herede texturas/propiedades de otras unidades seleccionadas
        set((store) => {
          store.currentObjectID = item.id
        })

        const lateralId = await get().drawItem(
          getId('linkedItem'),
          catalogID,
          '',
          true,
          lateralMatrix,
          'items',
        )

        // Restaurar el currentObjectID original
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })

        const finalPosition = new Vector3()
        lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
        get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

        if (item.tipoNormalizado === 'MAL') {
          get().editItemHeight(item.y, lateralId)
        }

        if (item.snappedWall && !isInCorner(item, isFirstItem)) {
          get().editSnappedWall(lateralId, item.snappedWall)
          setTimeout(() => {
            const lateralItem = get().items.get(lateralId)
            if (lateralItem && lateralItem.matrix) {
              get().projectItemOnWall(lateralId, item.snappedWall, lateralItem.matrix)
            }
          }, 100)
        }
      } catch (error: any) {
        // Restaurar el currentObjectID original en caso de error
        // Nota: originalCurrentObjectID ya está guardado antes del try
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })
        console.error(`❌ Error al dibujar lateral KBR ${catalogID}:`, error)
      }
    }

    /**
     * Procesa laterales para KARB (igual que KBR pero con regleta directa, no invertida)
     */
    async function processLateralKARB(item: LinkedItem, isFirstItem: boolean) {
      const lateralConfig = buildLateralConfig(item)
      if (!lateralConfig) {
        return
      }
      const { catalogID, heightOffset } = lateralConfig
      let lateralMatrix: Matrix4
      const originalCurrentObjectID = get().currentObjectID

      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')

      if (item.regleta) {
        const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

        if (isRegletaLateral) {
          lateralMatrix = calculateKARBLateralMatrix(item, isFirstItem, heightOffset)
        } else {
          const offset = isFirstItem ? -19 : item.width
          lateralMatrix = getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
        }
      } else {
        const offset = isFirstItem ? -19 : item.width
        lateralMatrix = getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
      }

      try {
        // Guardar el currentObjectID actual y establecerlo temporalmente al item del cual se crea el lateral
        // para evitar que herede texturas/propiedades de otras unidades seleccionadas
        set((store) => {
          store.currentObjectID = item.id
        })

        const lateralId = await get().drawItem(
          getId('linkedItem'),
          catalogID,
          '',
          true,
          lateralMatrix,
          'items',
        )

        // Restaurar el currentObjectID original
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })

        const finalPosition = new Vector3()
        lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
        get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

        if (item.tipoNormalizado === 'MAL') {
          get().editItemHeight(item.y, lateralId)
        }

        // Para laterales de KARB con regleta, NO proyectar en la pared porque la posición
        // ya está calculada correctamente con calculateKARBLateralMatrix
        const isKARBWithRegletaLateral =
          item.regleta && (isLeftRinconero ? isFirstItem : !isFirstItem)

        if (item.snappedWall && !isInCorner(item, isFirstItem) && !isKARBWithRegletaLateral) {
          get().editSnappedWall(lateralId, item.snappedWall)
          setTimeout(() => {
            const lateralItem = get().items.get(lateralId)
            if (lateralItem && lateralItem.matrix) {
              get().projectItemOnWall(lateralId, item.snappedWall, lateralItem.matrix)
            }
          }, 100)
        } else if (item.snappedWall && isKARBWithRegletaLateral) {
          // Para KARB con regleta, solo asignar la pared pero NO proyectar (la posición ya está correcta)
          get().editSnappedWall(lateralId, item.snappedWall)
        }
      } catch (error: any) {
        // Restaurar el currentObjectID original en caso de error
        // Nota: originalCurrentObjectID ya está guardado antes del try
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })
        console.error(`❌ Error al dibujar lateral KARB ${catalogID}:`, error)
      }
    }

    /**
     * Procesa laterales para rinconeros altos (KALB) en los lados cortos/alejados
     * Los rinconeros en L tienen dos lados cortos: uno en cada brazo del L (las esquinas)
     */
    async function processLateralKALB(item: LinkedItem, isFirstShortSide: boolean) {
      const lateralConfig = buildLateralConfig(item)
      if (!lateralConfig) return
      const { catalogID, heightOffset } = lateralConfig
      const originalCurrentObjectID = get().currentObjectID

      const basePosition = new Vector3()
      const baseQuaternion = new Quaternion()
      const baseScale = new Vector3()
      item.matrix.decompose(basePosition, baseQuaternion, baseScale)

      let lateralMatrix: Matrix4

      if (isFirstShortSide) {
        const offset = -19
        lateralMatrix = getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
      } else {
        // Segundo lado corto: en el extremo del segundo brazo (lado del segundo brazo, rotado 270°)
        // Este es el lateral que va apoyado al frente, sin desplazamiento extra de 19mm
        const rotationAdjustment = new Quaternion().setFromAxisAngle(
          new Vector3(0, 1, 0),
          (3 * Math.PI) / 2,
        )
        const newQuat = baseQuaternion.clone().multiply(rotationAdjustment)

        const xAxis = new Vector3(1, 0, 0).applyQuaternion(baseQuaternion)
        const zAxis = new Vector3(0, 0, 1).applyQuaternion(baseQuaternion)

        const endOfSecondArm = basePosition
          .clone()
          .add(xAxis.multiplyScalar(item.width))
          .add(zAxis.multiplyScalar(item.thickness))

        // Crear una matriz temporal en el extremo del segundo brazo con la rotación correcta
        const tempMatrix = new Matrix4().compose(endOfSecondArm, newQuat, baseScale)

        // Aquí NO aplicamos offset extra en X: el frente hace de referencia
        // (offset = 0 → solo heightOffset)
        lateralMatrix = getRelativeMatrix(tempMatrix, 0, undefined, heightOffset)
      }

      try {
        // Guardar el currentObjectID actual y establecerlo temporalmente al item del cual se crea el lateral
        // para evitar que herede texturas/propiedades de otras unidades seleccionadas
        set((store) => {
          store.currentObjectID = item.id
        })

        const lateralId = await get().drawItem(
          getId('linkedItem'),
          catalogID,
          '',
          true,
          lateralMatrix,
          'items',
        )

        // Restaurar el currentObjectID original
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })

        // Actualizar x, z del lateral desde la matriz
        const finalPosition = new Vector3()
        lateralMatrix.decompose(finalPosition, new Quaternion(), new Vector3())
        get().editItem(lateralId, { x: finalPosition.x, z: finalPosition.z })

        // Cambiar altura para normalizados
        if (item.tipoNormalizado === 'MAL') {
          get().editItemHeight(item.y, lateralId)
        }
      } catch (error: any) {
        // Restaurar el currentObjectID original en caso de error
        // Nota: originalCurrentObjectID ya está guardado antes del try
        set((store) => {
          store.currentObjectID = originalCurrentObjectID
        })
        console.error(
          `❌ Error al dibujar lateral KALB ${catalogID} para mueble ${item.id} (${item.catalogID}):`,
          error,
        )
      }
    }

    /**
     * Calcula la matriz del lateral para rinconeros KBR de manera relativa al rinconero
     */
    function calculateKBRLateralMatrix(
      item: LinkedItem,
      isFirstItem: boolean,
      heightOffset: number,
    ): Matrix4 {
      function getRegletaInvertedValue(value: number, morph: Morphs) {
        if (!morph) return value
        if (value >= morph.max) return morph.min
        if (value <= morph.min) return morph.max
        return morph.max + morph.min - value
      }

      const baseMatrix = item.matrix.clone()
      const basePosition = new Vector3()
      const baseQuaternion = new Quaternion()
      const baseScale = new Vector3()
      baseMatrix.decompose(basePosition, baseQuaternion, baseScale)

      // Obtener el valor de regleta usando la misma lógica que getRelativeMatrix
      // const regletaMorph = item.morphs?.find((m) => m.name === 'Regleta')
      // const regletaRaw = typeof item.regleta === 'number' ? item.regleta : 60
      // const regletaValue = getRegletaInvertedValue(regletaRaw, regletaMorph)

      // const thicknessValue =
      //   typeof item.thickness === 'number' && item.thickness > 0 ? item.thickness : 600
      // const widthValue = typeof item.width === 'number' && item.width > 0 ? item.width : 1000

      const hand = (item.hand || item.mano || '').toString().toLowerCase()
      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = hand.includes('cd') || catalog.endsWith('i')

      // Determinar si este lateral está más próximo a la regleta
      const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

      let lateralPosition: Vector3
      let lateralQuaternion: Quaternion

      if (isRegletaLateral) {
        const rinconeroQuat = baseQuaternion.clone()
        const rinconeroPos = basePosition.clone()

        if (!isLeftRinconero) {
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
          const newPos = rinconeroPos.clone().add(xAxis.multiplyScalar(item.width))

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            -Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          const rinconeroOffset =
            (item.thickness || 0) +
            (getRegletaInvertedValue(
              item.regleta || 0,
              item.morphs?.find((m) => m.name === 'Regleta'),
            ) || 0)

          const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinconeroQuat)
          newPos.add(zAxis.multiplyScalar(rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        } else {
          const newPos = rinconeroPos.clone()

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          const rinconeroOffset =
            (item.thickness || 0) +
            (getRegletaInvertedValue(
              item.regleta || 0,
              item.morphs?.find((m) => m.name === 'Regleta'),
            ) || 0)

          const zAxis = new Vector3(1, 0, 0).applyQuaternion(newQuat)
          newPos.add(zAxis.multiplyScalar(-rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        }
      } else {
        const offset = isFirstItem ? -19 : item.width
        return getRelativeMatrix(item.matrix, offset, item, heightOffset)
      }

      return new Matrix4().compose(lateralPosition, lateralQuaternion, baseScale)
    }

    /**
     * Calcula la matriz del lateral para rinconeros KARB (igual que KBR pero con regleta directa, no invertida, y ajuste -40mm)
     */
    function calculateKARBLateralMatrix(
      item: LinkedItem,
      isFirstItem: boolean,
      heightOffset: number,
    ): Matrix4 {
      const baseMatrix = item.matrix.clone()
      const basePosition = new Vector3()
      const baseQuaternion = new Quaternion()
      const baseScale = new Vector3()
      baseMatrix.decompose(basePosition, baseQuaternion, baseScale)

      const hand = (item.hand || item.mano || '').toString().toLowerCase()
      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = hand.includes('cd') || catalog.endsWith('i')

      // Determinar si este lateral está más próximo a la regleta
      const isRegletaLateral = isLeftRinconero ? isFirstItem : !isFirstItem

      let lateralPosition: Vector3
      let lateralQuaternion: Quaternion

      if (isRegletaLateral) {
        const rinconeroQuat = baseQuaternion.clone()
        const rinconeroPos = basePosition.clone()

        // Para KARB: usar regleta directa (NO invertida) y ajuste de -40mm
        const regletaValue = item.regleta || 0
        const RINCONERO_OFFSET_ADJUSTMENT = -50

        if (!isLeftRinconero) {
          const xAxis = new Vector3(1, 0, 0).applyQuaternion(rinconeroQuat)
          const newPos = rinconeroPos.clone().add(xAxis.multiplyScalar(item.width))

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            -Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          const rinconeroOffset = (item.thickness || 0) + regletaValue + RINCONERO_OFFSET_ADJUSTMENT

          const zAxis = new Vector3(0, 0, 1).applyQuaternion(rinconeroQuat)
          newPos.add(zAxis.multiplyScalar(rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        } else {
          const newPos = rinconeroPos.clone()

          const rotationAdjustment = new Quaternion().setFromAxisAngle(
            new Vector3(0, 1, 0),
            Math.PI / 2,
          )
          const newQuat = rinconeroQuat.clone().multiply(rotationAdjustment)

          const rinconeroOffset = (item.thickness || 0) + regletaValue + RINCONERO_OFFSET_ADJUSTMENT

          const zAxis = new Vector3(1, 0, 0).applyQuaternion(newQuat)
          newPos.add(zAxis.multiplyScalar(-rinconeroOffset))

          if (heightOffset) newPos.y += heightOffset

          lateralPosition = newPos
          lateralQuaternion = newQuat
        }
      } else {
        // Lateral que NO es de regleta: usar lógica simple sin pasar item como referenceItem
        // para evitar que getRelativeMatrix aplique lógica especial de rinconero
        const offset = isFirstItem ? -19 : item.width
        return getRelativeMatrix(item.matrix, offset, undefined, heightOffset)
      }

      return new Matrix4().compose(lateralPosition, lateralQuaternion, baseScale)
    }

    function isInCorner(item: LinkedItem, isFirstItem: boolean) {
      const catalog = item.catalogID?.toString().toLowerCase() ?? ''
      const isLeftRinconero = catalog.endsWith('i')
      return (isLeftRinconero && !isFirstItem) || (!isLeftRinconero && isFirstItem)
    }

    /**
     * Verifica si hay espacio disponible para colocar un lateral
     */
    function hasAvailableSpace(item: LinkedItem, isFirstItem: boolean): boolean {
      // Si el mueble no está pegado a una pared, asumimos que hay espacio
      if (!item.snappedWall) return true

      const wall = items.get(item.snappedWall) as Walls
      if (!wall) return false

      const itemPos = new Vector3()
      const itemQuat = new Quaternion()
      item.matrix.decompose(itemPos, itemQuat, new Vector3())

      const wallStart = new Vector3(wall.start[0], 0, wall.start[1])
      const wallEnd = new Vector3(wall.end[0], 0, wall.end[1])
      const wallDir = wallEnd.clone().sub(wallStart).normalize()

      const itemToWallStart = itemPos.clone().sub(wallStart)
      const projectedDist = itemToWallStart.dot(wallDir)

      const wallLength = wallStart.distanceTo(wallEnd)

      if (isFirstItem) {
        return projectedDist >= 20
      } else {
        const itemEndPos = projectedDist + item.width
        return wallLength - itemEndPos >= 20
      }
    }

    function buildLateralConfig(item: LinkedItem) {
      const type = item.tipoNormalizado
      if (!type) return null

      if (type === 'MAL') {
        // Laterales válidos disponibles: 30, 35, 37, 40, 42, 45, 47, 50, 52, 80, 82, 90, 92
        const validHeights = [30, 35, 37, 40, 42, 45, 47, 50, 52, 80, 82, 90, 92]

        const itemHeight = Math.round(item.height || 0)
        const heightInCm = Math.round(itemHeight / 10)

        // Encontrar el lateral válido más cercano
        let closestHeight = validHeights[0]
        let minDiff = Math.abs(heightInCm - closestHeight)

        for (const validHeight of validHeights) {
          const diff = Math.abs(heightInCm - validHeight)
          if (diff < minDiff) {
            minDiff = diff
            closestHeight = validHeight
          }
        }

        // Si la diferencia es muy grande (más de 5cm), no hay lateral válido
        if (minDiff > 5) {
          console.warn(
            `No se encontró lateral válido para altura ${itemHeight}mm (${heightInCm}cm). Más cercano: ${closestHeight}cm (diferencia: ${minDiff}cm)`,
          )
          return null
        }

        return {
          catalogID: `LA${closestHeight}35`,
          heightOffset: 20,
        }
      }

      let idPrefix = ''
      if (type === 'MBJ') {
        idPrefix = 'CB'
      } else if (type === 'COL') {
        idPrefix = 'CC'
      } else {
        return null
      }

      let height = Math.round(item.height || 0)
      let depth = Math.round(item.thickness || 0)
      if (depth < 350) {
        depth = 350
      }
      if (idPrefix === 'CB') {
        depth = 600
        const heightInCm = Math.round(height / 10)
        // Redondear hacia arriba a la decena más cercana (ceil a múltiplos de 10)
        const roundedHeightInCm = Math.ceil(heightInCm / 10) * 10
        // Convertir de vuelta a mm y asegurar mínimo de 90cm (900mm)
        height = Math.max(roundedHeightInCm * 10, 900)
      }
      if (idPrefix === 'CC') {
        height = height + 100
      }

      return {
        catalogID: `${idPrefix}${height / 10}${depth / 10}`,
        heightOffset: 0,
      }
    }
  },
  getFrontalHeight: () => {
    const items = get().items
    const prefs = get().prefs
    for (const [, item] of items) {
      if ((isItem(item) || isLinkedItem(item)) && item.catalogID === 'Frontal') {
        return item.height
      }
    }

    return prefs.ALTURAFRONTALES
  },
})
